// src/components/DealView.jsx
// Deal detail view with tasks, file uploads, document requests, and locations management

import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import {
  ChevronDown, ChevronRight, Upload, X, Plus, FileText, Trash2,
  Calendar, User, ExternalLink, Mail, Download, Loader2, Link, Check, Copy, ArrowLeft,
  MapPin, Star, Edit2, Search, Flame, CheckCircle, MessageSquare, Save,
  DollarSign, Bell, Clock, AlertTriangle, ClipboardList, Pencil, XCircle, Scale, Briefcase, Calculator, Sparkles, UserPlus, Users,
  Car, Building2, TrendingUp, PhoneCall, Shield, Zap, Home, Monitor, Wrench, HeartPulse,
  Send, RefreshCw
} from 'lucide-react';
import { useDeal, useTeam, useLfgLocations, useDealLocations, useDealNotes, useDealReminders, useDealObligations, useDealAccess, useDealMilestones, useDealReasonCategories } from '../hooks/useSupabase';
import MilestoneTimeline from './MilestoneTimeline';
import { useDisplayColors } from '../lib/ThemeContext';
import { getFileUrl, generateSellerToken, generateScopedSellerToken, buildSellerPortalUrl, sendEmail, supabase, createSharePointDealStructure, createCalendarEvent, cancelCalendarEvent, getDealVisits, uploadToSharePoint, sendInfoRequest, getInfoRequestByDealId, saveInfoSectionInternal, sanitizeDealName, sharePointSafeSegment, normalizeBusinessInfoRows } from '../lib/supabase';
import { extractPdfExtraFields, extractXlsxExtraFields } from '../lib/pdfExtract';
import DiligenceSection from './DiligenceSection';
import { SurveyReview } from './SurveyRenderer';

// LFG Brand Colors (defaults)
const LFG_COLORS = {
  navy: '#1A3665',
  gray: '#707578',
  green: '#85BD66',
};

// Fallback when stages aren't loaded via props — chronological cycle +
// On Hold (off-cycle) + two terminals.
const DEFAULT_PHASES = [
  { id: 'future-target', name: 'Future Target', color: '#94A3B8' },
  { id: 'prospect',      name: 'Prospect',      color: '#6B7280' },
  { id: 'diligence',     name: 'Diligence',     color: '#EAB308' },
  { id: 'offer-made',    name: 'Offer Made',    color: '#F97316' },
  { id: 'under-loi',     name: 'Under LOI',     color: '#3B82F6' },
  { id: 'on-hold',       name: 'On Hold',       color: '#A855F7' },
  { id: 'closed',        name: 'Closed',        color: '#22C55E', is_terminal: true },
  { id: 'canceled',      name: 'Canceled',      color: '#EF4444', is_terminal: true },
];

const STATUS_COLORS = {
  'Not Started': 'bg-gray-100 text-gray-600',
  'In Progress': 'bg-blue-100 text-blue-700',
  'Requested': 'bg-yellow-100 text-yellow-700',
  'Received': 'bg-purple-100 text-purple-700',
  'Under Review': 'bg-orange-100 text-orange-700',
  'Complete': 'bg-green-100 text-green-700',
  'N/A': 'bg-gray-50 text-gray-400'
};

const STATUSES = ['Not Started', 'In Progress', 'Requested', 'Received', 'Under Review', 'Complete', 'N/A'];

// Format phone number as (XXX) XXX-XXXX
function formatPhone(value) {
  var digits = (value || '').replace(/\D/g, '');
  if (digits.length === 0) return '';
  if (digits.length <= 3) return '(' + digits;
  if (digits.length <= 6) return '(' + digits.slice(0, 3) + ') ' + digits.slice(3);
  return '(' + digits.slice(0, 3) + ') ' + digits.slice(3, 6) + '-' + digits.slice(6, 10);
}

// Format number with commas for dollar display
function formatDollar(value) {
  if (value === '' || value === null || value === undefined) return '';
  var num = parseFloat(String(value).replace(/,/g, ''));
  if (isNaN(num)) return String(value);
  return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

// Parse dollar string back to number
function parseDollar(value) {
  if (!value) return '';
  return String(value).replace(/,/g, '');
}

// Phase C.5/C.6 — formats a pending_field_updates entry's current/suggested
// value per its declared format for the confirm banner.
function formatPendingValue(value, format) {
  if (value === null || value === undefined || value === '') return '—';
  if (format === 'currency') return '$' + formatDollar(value);
  if (format === 'number') return Number(value).toLocaleString();
  if (format === 'date') return new Date(value + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return String(value);
}

// Phase C.4e — financial form field. Consistent look across all financial
// inputs: label above, bordered input with optional currency prefix, focus
// ring in LFG navy, subtle tint when disabled.
function FinancialField({ label, value, onFocus, onChange, onBlur, prefix, placeholder, disabled, inputMode = 'decimal' }) {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wider mb-1.5 block" style={{ color: '#94A3B8' }}>
        {label}
      </label>
      <div className="relative">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-sm tabular-nums" style={{ color: '#94A3B8' }}>
            {prefix}
          </span>
        )}
        <input
          type="text"
          inputMode={inputMode}
          value={value}
          onFocus={onFocus}
          onChange={e => onChange(e.target.value)}
          onBlur={onBlur}
          disabled={disabled}
          placeholder={placeholder}
          className="w-full h-10 text-sm rounded-lg transition-colors tabular-nums focus:outline-none disabled:bg-slate-50 disabled:cursor-not-allowed"
          style={{
            border: '1px solid #E5E9F0',
            paddingLeft: prefix ? '1.75rem' : '0.875rem',
            paddingRight: '0.875rem',
            color: '#0F172A',
          }}
          onFocusCapture={e => { e.target.style.borderColor = '#1A3665'; e.target.style.boxShadow = '0 0 0 3px rgba(26, 54, 101, 0.1)'; }}
          onBlurCapture={e => { e.target.style.borderColor = '#E5E9F0'; e.target.style.boxShadow = 'none'; }}
        />
      </div>
    </div>
  );
}

// Phase C.4e — computed / read-only field (e.g. EBITDA Multiple). Visually
// distinguished from inputs so users know it's derived, not editable.
function ComputedField({ label, value, hint }) {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wider mb-1.5 block" style={{ color: '#94A3B8' }}>
        {label}
      </label>
      <div
        className="h-10 rounded-lg flex items-center justify-center text-center"
        style={{
          background: value
            ? 'linear-gradient(135deg, rgba(26, 54, 101, 0.06) 0%, rgba(26, 54, 101, 0.03) 100%)'
            : '#FAFBFD',
          border: '1px solid #E5E9F0',
        }}
        title={hint}
      >
        {value
          ? <span className="text-base font-semibold tabular-nums" style={{ color: '#1A3665', letterSpacing: '-0.01em' }}>{value}</span>
          : <span className="text-sm" style={{ color: '#CBD5E1' }}>—</span>}
      </div>
    </div>
  );
}

// Phase C.4h — compact stat tile used in the Activity tab's stats bar.
// Same visual grammar as the hero metric tiles but smaller/denser.
function StatTile({ label, value, accent }) {
  return (
    <div
      className="rounded-xl px-4 py-3 transition-colors"
      style={{
        background: 'linear-gradient(180deg, #FFFFFF 0%, #FAFBFD 100%)',
        border: '1px solid #E5E9F0',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.03)',
      }}
    >
      <div className="text-[10px] font-semibold uppercase tracking-[0.1em]" style={{ color: '#94A3B8' }}>
        {label}
      </div>
      <div className="text-xl font-semibold mt-1 tabular-nums leading-none" style={{ color: accent || '#0F172A', letterSpacing: '-0.015em' }}>
        {value}
      </div>
    </div>
  );
}

// Phase C.4g — contact detail card used in the Contacts tab. Icon badge +
// title + subtitle/action, followed by a 2×2 grid of ContactField inputs.
function ContactCard({ icon: Icon, title, subtitle, accent = '#1A3665', accentBg, action, children }) {
  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{ backgroundColor: '#ffffff', border: '1px solid #E5E9F0', boxShadow: '0 1px 2px rgba(15, 23, 42, 0.03)' }}
    >
      <div
        className="flex items-center justify-between px-4 py-3"
        style={{ borderBottom: '1px solid #F0F2F6' }}
      >
        <div className="flex items-center gap-2.5">
          {Icon && (
            <div
              className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: accentBg || `${accent}15`, color: accent }}
            >
              <Icon className="w-4 h-4" strokeWidth={2.2} />
            </div>
          )}
          <div>
            <div className="text-xs font-semibold" style={{ color: '#0F172A' }}>{title}</div>
            {subtitle && (
              <div className="text-[11px]" style={{ color: '#94A3B8' }}>{subtitle}</div>
            )}
          </div>
        </div>
        {action}
      </div>
      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {children}
      </div>
    </div>
  );
}

// Phase C.4g — contact/text input field with consistent styling across
// Contacts tab. Label above, bordered input with focus ring matching
// the Financials fields.
function ContactField({ label, value, onChange, onBlur, disabled, placeholder, type = 'text' }) {
  return (
    <div>
      <label className="text-[10px] font-semibold uppercase tracking-wider mb-1 block" style={{ color: '#94A3B8' }}>
        {label}
      </label>
      <input
        type={type}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        onBlur={onBlur}
        disabled={disabled}
        placeholder={placeholder}
        className="w-full h-9 text-sm rounded-lg transition-colors focus:outline-none disabled:bg-slate-50 disabled:cursor-not-allowed"
        style={{
          border: '1px solid #E5E9F0',
          padding: '0 0.75rem',
          color: '#0F172A',
        }}
        onFocusCapture={e => { e.target.style.borderColor = '#1A3665'; e.target.style.boxShadow = '0 0 0 3px rgba(26, 54, 101, 0.1)'; }}
        onBlurCapture={e => { e.target.style.borderColor = '#E5E9F0'; e.target.style.boxShadow = 'none'; }}
      />
    </div>
  );
}

// Phase C.4f — detail card shown in the expanded AI Intelligence view.
// Header: icon + category label + headline metric. Body: bullet list of
// the extracted items (e.g. employee names, vehicle rows, properties).
function AiDetailCard({ icon: Icon, label, accent, headline, items }) {
  return (
    <div
      className="rounded-xl p-4"
      style={{
        backgroundColor: '#ffffff',
        border: '1px solid #E5E9F0',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.03)',
      }}
    >
      <div className="flex items-center gap-2.5 mb-3">
        {Icon && (
          <div
            className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${accent}15`, color: accent }}
          >
            <Icon className="w-4 h-4" strokeWidth={2.2} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold" style={{ color: '#0F172A' }}>{label}</div>
          {headline && (
            <div className="text-[11px] tabular-nums" style={{ color: '#64748B' }}>{headline}</div>
          )}
        </div>
      </div>
      <ul className="space-y-1">
        {items.slice(0, 10).map((it, i) => (
          <li key={i} className="text-xs leading-snug flex gap-2" style={{ color: '#475569' }}>
            <span className="flex-shrink-0 mt-1.5 w-1 h-1 rounded-full" style={{ backgroundColor: accent, opacity: 0.6 }} />
            <span className="min-w-0 break-words">{it}</span>
          </li>
        ))}
        {items.length > 10 && (
          <li className="text-[11px] italic pt-1" style={{ color: '#94A3B8' }}>
            + {items.length - 10} more
          </li>
        )}
      </ul>
    </div>
  );
}

// Phase C.4e — stat chip used inside AI Intelligence. Uniform styling
// (white card, soft border, icon + label + value). Replaces the 12-color
// pastel badge rainbow that was previously used.
function AiStatChip({ icon: Icon, label, value, accent, onClick }) {
  const Comp = onClick ? 'button' : 'div';
  const interactiveProps = onClick
    ? {
        type: 'button',
        onClick,
        title: `Jump to ${label}`,
        className: 'inline-flex items-center gap-2 px-3 py-2 rounded-xl transition-all hover:bg-white hover:shadow-md cursor-pointer text-left',
      }
    : {
        className: 'inline-flex items-center gap-2 px-3 py-2 rounded-xl transition-colors hover:bg-white',
      };
  return (
    <Comp
      {...interactiveProps}
      style={{
        backgroundColor: 'rgba(255, 255, 255, 0.6)',
        border: '1px solid #E5E9F0',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.03)',
      }}
    >
      {Icon && (
        <div
          className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${accent}15`, color: accent }}
        >
          <Icon className="w-3.5 h-3.5" strokeWidth={2.2} />
        </div>
      )}
      <div className="flex flex-col leading-tight">
        <span className="text-[10px] font-medium uppercase tracking-wider" style={{ color: '#94A3B8' }}>{label}</span>
        <span className="text-sm font-semibold tabular-nums" style={{ color: '#0F172A' }}>{value}</span>
      </div>
    </Comp>
  );
}

// Phase C.4d — unified header action button. Single source of truth for
// the "secondary chip" style used across all top-bar actions so they
// align (h-9), share spacing, and read as one action toolbar.
function HeaderActionBtn({ children, onClick, disabled, title, minWidth }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="h-9 inline-flex items-center justify-center gap-1.5 px-3 text-sm font-medium rounded-lg transition-all hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
      style={{
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
        color: '#334155',
        border: '1px solid rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(6px)',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
        minWidth: minWidth || undefined,  // prevent reflow when label toggles to "…ing"
      }}
    >
      {children}
    </button>
  );
}

// Phase C.4 — hero metric tile. White card, subtle inner gradient gives
// dimension. Optional accent color tints the value.
function DealMetricTile({ label, value, accent }) {
  return (
    <div
      className="group rounded-2xl px-5 py-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
      style={{
        background: 'linear-gradient(180deg, #FFFFFF 0%, #FAFBFD 100%)',
        border: '1px solid #E5E9F0',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.02)',
      }}
    >
      <div className="text-[10px] font-semibold uppercase tracking-[0.1em]" style={{ color: '#94A3B8' }}>
        {label}
      </div>
      <div
        className="text-2xl font-semibold mt-2 tabular-nums leading-none"
        style={{ color: accent || '#0F172A', letterSpacing: '-0.015em' }}
      >
        {value}
      </div>
    </div>
  );
}

// Special "feature" variant of the metric tile — used for the Stage tile
// since Stage is the most narrative/contextual metric. Filled navy gradient,
// white text, lifts above the rest visually.
function DealStageMetricTile({ value, accent }) {
  return (
    <div
      className="rounded-2xl px-5 py-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg relative overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${accent || '#1A3665'} 0%, #2E4B7C 70%, #3D5C8F 100%)`,
        boxShadow: '0 4px 14px rgba(26, 54, 101, 0.25), 0 2px 4px rgba(26, 54, 101, 0.15)',
      }}
    >
      {/* Subtle inner highlight for depth */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 20% 0%, rgba(255,255,255,0.2) 0%, transparent 60%)',
        }}
      />
      <div className="relative">
        <div className="text-[10px] font-semibold uppercase tracking-[0.1em]" style={{ color: 'rgba(255,255,255,0.7)' }}>
          Stage
        </div>
        <div className="text-2xl font-semibold mt-2 leading-none text-white" style={{ letterSpacing: '-0.015em' }}>
          {value}
        </div>
      </div>
    </div>
  );
}

export default function DealView({
  dealId, organization, userProfile, pc, sc, isViewer: isViewerProp, isLimitedViewer, isIntegration, isBoardMember, isDealAdvisor,
  expanded, setExpanded, selected, setSelected,
  showModal, setShowModal, newTask, setNewTask,
  requestMsg, setRequestMsg, setView, setSellerDealId,
  uploading, setUploading, showToast, onBack, stages: stagesProp
}) {
  // Use database stages or fallback to defaults
  const PHASES = (stagesProp?.length > 0 ? stagesProp : DEFAULT_PHASES).map(s => ({
    ...s,
    hex: s.color, // DB stores hex in color field
  }));
  const { 
    deal, tasks, loading, updateDeal, updateTask, addTask, deleteTask,
    uploadFile, deleteFile, applyDueDates, markTasksRequested, refresh: refreshDeal
  } = useDeal(dealId);

  // Board members get full visibility but can only edit deals they
  // personally created. Shadow the isViewer prop to true on deals they
  // didn't create — the existing isViewer-gated affordances (save,
  // delete, upload, add task, etc.) will all switch off automatically.
  const isOwnDeal = !!deal && !!userProfile && deal.created_by === userProfile.id;
  // Deal advisors are external third parties scoped to this one deal via
  // deal_access — shadow them into isViewer so every existing edit-gated
  // affordance in this file (uploads, stage/priority editing, SharePoint
  // actions, DD checklist editing, etc.) collapses to read-only for them
  // automatically, the same way board members are shadowed above.
  const isViewer = isViewerProp || (isBoardMember && !!deal && !isOwnDeal) || isDealAdvisor;
  const { team } = useTeam(organization?.id);
  const { locations: lfgLocations, addLocation: addLfgLocation } = useLfgLocations(organization?.id);
  const {
    locations: dealLocations,
    pendingLocations,
    loading: locationsLoading,
    addLocation: addDealLocation,
    updateLocation: updateDealLocation,
    deleteLocation: deleteDealLocation,
    confirmLocation: confirmDealLocation,
    rejectLocation: rejectDealLocation,
    geocodeAddress
  } = useDealLocations(dealId, organization?.id);
  const {
    notes,
    loading: notesLoading,
    addNote,
    updateNote,
    deleteNote
  } = useDealNotes(dealId, organization?.id);
  const {
    reminders, loading: remindersLoading, addReminder, updateReminder,
    completeReminder, dismissReminder, deleteReminder
  } = useDealReminders(dealId);

  const {
    obligations, loading: obligationsLoading,
    addObligation, updateObligation, deleteObligation
  } = useDealObligations(dealId, organization?.id);
  const { accessList, grantAccess, grantAccessByEmail, revokeAccess } = useDealAccess(dealId);
  const { cancellationCategories } = useDealReasonCategories(deal?.organization_id || organization?.id);

  // Phase C.2 — milestone timeline
  const { milestones, loading: milestonesLoading, updateMilestone } = useDealMilestones(dealId);

  // When a milestone's "Kickoff" button is clicked, we stash the target role
  // here; DiligenceSection picks it up and opens its Send DD Request modal.
  const [pendingDdSendScope, setPendingDdSendScope] = useState(null);

  // Phase C.4 — DealView IA: tabs as primary navigation
  // Deal advisors land straight on Diligence (their only tab) rather than
  // Overview, so financial/task/contact content never renders even briefly.
  const [activeTab, setActiveTab] = useState(isDealAdvisor ? 'diligence' : 'overview');

  // If a stale activeTab points at a tab that's hidden for the current
  // role (e.g. integration users had 'diligence' open, then their tab set
  // shrunk), bounce them back to their default tab instead of rendering nothing.
  useEffect(() => {
    const visibleTabIds = isIntegration ? ['overview'] : isDealAdvisor ? ['diligence'] : ['overview', 'team', 'diligence', 'documents', 'activity'];
    if (!visibleTabIds.includes(activeTab)) setActiveTab(isDealAdvisor ? 'diligence' : 'overview');
  }, [isIntegration, isDealAdvisor, activeTab]);

  // Jump from a clickable AI chip to the matching Business Info section.
  // Switches to the Diligence tab if needed and scrolls the section into
  // view on the next paint (so the section actually exists in the DOM).
  const jumpToBiSection = useCallback((anchorId) => {
    setActiveTab('diligence');
    setInfoExpanded(true);
    setTimeout(() => {
      const el = document.getElementById(anchorId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Brief highlight pulse so the user sees where they landed
        el.style.transition = 'box-shadow 0.4s ease';
        const prev = el.style.boxShadow;
        el.style.boxShadow = '0 0 0 3px rgba(26, 54, 101, 0.35)';
        setTimeout(() => { el.style.boxShadow = prev || '0 1px 2px rgba(15, 23, 42, 0.03)'; }, 1400);
      }
    }, 60);
  }, []);
  // Tabs visible per role. Integration users get a single consolidated view
  // (timeline + integration tasks) rendered inside Overview, and deal
  // advisors get a single Diligence-only view — in both cases the tab bar
  // itself is hidden so they don't see a one-tab nav.
  const DEAL_TABS = isIntegration
    ? [{ id: 'overview', label: 'Overview' }]
    : isDealAdvisor
    ? [{ id: 'diligence', label: 'Diligence' }]
    : [
        { id: 'overview',  label: 'Overview' },
        { id: 'team',      label: 'Contacts' },
        { id: 'diligence', label: 'Diligence' },
        { id: 'documents', label: 'Documents' },
        { id: 'activity',  label: 'Notes & Tasks' },
      ];

  // Cheap presence check so the task-based request modal can nudge toward
  // the DD checklist when one exists. No realtime needed — the modal is
  // short-lived and the banner is informational.
  const [hasDdItems, setHasDdItems] = useState(false);
  useEffect(() => {
    if (!dealId) return;
    supabase
      .from('deal_dd_items')
      .select('id', { count: 'exact', head: true })
      .eq('deal_id', dealId)
      .then(({ count }) => setHasDdItems((count || 0) > 0));
  }, [dealId]);

  const fileInputRefs = useRef({});
  const [sellerLinkCopied, setSellerLinkCopied] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [converting, setConverting] = useState(false);
  const [loiUpdateBusy, setLoiUpdateBusy] = useState(false);
  const [editingName, setEditingName] = useState(null);
  const [showManageAccess, setShowManageAccess] = useState(false);
  // Phase Q.8 — "Send request to deal advisor" modal. Set to the advisor's
  // user object to open; null to close.
  const [requestAdvisor, setRequestAdvisor] = useState(null);
  // user id currently mid-flight for the one-click "Resend link" button
  const [resendingAdvisorId, setResendingAdvisorId] = useState(null);
  const [sendingSellerWelcome, setSendingSellerWelcome] = useState(false);
  const [grantingBulkAccess, setGrantingBulkAccess] = useState(false);
  const [bulkGrantProgress, setBulkGrantProgress] = useState(null); // { done, total }
  const [bulkGrantResults, setBulkGrantResults] = useState(null); // [{ email, ok, message }]
  const [scanningSharePoint, setScanningSharePoint] = useState(false);
  const [analyzingFiles, setAnalyzingFiles] = useState(false);
  const [analyzeProgress, setAnalyzeProgress] = useState(null); // { done, total }
  // Ad-hoc internal file upload (the "Upload" header button + hidden input).
  // Multi-file: uploads each one, fires AI categorization + DD matching, syncs
  // to SharePoint in the background, then refreshes the file list.
  const [internalUploading, setInternalUploading] = useState(false);
  const [internalUploadProgress, setInternalUploadProgress] = useState(null); // { done, total }
  const internalUploadInputRef = useRef(null);
  const [viewingFile, setViewingFile] = useState(null); // { url, name, type }
  const [allDealFiles, setAllDealFiles] = useState([]);
  // file_categories list — loaded once, used by the recategorize menu on
  // each file row in the Documents tab.
  const [fileCategories, setFileCategories] = useState([]);
  // Integration role can't see files at all — files often contain
  // financial / legal info that role shouldn't have visibility into.
  // (Earlier this was a category-slug filter, but the user decided the
  // safer default is to hide files entirely from integration.)
  const visibleDealFiles = useMemo(() => {
    if (isIntegration) return [];
    return allDealFiles;
  }, [allDealFiles, isIntegration]);
  const [filesExpanded, setFilesExpanded] = useState(false);
  const [locationsExpanded, setLocationsExpanded] = useState(true);
  const [notesExpanded, setNotesExpanded] = useState(true);
  const [financialsExpanded, setFinancialsExpanded] = useState(false);
  const [localFinancials, setLocalFinancials] = useState({});
  const [remindersExpanded, setRemindersExpanded] = useState(true);
  const [obligationsExpanded, setObligationsExpanded] = useState(true);
  const [aiSummaryExpanded, setAiSummaryExpanded] = useState(false);
  const [showObligationForm, setShowObligationForm] = useState(false);
  const [editingObligation, setEditingObligation] = useState(null);
  const [obligationForm, setObligationForm] = useState({
    type: 'lien', description: '', amount: '', holder: '', status: 'open', resolved_date: '', notes: ''
  });
  const [showReminderForm, setShowReminderForm] = useState(false);
  const [reminderForm, setReminderForm] = useState({
    title: '', description: '', due_date: '', amount: '', reminder_type: 'follow_up', include_in_digest: true
  });
  const [showLocationForm, setShowLocationForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [locationSaving, setLocationSaving] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [locationForm, setLocationForm] = useState({
    name: '', address: '', city: '', state: '', zip: '', 
    latitude: '', longitude: '', is_crematory: false, notes: ''
  });
  
  // Notes state
  const [newNote, setNewNote] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteContent, setEditingNoteContent] = useState('');

  // Send request modal state
  const [sendingRequest, setSendingRequest] = useState(false);
  const [requestSent, setRequestSent] = useState(false);

  // Info request state
  const [infoRequest, setInfoRequest] = useState(null);
  const [infoData, setInfoData] = useState(null);
  // Phase S — schema-driven HR survey definition loaded once per org.
  const [surveySchemas, setSurveySchemas] = useState({});
  useEffect(() => {
    const orgId = organization?.id || deal?.organization_id;
    if (!orgId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('survey_definitions')
        .select('key, schema')
        .eq('organization_id', orgId);
      if (cancelled || !data) return;
      const map = {};
      for (const row of data) map[row.key] = row.schema;
      setSurveySchemas(map);
    })();
    return () => { cancelled = true; };
  }, [organization?.id, deal?.organization_id]);
  const [duplicatePairs, setDuplicatePairs] = useState({ personnel: [], vehicles: [] });
  const [sendingInfoRequest, setSendingInfoRequest] = useState(false);
  const [infoRequestSent, setInfoRequestSent] = useState(false);
  const [editingInfo, setEditingInfo] = useState(false);
  const [infoExpanded, setInfoExpanded] = useState(false);
  const [expandedPersonnelId, setExpandedPersonnelId] = useState(null);
  const [expandedVehicleId, setExpandedVehicleId] = useState(null);
  const [showInfoEmailModal, setShowInfoEmailModal] = useState(false);
  // Phase U.2 — standalone HR survey send
  const [showHrSurveyModal, setShowHrSurveyModal] = useState(false);
  const [hrSurveyEmail, setHrSurveyEmail] = useState('');
  const [hrSurveyName, setHrSurveyName] = useState('');
  const [sendingHrSurvey, setSendingHrSurvey] = useState(false);
  const [hrSurveySent, setHrSurveySent] = useState(false);
  const [selectedInfoEmail, setSelectedInfoEmail] = useState('');
  const [selectedDocEmail, setSelectedDocEmail] = useState('');
  const [selectedDocIds, setSelectedDocIds] = useState(new Set());

  // SharePoint state
  const [creatingSPFolders, setCreatingSPFolders] = useState(false);

  // Schedule visit state
  const [showScheduleVisit, setShowScheduleVisit] = useState(false);
  const [visitForm, setVisitForm] = useState({ date: '', startTime: '09:00', endTime: '10:00', notes: '' });
  const [creatingEvent, setCreatingEvent] = useState(false);
  const [visits, setVisits] = useState([]);
  const [visitsLoading, setVisitsLoading] = useState(false);

  // Address autocomplete state
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [addressQuery, setAddressQuery] = useState('');
  const addressTimeoutRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Debounced address lookup via Nominatim
  const searchAddress = useCallback((query) => {
    if (addressTimeoutRef.current) clearTimeout(addressTimeoutRef.current);
    if (!query || query.length < 4) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    addressTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query + ', USA')}&limit=5&addressdetails=1`,
          { headers: { 'User-Agent': 'LFG-Acquisitions-Portal' } }
        );
        const data = await response.json();
        if (data?.length) {
          setAddressSuggestions(data.map(r => ({
            display: r.display_name,
            address: [r.address?.house_number, r.address?.road].filter(Boolean).join(' '),
            city: r.address?.city || r.address?.town || r.address?.village || r.address?.hamlet || '',
            state: r.address?.state || '',
            zip: r.address?.postcode || '',
            lat: parseFloat(r.lat),
            lon: parseFloat(r.lon),
          })));
          setShowSuggestions(true);
        } else {
          setAddressSuggestions([]);
          setShowSuggestions(false);
        }
      } catch (err) {
        console.error('Address search error:', err);
      }
    }, 400);
  }, []);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync local financials on initial deal load only (not after our own saves)
  useEffect(() => {
    if (deal) {
      setLocalFinancials({
        asking_price: formatDollar(deal.asking_price) || '',
        purchase_price: formatDollar(deal.purchase_price) || '',
        ebitda: formatDollar(deal.ebitda) || '',
        annual_revenue: formatDollar(deal.annual_revenue) || '',
        annual_calls: deal.annual_calls ? Number(deal.annual_calls).toLocaleString('en-US') : '',
        goodwill: formatDollar(deal.goodwill) || '',
        working_capital: formatDollar(deal.working_capital) || '',
        real_estate_value: deal.real_estate_value || '',
        inventory_value: deal.inventory_value || '',
        deal_structure_notes: deal.deal_structure_notes || '',
      });
    }
  }, [deal?.id]);

  // When AI updates deal financials in the background, merge into local state (only for empty fields)
  useEffect(() => {
    if (!deal) return;
    setLocalFinancials(prev => {
      const updates = {};
      if (!prev.ebitda && deal.ebitda) updates.ebitda = formatDollar(deal.ebitda);
      if (!prev.annual_revenue && deal.annual_revenue) updates.annual_revenue = formatDollar(deal.annual_revenue);
      if (!prev.annual_calls && deal.annual_calls) updates.annual_calls = Number(deal.annual_calls).toLocaleString('en-US');
      if (!prev.real_estate_value && deal.real_estate_value) updates.real_estate_value = formatDollar(deal.real_estate_value);
      if (Object.keys(updates).length === 0) return prev;
      return { ...prev, ...updates };
    });
  }, [deal?.ebitda, deal?.annual_revenue, deal?.annual_calls, deal?.real_estate_value]);

  // Load visits for this deal
  const loadVisits = async () => {
    if (!dealId) return;
    setVisitsLoading(true);
    try {
      const result = await getDealVisits(dealId);
      if (result.success) setVisits(result.visits || []);
    } catch (err) { console.error('Failed to load visits:', err); }
    finally { setVisitsLoading(false); }
  };

  useEffect(() => { loadVisits(); }, [dealId]);

  // Load info request for this deal
  const loadInfoRequest = async () => {
    if (!dealId) return;
    try {
      const result = await getInfoRequestByDealId(dealId);
      if (result) {
        setInfoRequest(result.info_request || null);
        setInfoData(result.submitted_data || null);
      } else {
        // No info_request record, but data may exist from AI extraction — fetch directly
        setInfoRequest(null);
        const [personnel, personnelSurvey, vehicles, utilities, preneed, facilities, crematory, computers, software, techOther] = await Promise.all([
          supabase.from('deal_personnel').select('*').eq('deal_id', dealId),
          supabase.from('deal_personnel_survey').select('*').eq('deal_id', dealId).maybeSingle(),
          supabase.from('deal_vehicles').select('*').eq('deal_id', dealId),
          supabase.from('deal_utilities').select('*').eq('deal_id', dealId),
          supabase.from('deal_preneed').select('*').eq('deal_id', dealId),
          supabase.from('deal_facilities').select('*').eq('deal_id', dealId),
          supabase.from('deal_crematory').select('*').eq('deal_id', dealId),
          supabase.from('deal_tech_computers').select('*').eq('deal_id', dealId),
          supabase.from('deal_tech_software').select('*').eq('deal_id', dealId),
          supabase.from('deal_tech_other').select('*').eq('deal_id', dealId),
        ]);
        const hasAnyData = [personnel, vehicles, utilities, preneed, facilities, crematory, computers, software, techOther]
          .some(r => r.data && r.data.length > 0) || !!personnelSurvey.data;
        if (hasAnyData) {
          setInfoData({
            ...normalizeBusinessInfoRows({
              personnel: personnel.data,
              vehicles: vehicles.data,
              utilities: utilities.data,
              preneed: preneed.data,
              facilities: facilities.data,
              crematory: crematory.data,
              computers: computers.data,
              software: software.data,
              techOther: techOther.data,
            }),
            personnel_survey: personnelSurvey.data || null,
          });
        }
      }
    } catch (err) {
      console.error('Failed to load info request:', err);
    }
  };

  useEffect(() => { loadInfoRequest(); }, [dealId]);

  // Load possible duplicates flagged by dedup_deal_extractions. Two queries
  // (the duplicate rows + their canonical counterparts) paired client-side
  // because supabase-js relational selects don't follow self-referential
  // foreign keys cleanly.
  const loadDuplicates = useCallback(async () => {
    if (!dealId) return;
    try {
      const personnelCols = 'id, first_name, last_name, position, phone_number, email_address, hire_date, source_file_id, possible_duplicate_of, dup_confidence, created_at';
      const vehicleCols   = 'id, year, make, model, vin, mileage, balance_owed, source_file_id, possible_duplicate_of, dup_confidence, created_at';

      const [pDups, vDups] = await Promise.all([
        supabase.from('deal_personnel').select(personnelCols).eq('deal_id', dealId).not('possible_duplicate_of', 'is', null),
        supabase.from('deal_vehicles').select(vehicleCols).eq('deal_id', dealId).not('possible_duplicate_of', 'is', null),
      ]);

      const pCanonIds = [...new Set((pDups.data || []).map(r => r.possible_duplicate_of))];
      const vCanonIds = [...new Set((vDups.data || []).map(r => r.possible_duplicate_of))];

      const [pCanon, vCanon] = await Promise.all([
        pCanonIds.length ? supabase.from('deal_personnel').select(personnelCols).in('id', pCanonIds) : Promise.resolve({ data: [] }),
        vCanonIds.length ? supabase.from('deal_vehicles').select(vehicleCols).in('id', vCanonIds) : Promise.resolve({ data: [] }),
      ]);

      const fileIds = new Set();
      (pDups.data || []).concat(pCanon.data || []).forEach(r => r.source_file_id && fileIds.add(r.source_file_id));
      (vDups.data || []).concat(vCanon.data || []).forEach(r => r.source_file_id && fileIds.add(r.source_file_id));
      let filesById = {};
      if (fileIds.size > 0) {
        const fr = await supabase.from('files').select('id, original_name').in('id', [...fileIds]);
        (fr.data || []).forEach(f => { filesById[f.id] = f.original_name; });
      }

      const pCanonById = Object.fromEntries((pCanon.data || []).map(r => [r.id, r]));
      const vCanonById = Object.fromEntries((vCanon.data || []).map(r => [r.id, r]));

      setDuplicatePairs({
        personnel: (pDups.data || [])
          .filter(r => pCanonById[r.possible_duplicate_of])
          .map(r => ({ dup: r, canon: pCanonById[r.possible_duplicate_of] })),
        vehicles: (vDups.data || [])
          .filter(r => vCanonById[r.possible_duplicate_of])
          .map(r => ({ dup: r, canon: vCanonById[r.possible_duplicate_of] })),
        files: filesById,
      });
    } catch (e) {
      console.error('loadDuplicates failed:', e);
    }
  }, [dealId]);

  useEffect(() => { loadDuplicates(); }, [dealId, loadDuplicates]);

  // Load the org's file_categories once. Used by the per-row recategorize
  // menu in the Documents tab.
  useEffect(() => {
    if (!organization?.id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('file_categories')
        .select('id, display_name, slug, parent_id, is_active, sort_order')
        .eq('organization_id', organization.id)
        .eq('is_active', true)
        .order('sort_order');
      if (!cancelled) setFileCategories(data || []);
    })();
    return () => { cancelled = true; };
  }, [organization?.id]);

  // Save a recategorization. Updates files.category_id + category_slug, and
  // writes a row to category_corrections so future AI runs can use it as a
  // few-shot example. Captures the AI's original suggestion (if any) so the
  // correction has a clear before/after.
  const handleRecategorizeFile = useCallback(async (file, newCategoryId) => {
    if (!file || !newCategoryId || file.category_id === newCategoryId) return;
    try {
      const newCat = fileCategories.find(c => c.id === newCategoryId);
      if (!newCat) throw new Error('Unknown category');
      const aiSuggestedCategoryId = file.category_id || null;

      // Update the file row first (so the UI reflects the change immediately)
      const { error: updErr } = await supabase
        .from('files')
        .update({ category_id: newCategoryId, category_slug: newCat.slug })
        .eq('id', file.id);
      if (updErr) throw updErr;

      // Try to enrich the correction record with the AI's prior summary +
      // document_type from activity_log (if this file has been categorized
      // before). Best-effort — failure here is fine.
      let summary = null;
      let documentType = null;
      try {
        const { data: logRows } = await supabase
          .from('activity_log')
          .select('metadata')
          .eq('deal_id', dealId)
          .eq('activity_type', 'ai_categorization')
          .order('created_at', { ascending: false })
          .limit(50);
        const m = (logRows || []).find(r => r.metadata?.file_id === file.id)?.metadata;
        if (m) { summary = m.summary || null; documentType = m.document_type || null; }
      } catch { /* ignore */ }

      const { error: corrErr } = await supabase.from('category_corrections').insert({
        organization_id: organization.id,
        file_id: file.id,
        file_name: file.original_name,
        document_type: documentType,
        summary,
        ai_suggested_category_id: aiSuggestedCategoryId,
        corrected_category_id: newCategoryId,
        corrected_by: userProfile?.id || null,
      });
      if (corrErr) console.warn('Failed to log category correction:', corrErr.message);

      // Refresh local file list
      const { data: refreshed } = await supabase
        .from('files').select('*').eq('deal_id', dealId).order('created_at', { ascending: false });
      if (refreshed) setAllDealFiles(refreshed);

      showToast(`Moved to ${newCat.display_name}`);
    } catch (err) {
      showToast('Recategorize failed: ' + err.message, 'error');
    }
  }, [fileCategories, dealId, organization?.id, userProfile?.id]);

  // Run dedup on demand from a button. Reloads pairs + Business Info after.
  const handleRunDedup = async () => {
    try {
      const r = await supabase.rpc('dedup_deal_extractions', { p_deal_id: dealId });
      const data = r.data || {};
      const total = (data.personnel_flagged || 0) + (data.vehicles_flagged || 0);
      showToast(total === 0 ? 'No duplicates found' : `Flagged ${data.personnel_flagged || 0} personnel + ${data.vehicles_flagged || 0} vehicle duplicate(s) for review`);
      await loadDuplicates();
      await loadInfoRequest();
    } catch (e) {
      showToast('Dedup failed: ' + e.message, 'error');
    }
  };

  const handleResolveDuplicate = async (kind, action, dupId, canonId) => {
    try {
      let r;
      if (action === 'smart_merge') {
        // Combine non-null fields from both rows into the canonical, then drop dup.
        const fnName = kind === 'personnel' ? 'merge_personnel_pair' : 'merge_vehicle_pair';
        r = await supabase.rpc(fnName, { p_canon_id: canonId, p_dup_id: dupId });
      } else if (action === 'drop') {
        // Hard delete duplicate, no merge — used when the dup is just wrong.
        const fnName = kind === 'personnel' ? 'merge_personnel_duplicate' : 'merge_vehicle_duplicate';
        r = await supabase.rpc(fnName, { p_drop_id: dupId });
      } else {
        // Dismiss flag — both records stay.
        const fnName = kind === 'personnel' ? 'dismiss_personnel_duplicate' : 'dismiss_vehicle_duplicate';
        r = await supabase.rpc(fnName, { p_dup_id: dupId });
      }
      if (r.error) throw r.error;
      // Re-run dedup so any flags orphaned by this resolve (e.g. row C pointed
      // to row B which we just merged into A — ON DELETE SET NULL nulled C's
      // pointer, but C might still be a dup of A) get re-flagged. Without this
      // a chain of 3+ duplicates collapsed one at a time falls out of the panel
      // before being fully resolved.
      await supabase.rpc('dedup_deal_extractions', { p_deal_id: dealId });
      await loadDuplicates();
      await loadInfoRequest();
    } catch (e) {
      showToast('Failed: ' + e.message, 'error');
    }
  };

  // Fetch ALL files for this deal (including SP-imported without task_id)
  useEffect(() => {
    if (!dealId) return;
    supabase.from('files')
      .select('*')
      .eq('deal_id', dealId)
      .order('created_at', { ascending: false })
      .then(function(result) {
        if (result.data) setAllDealFiles(result.data);
      });
  }, [dealId, deal?.updated_at]);

  // Poll for AI processing updates (every 5s while processing is active, max 5 minutes)
  useEffect(() => {
    const isProcessing = deal?.ai_summary?.total_expected &&
      (deal.ai_summary.documents_processed || 0) < deal.ai_summary.total_expected;
    if (!isProcessing) return;

    const startedAt = Date.now();
    const MAX_POLL_MS = 5 * 60 * 1000; // 5 minutes

    const interval = setInterval(() => {
      if (Date.now() - startedAt > MAX_POLL_MS) {
        clearInterval(interval);
        console.warn('AI processing poll stopped after 5 minutes — some files may have failed');
        return;
      }
      refreshDeal();
      loadInfoRequest();
    }, 5000);

    return () => clearInterval(interval);
  }, [deal?.ai_summary?.documents_processed, deal?.ai_summary?.total_expected, refreshDeal]);

  const handleSendInfoRequest = async (emailTo) => {
    if (!emailTo) return;
    setShowInfoEmailModal(false);
    setSendingInfoRequest(true);
    try {
      await sendInfoRequest({
        dealId,
        organizationId: organization?.id,
        userId: userProfile?.id,
        deal,
        sellerEmail: emailTo,
        sellerContact: deal.primary_contact || '',
        primaryColor,
      });
      setInfoRequestSent(true);
      setTimeout(() => setInfoRequestSent(false), 3000);
      await loadInfoRequest();
    } catch (err) {
      console.error('Failed to send info request:', err);
      alert('Failed to send info request: ' + err.message);
    } finally {
      setSendingInfoRequest(false);
    }
  };

  // Phase U.2 — Send the standalone HR survey to the seller. Generates
  // a scope='hr_survey' token via the edge function and emails them a
  // link that opens just the HR survey (no other BI sections visible).
  const handleSendHrSurvey = async () => {
    if (!hrSurveyEmail || !hrSurveyEmail.includes('@')) return;
    setSendingHrSurvey(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const res = await fetch(
        import.meta.env.VITE_SUPABASE_URL + '/functions/v1/send-hr-survey-invite',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + session.access_token,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            deal_id: dealId,
            email: hrSurveyEmail,
            name: hrSurveyName || null,
          }),
        }
      );
      const result = await res.json();
      if (!res.ok || !result.ok) throw new Error(result.error || 'Send failed');
      setHrSurveySent(true);
      setShowHrSurveyModal(false);
      setTimeout(() => setHrSurveySent(false), 3000);
      showToast('HR survey sent to ' + hrSurveyEmail);
    } catch (err) {
      console.error('Send HR survey failed:', err);
      showToast('Failed to send HR survey: ' + (err.message || 'unknown'), 'error');
    } finally {
      setSendingHrSurvey(false);
    }
  };

  // Open the doc request modal pre-populated with a third-party email.
  // `roleKey` is 'accountant' | 'broker' — on send, scope is re-derived from
  // selectedDocEmail so this only drives the initial task pre-selection.
  const handleRequestDocsForThirdParty = (email, contactName, role, roleKey) => {
    var docTasks = tasks.filter(function(t) { return t.is_doc_request && (t.status === 'Not Started' || t.status === 'Requested'); });
    if (docTasks.length === 0) { showToast('No pending document requests', 'error'); return; }

    // Pre-select tasks already assigned to this contact (if any). Admin can
    // add more before sending — any newly-added selections get auto-assigned
    // on send in handleSendRequest.
    var preAssigned = docTasks.filter(function(t) { return t.assigned_to_contact === roleKey; });
    if (preAssigned.length > 0) {
      setSelectedDocIds(new Set(preAssigned.map(function(t) { return t.id; })));
    }

    var msg = 'Dear ' + contactName + ',\n\nAs ' + role + ' for ' + deal.name + ', we are requesting the following documents on behalf of the seller:\n\n';
    var listTasks = preAssigned.length > 0 ? preAssigned : docTasks;
    listTasks.forEach(function(task) { msg += '\u2022 ' + task.name + '\n'; });
    msg += '\nPlease upload these documents at your earliest convenience.';
    setRequestMsg(msg);
    setSelectedDocEmail(email);
    setShowModal('request');
  };

  const handleExportBusinessInfo = async (section) => {
    if (!infoData) return;
    const dealName = deal?.name || 'Deal';
    const safeName = dealName.replace(/[^a-z0-9]/gi, '_');
    const escapeCsv = (val) => {
      const s = String(val == null ? '' : val);
      return s.includes(',') || s.includes('"') || s.includes('\n') ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    const downloadCsv = (headers, dataRows, filename) => {
      const csv = [headers.map(escapeCsv).join(','), ...dataRows.map(r => r.map(escapeCsv).join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    };

    // Personnel-only export. Integration role gets a stripped version
    // without compensation, PTO, or benefits — just identity + role.
    if (section === 'personnel' && infoData.personnel?.length > 0) {
      if (isIntegration) {
        downloadCsv(
          ['Last Name','Middle Initial','First Name','Phone Number','Email Address','Hire Date','Position Title','Employment Status'],
          infoData.personnel.map(p => [p.last_name, p.middle_initial||'', p.first_name, p.phone_number||'', p.email_address||'', p.hire_date||'', p.position, p.employment_type]),
          `${safeName}_Personnel.csv`
        );
      } else {
        downloadCsv(
          ['Last Name','Middle Initial','First Name','Phone Number','Email Address','Hire Date (Seniority)','Position Title','Employment Status (FULL/PART TIME)','Hours worked per/year','Hourly Rate','Salary Rate','YTD PTO Accrued','PTO Entitlement (for the year)','401(k)','Company Credit Card','Phone Stipend','Company Vehicle','Life Insurance','Clothing Allowance'],
          infoData.personnel.map(p => [p.last_name, p.middle_initial||'', p.first_name, p.phone_number||'', p.email_address||'', p.hire_date||'', p.position, p.employment_type, p.hours_per_year||'', p.hourly_rate||'', p.salary_rate||'', p.ytd_pto_accrued||'', p.pto_entitlement||'', p.has_401k?'Yes':'No', p.company_credit_card?'Yes':'No', p.phone_stipend?'Yes':'No', p.company_vehicle?'Yes':'No', p.life_insurance?'Yes':'No', p.clothing_allowance?'Yes':'No']),
          `${safeName}_Personnel.csv`
        );
      }
      showToast('Personnel exported');
      return;
    }

    // Vehicles-only export
    if (section === 'vehicles' && infoData.vehicles?.length > 0) {
      downloadCsv(
        ['Year', 'Make', 'Model', 'VIN', 'Mileage', 'Loan Balance', 'Est. Value'],
        infoData.vehicles.map(v => [v.year, v.make, v.model, v.vin, v.mileage||'', v.balance_owed||'', v.kelly_blue_book||'']),
        `${safeName}_Vehicles.csv`
      );
      showToast('Vehicles exported');
      return;
    }

    // Full export — XLSX with tabs per category
    try {
      const XLSX = await import('https://cdn.sheetjs.com/xlsx-0.20.3/package/xlsx.mjs');
      const wb = XLSX.utils.book_new();

      if (infoData.personnel?.length > 0) {
        const ws = isIntegration
          ? XLSX.utils.aoa_to_sheet([
              ['Last Name','MI','First Name','Phone','Email','Hire Date','Position','Status'],
              ...infoData.personnel.map(p => [p.last_name, p.middle_initial||'', p.first_name, p.phone_number||'', p.email_address||'', p.hire_date||'', p.position, p.employment_type])
            ])
          : XLSX.utils.aoa_to_sheet([
              ['Last Name','MI','First Name','Phone','Email','Hire Date','Position','Status','Hrs/Yr','Hourly Rate','Salary','YTD PTO','PTO Entitlement','401(k)','Credit Card','Phone Stipend','Co. Vehicle','Life Insurance','Clothing'],
              ...infoData.personnel.map(p => [p.last_name, p.middle_initial||'', p.first_name, p.phone_number||'', p.email_address||'', p.hire_date||'', p.position, p.employment_type, p.hours_per_year||'', p.hourly_rate||'', p.salary_rate||'', p.ytd_pto_accrued||'', p.pto_entitlement||'', p.has_401k?'Yes':'No', p.company_credit_card?'Yes':'No', p.phone_stipend||'', p.company_vehicle?'Yes':'No', p.life_insurance?'Yes':'No', p.clothing_allowance||''])
            ]);
        XLSX.utils.book_append_sheet(wb, ws, 'Personnel');
      }

      if (infoData.vehicles?.length > 0) {
        const ws = XLSX.utils.aoa_to_sheet([
          ['Year','Make','Model','VIN','Mileage','Has Loan','Loan Holder','Balance Owed','Est. Value'],
          ...infoData.vehicles.map(v => [v.year, v.make, v.model, v.vin, v.mileage||'', v.has_loan?'Yes':'No', v.loan_holder||'', v.balance_owed||'', v.kelly_blue_book||''])
        ]);
        XLSX.utils.book_append_sheet(wb, ws, 'Vehicles');
      }

      if (infoData.utilities?.length > 0) {
        const ws = XLSX.utils.aoa_to_sheet([
          ['Type','Vendor','Account #','Has Contract','Contract Details'],
          ...infoData.utilities.map(u => [u.utility_type, u.vendor||u.vendor_name, u.account_number, u.has_contract?'Yes':'No', u.contract_details||''])
        ]);
        XLSX.utils.book_append_sheet(wb, ws, 'Utilities');
      }

      if (infoData.preneed?.length > 0) {
        const ws = XLSX.utils.aoa_to_sheet([
          ['Company','Contract #','Contact'],
          ...infoData.preneed.map(p => [p.company_name, p.contract_number, p.contact])
        ]);
        XLSX.utils.book_append_sheet(wb, ws, 'Pre-Need');
      }

      if (infoData.facilities?.length > 0) {
        const ws = XLSX.utils.aoa_to_sheet([
          ['Type','Vendor','Contact','Phone','Contract Details'],
          ...infoData.facilities.map(f => [f.facility_type, f.vendor_name, f.contact, f.phone, f.contract_details||''])
        ]);
        XLSX.utils.book_append_sheet(wb, ws, 'Facilities');
      }

      if (infoData.crematory?.length > 0) {
        const ws = XLSX.utils.aoa_to_sheet([
          ['Has Crematory','Machine','Install Date','Last Service','Capacity','EPA Cert','Regulatory','Maintenance'],
          ...infoData.crematory.map(c => [c.has_crematory?'Yes':'No', c.machine, c.installation_date||'', c.last_service_date||'', c.capacity, c.epa_certification||'', c.regulatory_info||'', c.maintenance_program||''])
        ]);
        XLSX.utils.book_append_sheet(wb, ws, 'Crematory');
      }

      if (infoData.tech_computers?.length > 0) {
        const ws = XLSX.utils.aoa_to_sheet([
          ['Type','Make','Model','Serial #','Notes'],
          ...infoData.tech_computers.map(c => [c.computer_type, c.make, c.model, c.serial_number, c.notes||''])
        ]);
        XLSX.utils.book_append_sheet(wb, ws, 'Computers');
      }

      if (infoData.tech_peripherals?.length > 0) {
        const ws = XLSX.utils.aoa_to_sheet([
          ['Type','Make','Model','Serial #','Notes'],
          ...infoData.tech_peripherals.map(c => [c.computer_type, c.make, c.model, c.serial_number, c.notes||''])
        ]);
        XLSX.utils.book_append_sheet(wb, ws, 'Peripherals');
      }

      if (infoData.tech_software?.length > 0) {
        const ws = XLSX.utils.aoa_to_sheet([
          ['Name','Monthly Cost','Annual Cost','Renewal Date','Login URL','Username','Notes'],
          ...infoData.tech_software.map(s => [s.name, s.monthly_cost||'', s.annual_cost||'', s.renewal_date||'', s.login_url||'', s.username||'', s.notes||''])
        ]);
        XLSX.utils.book_append_sheet(wb, ws, 'Software');
      }

      if (infoData.tech_other?.length > 0) {
        const ws = XLSX.utils.aoa_to_sheet([
          ['Name','URL','Notes'],
          ...infoData.tech_other.map(o => [o.name||'', o.url||'', o.notes||''])
        ]);
        XLSX.utils.book_append_sheet(wb, ws, 'Tech - Other');
      }

      if (wb.SheetNames.length === 0) {
        showToast('No data to export', 'error');
        return;
      }

      XLSX.writeFile(wb, `${safeName}_Business_Info.xlsx`);
      showToast('Business info exported to Excel');
    } catch (err) {
      console.error('XLSX export error:', err);
      showToast('Export failed: ' + err.message, 'error');
    }
  };

  // State abbreviation lookup
  const stateAbbrev = (stateName) => {
    const map = { 'Alabama':'AL','Alaska':'AK','Arizona':'AZ','Arkansas':'AR','California':'CA','Colorado':'CO','Connecticut':'CT','Delaware':'DE','Florida':'FL','Georgia':'GA','Hawaii':'HI','Idaho':'ID','Illinois':'IL','Indiana':'IN','Iowa':'IA','Kansas':'KS','Kentucky':'KY','Louisiana':'LA','Maine':'ME','Maryland':'MD','Massachusetts':'MA','Michigan':'MI','Minnesota':'MN','Mississippi':'MS','Missouri':'MO','Montana':'MT','Nebraska':'NE','Nevada':'NV','New Hampshire':'NH','New Jersey':'NJ','New Mexico':'NM','New York':'NY','North Carolina':'NC','North Dakota':'ND','Ohio':'OH','Oklahoma':'OK','Oregon':'OR','Pennsylvania':'PA','Rhode Island':'RI','South Carolina':'SC','South Dakota':'SD','Tennessee':'TN','Texas':'TX','Utah':'UT','Vermont':'VT','Virginia':'VA','Washington':'WA','West Virginia':'WV','Wisconsin':'WI','Wyoming':'WY' };
    return map[stateName] || stateName?.slice(0, 2).toUpperCase() || '';
  };

  // Per-location distance to nearest LFG
  const locationDistances = (() => {
    const lfgWithCoords = lfgLocations.filter(l => l.latitude && l.longitude);
    if (!lfgWithCoords.length) return {};
    const haversine = (lat1, lon1, lat2, lon2) => {
      const R = 3959;
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLon = (lon2 - lon1) * Math.PI / 180;
      const a = Math.sin(dLat/2)**2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon/2)**2;
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    };
    const result = {};
    dealLocations.forEach(loc => {
      if (!loc.latitude || !loc.longitude) return;
      let minDist = Infinity;
      lfgWithCoords.forEach(lfg => {
        const d = haversine(loc.latitude, loc.longitude, lfg.latitude, lfg.longitude);
        if (d < minDist) minDist = d;
      });
      if (minDist < Infinity) result[loc.id] = Math.round(minDist);
    });
    return result;
  })();

  // Local state for text inputs (save onBlur, not every keystroke)
  const [localContact, setLocalContact] = useState('');
  const [localEmail, setLocalEmail] = useState('');
  const [localPersonalEmail, setLocalPersonalEmail] = useState('');
  const [localPhone, setLocalPhone] = useState('');
  const [localCounselName, setLocalCounselName] = useState('');
  const [localCounselFirm, setLocalCounselFirm] = useState('');
  const [localCounselEmail, setLocalCounselEmail] = useState('');
  const [localCounselPhone, setLocalCounselPhone] = useState('');
  const [localAccountantName, setLocalAccountantName] = useState('');
  const [localAccountantFirm, setLocalAccountantFirm] = useState('');
  const [localAccountantEmail, setLocalAccountantEmail] = useState('');
  const [localAccountantPhone, setLocalAccountantPhone] = useState('');
  const [localBrokerName, setLocalBrokerName] = useState('');
  const [localBrokerFirm, setLocalBrokerFirm] = useState('');
  const [localBrokerEmail, setLocalBrokerEmail] = useState('');
  const [localBrokerPhone, setLocalBrokerPhone] = useState('');
  const [localDates, setLocalDates] = useState({});

  // Sync local state when deal loads or changes externally
  useEffect(() => {
    if (deal) {
      setLocalContact(deal.primary_contact || '');
      setLocalEmail(deal.primary_email || '');
      setLocalPersonalEmail(deal.personal_email || '');
      setLocalPhone(formatPhone(deal.primary_phone || ''));
      setLocalCounselName(deal.counsel_name || '');
      setLocalCounselFirm(deal.counsel_firm || '');
      setLocalCounselEmail(deal.counsel_email || '');
      setLocalCounselPhone(formatPhone(deal.counsel_phone || ''));
      setLocalAccountantName(deal.accountant_name || '');
      setLocalAccountantFirm(deal.accountant_firm || '');
      setLocalAccountantEmail(deal.accountant_email || '');
      setLocalAccountantPhone(formatPhone(deal.accountant_phone || ''));
      setLocalBrokerName(deal.broker_name || '');
      setLocalBrokerFirm(deal.broker_firm || '');
      setLocalBrokerEmail(deal.broker_email || '');
      setLocalBrokerPhone(formatPhone(deal.broker_phone || ''));
      setLocalDates({
        first_visit_date: deal.first_visit_date || '',
        nda_sent_date: deal.nda_sent_date || '',
        nda_executed_date: deal.nda_executed_date || '',
        prelim_request_date: deal.prelim_request_date || '',
        prelim_info_date: deal.prelim_info_date || '',
        loi_date: deal.loi_date || '',
        target_close_date: deal.target_close_date || '',
        actual_close_date: deal.actual_close_date || '',
      });
    }
  }, [deal?.id, deal?.primary_contact, deal?.primary_email, deal?.primary_phone,
      deal?.counsel_name, deal?.counsel_firm, deal?.counsel_email, deal?.counsel_phone,
      deal?.accountant_name, deal?.accountant_firm, deal?.accountant_email, deal?.accountant_phone,
      deal?.broker_name, deal?.broker_firm, deal?.broker_email, deal?.broker_phone,
      deal?.first_visit_date, deal?.nda_sent_date, deal?.nda_executed_date,
      deal?.prelim_request_date, deal?.prelim_info_date, deal?.loi_date,
      deal?.target_close_date, deal?.actual_close_date]);

  // Use provided colors or fall back to LFG defaults
  const primaryColor = pc || LFG_COLORS.navy;
  const secondaryColor = sc || LFG_COLORS.gray;
  const { displayPrimary, displaySecondary, displayNavy, displayGreen } = useDisplayColors(pc, sc);

  // Check which deal locations are already in LFG
  const getLocationsInLfg = () => {
    if (!dealLocations.length || !lfgLocations.length) return new Set();
    
    const inLfg = new Set();
    dealLocations.forEach(dl => {
      const exists = lfgLocations.some(lfg => 
        lfg.name === dl.name && 
        (lfg.address || '') === (dl.address || '') &&
        (lfg.city || '') === (dl.city || '')
      );
      if (exists) inLfg.add(dl.id);
    });
    return inLfg;
  };

  const locationsInLfg = getLocationsInLfg();
  const allLocationsInLfg = dealLocations.length > 0 && dealLocations.every(dl => locationsInLfg.has(dl.id));
  const someLocationsInLfg = locationsInLfg.size > 0;
  const locationsNotInLfg = dealLocations.filter(dl => !locationsInLfg.has(dl.id));

  // Calculate distance to nearest LFG location
  const nearestLfgDistance = (() => {
    if (allLocationsInLfg) return null; // This IS an LFG location
    const lfgWithCoords = lfgLocations.filter(l => l.latitude && l.longitude);
    const dealWithCoords = dealLocations.filter(l => l.latitude && l.longitude);
    if (!lfgWithCoords.length || !dealWithCoords.length) return null;

    const haversine = (lat1, lon1, lat2, lon2) => {
      const R = 3959;
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLon = (lon2 - lon1) * Math.PI / 180;
      const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                Math.sin(dLon/2) * Math.sin(dLon/2);
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    };

    let minDist = Infinity;
    dealWithCoords.forEach(dl => {
      lfgWithCoords.forEach(lfg => {
        const d = haversine(dl.latitude, dl.longitude, lfg.latitude, lfg.longitude);
        if (d < minDist) minDist = d;
      });
    });
    return minDist < Infinity ? Math.round(minDist) : null;
  })();

  const resetLocationForm = () => {
    setLocationForm({
      name: '', address: '', city: '', state: '', zip: '', 
      latitude: '', longitude: '', is_crematory: false, notes: ''
    });
    setShowLocationForm(false);
    setEditingLocation(null);
  };

  // Generate and copy seller portal link. Reuses an existing valid token
  // rather than always minting a new one (see generate_seller_token) —
  // so the "days left" shown here reflects the real remaining window,
  // which may be less than a fresh link's full 7 days if one was already
  // issued and is still live.
  const handleCopySellerLink = async () => {
    try {
      setGeneratingLink(true);
      const result = await generateSellerToken(dealId);
      const token = Array.isArray(result) ? result[0].token : result.token;
      const expiresAt = Array.isArray(result) ? result[0].expires_at : result.expires_at;
      const url = buildSellerPortalUrl(token);
      const daysLeft = Math.max(1, Math.round((new Date(expiresAt) - new Date()) / 86400000));

      await navigator.clipboard.writeText(url);
      setSellerLinkCopied(true);
      showToast(`Seller portal link copied! Valid for ${daysLeft} more day${daysLeft === 1 ? '' : 's'}.`);

      setTimeout(() => setSellerLinkCopied(false), 3000);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setGeneratingLink(false);
    }
  };

  // Convert deal locations to LFG Locations (only those not already added)
  const handleConvertToLfgLocations = async () => {
    if (!deal || locationsNotInLfg.length === 0) return;
    if (!deal.actual_close_date) {
      showToast('Set the actual close date before converting to LFG', 'error');
      return;
    }

    const count = locationsNotInLfg.length;
    if (!confirm(`Add ${count} location(s) from "${deal.name}" to LFG Locations?`)) {
      return;
    }
    
    setConverting(true);
    try {
      let added = 0;
      for (const loc of locationsNotInLfg) {
        await addLfgLocation({
          name: loc.name || deal.name,
          address: loc.address,
          city: loc.city,
          state: loc.state,
          zip: loc.zip,
          latitude: loc.latitude,
          longitude: loc.longitude,
          is_crematory: loc.is_crematory || false,
          notes: `From: ${deal.name}${loc.is_crematory ? ' (Crematory)' : ''}`
        });
        added++;
      }
      showToast(`Added ${added} location(s) to LFG Locations!`);
    } catch (err) {
      showToast(err.message || 'Failed to add locations', 'error');
    } finally {
      setConverting(false);
    }
  };

  // Geocode location address
  const handleGeocodeLocation = async () => {
    if (!locationForm.address || !locationForm.city || !locationForm.state) {
      showToast('Enter address, city, and state first', 'error');
      return;
    }
    setGeocoding(true);
    try {
      const result = await geocodeAddress(locationForm.address, locationForm.city, locationForm.state, locationForm.zip);
      if (result) {
        setLocationForm(prev => ({
          ...prev,
          latitude: result.latitude.toFixed(6),
          longitude: result.longitude.toFixed(6)
        }));
        showToast('Coordinates found!');
      } else {
        showToast('Could not find coordinates', 'error');
      }
    } catch (err) {
      showToast('Geocoding failed', 'error');
    } finally {
      setGeocoding(false);
    }
  };

  // Save location (add or update)
  const handleSaveLocation = async () => {
    if (!locationForm.name.trim()) {
      showToast('Enter a location name', 'error');
      return;
    }
    setLocationSaving(true);
    try {
      let lat = locationForm.latitude ? parseFloat(locationForm.latitude) : null;
      let lng = locationForm.longitude ? parseFloat(locationForm.longitude) : null;

      // Auto-geocode if we have address but no coordinates
      let geocodeAttempted = false;
      if ((!lat || !lng) && locationForm.address && locationForm.city && locationForm.state) {
        geocodeAttempted = true;
        try {
          const coords = await geocodeAddress(locationForm.address, locationForm.city, locationForm.state, locationForm.zip);
          if (coords) {
            lat = coords.latitude;
            lng = coords.longitude;
          }
        } catch (geoErr) {
          console.error('Auto-geocode failed:', geoErr);
        }
      }

      const data = {
        name: locationForm.name.trim(),
        address: locationForm.address.trim() || null,
        city: locationForm.city.trim() || null,
        state: locationForm.state.trim().toUpperCase() || null,
        zip: locationForm.zip.trim() || null,
        latitude: lat,
        longitude: lng,
        is_crematory: locationForm.is_crematory,
        notes: locationForm.notes.trim() || null
      };

      if (editingLocation) {
        await updateDealLocation(editingLocation.id, data);
        if (geocodeAttempted && (!lat || !lng)) {
          showToast('Location updated — couldn\'t auto-find coordinates, click 🔍 to retry', 'warning');
        } else {
          showToast('Location updated');
        }
      } else {
        await addDealLocation(data);
        if (lat && lng) {
          showToast('Location added (coordinates auto-detected)');
        } else if (geocodeAttempted) {
          showToast('Location added — couldn\'t auto-find coordinates, click 🔍 to retry', 'warning');
        } else {
          showToast('Location added');
        }
      }
      resetLocationForm();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setLocationSaving(false);
    }
  };

  // Edit existing location
  const handleEditLocation = (loc) => {
    setLocationForm({
      name: loc.name || '',
      address: loc.address || '',
      city: loc.city || '',
      state: loc.state || '',
      zip: loc.zip || '',
      latitude: loc.latitude?.toString() || '',
      longitude: loc.longitude?.toString() || '',
      is_crematory: loc.is_crematory || false,
      notes: loc.notes || ''
    });
    setEditingLocation(loc);
    setShowLocationForm(true);
  };

  // Delete location
  const handleDeleteLocation = async (loc) => {
    if (!confirm(`Delete location "${loc.name}"?`)) return;
    try {
      await deleteDealLocation(loc.id);
      showToast('Location deleted');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  // Notes handlers
  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setNoteSaving(true);
    try {
      await addNote(newNote, userProfile?.full_name || 'Unknown', userProfile?.id);
      setNewNote('');
      showToast('Note added');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setNoteSaving(false);
    }
  };

  const handleUpdateNote = async (noteId) => {
    if (!editingNoteContent.trim()) return;
    try {
      await updateNote(noteId, editingNoteContent);
      setEditingNoteId(null);
      setEditingNoteContent('');
      showToast('Note updated');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!confirm('Delete this note?')) return;
    try {
      await deleteNote(noteId);
      showToast('Note deleted');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const startEditingNote = (note) => {
    setEditingNoteId(note.id);
    setEditingNoteContent(note.content);
  };

  // Create SharePoint folder structure if needed, then open. The folder
  // name on disk gets sanitized by the edge function (trailing periods
  // and forbidden chars stripped), so we mirror that locally so the
  // open URL lands on the actual folder.
  const handleCreateSharePointFolders = async () => {
    if (!deal) return;
    var safeName = sharePointSafeSegment(deal.name);
    var folderPath = '/sites/Acquisitions/Shared Documents/Acquisitions/' + safeName;
    var spUrl = 'https://walkerfuneralhomeco.sharepoint.com/sites/Acquisitions/Shared%20Documents/Forms/AllItems.aspx?id=' + encodeURIComponent(folderPath) + '&viewid=658e47d3-098f-4e46-9bc4-69330c8ad528';
    
    setCreatingSPFolders(true);
    try {
      // Create folder structure (idempotent - safe to call if folders already exist)
      await createSharePointDealStructure(deal.name, deal.organization_id);
      window.open(spUrl, '_blank');
    } catch (err) {
      // If creation fails, still try to open (folders may already exist)
      window.open(spUrl, '_blank');
      showToast(err.message || 'SharePoint error', 'error');
    } finally {
      setCreatingSPFolders(false);
    }
  };

  // Scan SharePoint folder and import untracked files (server-side)
  const handleScanSharePoint = async () => {
    if (!deal) return;
    setScanningSharePoint(true);
    try {
      var session = await supabase.auth.getSession();
      var token = session.data.session?.access_token;
      var response = await fetch(
        import.meta.env.VITE_SUPABASE_URL + '/functions/v1/sharepoint-sync',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + (token || ''),
          },
          body: JSON.stringify({ action: 'sync-deal-files', dealId: dealId }),
        }
      );
      var result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Sync failed');
      
      if (result.imported > 0) {
        showToast('Imported ' + result.imported + ' file(s) from SharePoint (' + result.total + ' total)');
        refreshDeal();
        // Refresh the files list, then auto-analyze any uncategorized files
        var refreshed = await supabase.from('files').select('*').eq('deal_id', dealId).order('created_at', { ascending: false });
        if (refreshed.data) setAllDealFiles(refreshed.data);
        handleAnalyzeUncategorized({ silent: true });
      } else {
        showToast(result.message || 'All SharePoint files are already tracked');
      }
    } catch (err) {
      showToast('SharePoint sync failed: ' + err.message, 'error');
    } finally {
      setScanningSharePoint(false);
    }
  };

  // Run AI categorization + DD matching on every file on the deal that has no
  // category yet. Used for SharePoint-synced files (which bypass AI on insert)
  // and as a backfill for Toland-Herzig-style legacy imports.
  // Ad-hoc internal file upload — drops files straight onto the deal (no
  // task association required), runs AI categorization + DD matching, and
  // syncs to SharePoint in the background. Mirrors the seller-portal upload
  // path but for LFG team members operating from inside DealView.
  const handleInternalFilesUpload = async (fileList) => {
    if (!deal || !fileList || fileList.length === 0) return;
    if (internalUploading) return;
    const files = Array.from(fileList);
    setInternalUploading(true);
    setInternalUploadProgress({ done: 0, total: files.length });

    var spUrl = import.meta.env.VITE_SUPABASE_URL;
    var spKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    var failures = [];
    var done = 0;

    for (var i = 0; i < files.length; i++) {
      var file = files[i];
      try {
        // 1. Upload to Supabase storage (organization-scoped path)
        var ext = file.name.includes('.') ? file.name.split('.').pop() : '';
        var storageName = Date.now() + '-' + Math.random().toString(36).slice(2, 9) + (ext ? '.' + ext : '');
        var storagePath = (organization?.id || deal.organization_id) + '/' + dealId + '/internal-uploads/' + storageName;
        var up = await supabase.storage
          .from('deal-documents')
          .upload(storagePath, file, { cacheControl: '3600', upsert: false });
        if (up.error) throw up.error;

        // 2. Create files row (no task_id — this is an ad-hoc deal-scoped file)
        var ins = await supabase.from('files').insert({
          deal_id: dealId,
          organization_id: organization?.id || deal.organization_id,
          name: storageName,
          original_name: file.name,
          storage_path: storagePath,
          size_bytes: file.size,
          mime_type: file.type || 'application/octet-stream',
          uploaded_by_id: userProfile?.id || null,
          uploaded_by_name: userProfile?.full_name || 'Internal',
          uploaded_by_type: 'user',
        }).select('id').single();
        if (ins.error) throw ins.error;
        var newFileId = ins.data.id;

        // 3. Fire AI categorization + DD match (non-blocking, same pattern
        //    as Analyze button — extracts PDF text in the browser first,
        //    same helper the Analyze button uses, instead of leaving any
        //    real-text PDF over 4MB to fail server-side as "too large for
        //    vision" just because this path used to skip extraction.
        var bodyPayload = { file_id: newFileId, deal_id: dealId };
        try {
          var pdfExtra = await extractPdfExtraFields(file);
          Object.assign(bodyPayload, pdfExtra);
          var xlsxExtra = await extractXlsxExtraFields(file);
          Object.assign(bodyPayload, xlsxExtra);
        } catch (e) { /* best-effort */ }
        fetch(spUrl + '/functions/v1/auto-categorize-document', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', apikey: spKey },
          body: JSON.stringify(bodyPayload),
        }).catch(function() {});
        fetch(spUrl + '/functions/v1/ai-match-dd-item', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', apikey: spKey },
          body: JSON.stringify(bodyPayload),
        }).catch(function() {});

        // 4. SharePoint sync (non-blocking)
        try {
          await uploadToSharePoint(file, deal.name, file.name, null);
        } catch (spErr) {
          console.warn('SharePoint sync failed for ' + file.name + ':', spErr.message);
        }
      } catch (err) {
        console.error('Internal upload failed for ' + file.name, err);
        failures.push({ name: file.name, reason: err.message || 'unknown error' });
      }
      done++;
      setInternalUploadProgress({ done: done, total: files.length });
    }

    // Refresh the deal file list
    var refreshed = await supabase.from('files').select('*').eq('deal_id', dealId).order('created_at', { ascending: false });
    if (refreshed.data) setAllDealFiles(refreshed.data);

    setInternalUploading(false);
    setInternalUploadProgress(null);
    if (internalUploadInputRef.current) internalUploadInputRef.current.value = '';

    var ok = files.length - failures.length;
    if (failures.length === 0) {
      showToast('Uploaded ' + ok + ' file(s) — AI analysis running');
    } else {
      var first = failures[0];
      showToast(
        'Uploaded ' + ok + ' of ' + files.length + ' · ' + failures.length + ' failed (' + first.name + ': ' + first.reason + ')',
        failures.length === files.length ? 'error' : 'warning'
      );
    }
  };

  const [retryingFileId, setRetryingFileId] = useState(null);
  const [reanalyzingFileId, setReanalyzingFileId] = useState(null);

  // Force a fresh AI pass on a file that already succeeded — unlike Retry
  // (only for files that errored), this is for when a later fix (parsing,
  // extraction, prompt) means an already-processed file is worth re-reading.
  // Goes through the same client-side PDF/XLSX extraction as every other
  // upload path so a >4MB PDF doesn't hit the server's "too large for
  // vision" wall just because it's being re-run instead of freshly uploaded.
  const handleReanalyzeFile = async (file) => {
    if (!file?.id || reanalyzingFileId || isViewer) return;
    setReanalyzingFileId(file.id);
    try {
      showToast('Re-analyzing ' + (file.original_name || file.name || 'file') + '…');
      const spUrl = import.meta.env.VITE_SUPABASE_URL;
      const spKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const bodyPayload = { file_id: file.id, deal_id: dealId };
      const asExtractableFile = {
        type: file.mime_type,
        name: file.original_name || file.name,
        arrayBuffer: async () => {
          const dl = await supabase.storage.from('deal-documents').download(file.storage_path);
          if (dl.error) throw new Error('storage download failed: ' + dl.error.message);
          return await dl.data.arrayBuffer();
        },
      };
      try {
        Object.assign(bodyPayload, await extractPdfExtraFields(asExtractableFile));
        Object.assign(bodyPayload, await extractXlsxExtraFields(asExtractableFile));
      } catch (e) { /* best-effort — server falls back to its own parsing */ }

      const resp = await fetch(spUrl + '/functions/v1/auto-categorize-document', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', apikey: spKey },
        body: JSON.stringify(bodyPayload),
      });
      if (!resp.ok) {
        let msg = 'HTTP ' + resp.status;
        try { const b = await resp.json(); if (b && b.error) msg = b.error; } catch (e) { /* non-JSON body */ }
        showToast('Re-analyze failed: ' + msg, 'error');
        return;
      }
      showToast('Re-analysis complete');
    } catch (e) {
      let msg = e.message || 'Re-analyze failed';
      if (/failed to fetch/i.test(msg)) {
        msg = 'Failed to fetch — likely the extracted page images were too large to send. Try re-uploading a lower-resolution scan, or split the PDF into smaller files.';
      }
      showToast(msg, 'error');
    } finally {
      setReanalyzingFileId(null);
    }
  };

  const handleAnalyzeUncategorized = async (opts) => {
    if (!deal) return;
    var silent = opts && opts.silent;
    if (analyzingFiles) return;
    setAnalyzingFiles(true);
    try {
      // Skip files that previously failed analysis — re-running the same
      // encrypted PDF or oversize doc just burns Anthropic tokens. Users
      // can clear ai_error_reason (re-upload the file) to retry.
      var { data: toAnalyze, error: qErr } = await supabase
        .from('files')
        .select('id, name, mime_type, storage_path, size_bytes')
        .eq('deal_id', dealId)
        .is('category_id', null)
        .is('ai_error_reason', null)
        .order('created_at', { ascending: true });
      if (qErr) throw qErr;
      if (!toAnalyze || toAnalyze.length === 0) {
        if (!silent) showToast('All files already categorized');
        setAnalyzingFiles(false);
        setAnalyzeProgress(null);
        return;
      }
      if (!silent) showToast('Analyzing ' + toAnalyze.length + ' file(s)…');
      setAnalyzeProgress({ done: 0, total: toAnalyze.length });

      var spUrl = import.meta.env.VITE_SUPABASE_URL;
      var spKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      var done = 0;
      var failures = []; // { name, reason }

      // Persist a failure reason on the file row so it's visible in the UI
      // even when the server-side catch never ran (timeout, OOM, 504, etc.).
      // The server clears ai_error_reason on success, so this is safe to
      // write before each call too.
      async function recordClientFailure(fileId, reason) {
        try {
          await supabase.from('files').update({
            ai_error_reason: (reason || 'unknown error').slice(0, 500),
            ai_error_at: new Date().toISOString(),
          }).eq('id', fileId);
        } catch (e) {
          console.error('recordClientFailure failed:', e);
        }
      }

      // Browser-side PDF text extraction — delegates to the shared helper
      // (src/lib/pdfExtract.js) instead of keeping its own copy, so the
      // per-page resilience fix (one bad page in a large scanned PDF no
      // longer aborts the whole extraction) only has to live in one place.
      async function extractPdfInBrowser(storagePath) {
        const extra = await extractPdfExtraFields({
          type: 'application/pdf',
          arrayBuffer: async () => {
            var dl = await supabase.storage.from('deal-documents').download(storagePath);
            if (dl.error) throw new Error('storage download failed: ' + dl.error.message);
            return await dl.data.arrayBuffer();
          },
        });
        return { text: extra.extracted_text || '', images: extra.extracted_images || null };
      }

      // Anthropic and Supabase edge can both return 5xx during overload spikes
      // (we hit a 503 on the Verizon PDF). Retry transient errors with backoff
      // before counting it as a real failure.
      async function fetchWithRetry(url, init, attempts) {
        attempts = attempts || 3;
        var lastErr;
        for (var a = 0; a < attempts; a++) {
          try {
            var r = await fetch(url, init);
            if (r.status === 429 || (r.status >= 500 && r.status < 600)) {
              if (a < attempts - 1) {
                await new Promise(function(res) { setTimeout(res, 1000 * Math.pow(2, a)); });
                continue;
              }
            }
            return r;
          } catch (e) {
            lastErr = e;
            if (a < attempts - 1) {
              await new Promise(function(res) { setTimeout(res, 1000 * Math.pow(2, a)); });
              continue;
            }
          }
        }
        if (lastErr) throw lastErr;
      }

      for (var i = 0; i < toAnalyze.length; i++) {
        var f = toAnalyze[i];
        try {
          var bodyPayload = { file_id: f.id, deal_id: dealId };
          // For PDFs, extract text in the browser first and pass it to the
          // function so the function never has to parse the PDF itself.
          // If the PDF has no text (scanned image), also send the first few
          // page renders as JPEGs so Claude vision can OCR them.
          if (f.mime_type === 'application/pdf' && f.storage_path) {
            try {
              var ext = await extractPdfInBrowser(f.storage_path);
              if (ext.text && ext.text.length >= 200) bodyPayload.extracted_text = ext.text;
              if (ext.images && ext.images.length > 0) bodyPayload.extracted_images = ext.images;
            } catch (e) {
              console.warn('Browser PDF extract failed for ' + f.name + ':', e.message);
              // fall through and let the server try the vision path
            }
          }
          var catResp = await fetchWithRetry(spUrl + '/functions/v1/auto-categorize-document', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'apikey': spKey },
            body: JSON.stringify(bodyPayload),
          });
          // Fire DD match in parallel — its failure doesn't count as an analyze failure
          fetch(spUrl + '/functions/v1/ai-match-dd-item', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'apikey': spKey },
            body: JSON.stringify({ file_id: f.id, deal_id: dealId }),
          }).catch(function() {});
          if (!catResp.ok) {
            var msg = 'HTTP ' + catResp.status;
            try {
              var body = await catResp.json();
              if (body && body.error) msg = body.error;
            } catch (e) { /* non-JSON body */ }
            failures.push({ name: f.name, reason: msg });
            await recordClientFailure(f.id, msg);
            console.error('Categorize failed for ' + f.name + ': ' + msg);
          }
        } catch (e) {
          // A bare "Failed to fetch" (fetch() throwing with no HTTP response
          // at all) after fetchWithRetry already retried 3x is almost always
          // an oversized request body getting rejected before the function
          // even runs, not a flaky connection — say so instead of leaving a
          // dead-end error message.
          var netMsg = e.message || 'network error';
          if (/failed to fetch/i.test(netMsg)) {
            netMsg = 'Failed to fetch (no server response after 3 retries) — likely the extracted page images were too large to send. Try re-uploading a lower-resolution scan, or split the PDF into smaller files.';
          }
          failures.push({ name: f.name, reason: netMsg });
          await recordClientFailure(f.id, netMsg);
          console.error('Analyze failed for ' + f.name, e);
        }
        done++;
        setAnalyzeProgress({ done: done, total: toAnalyze.length });
      }

      // Refresh files to pick up new category_id / dd_item_id values
      var refreshed = await supabase.from('files').select('*').eq('deal_id', dealId).order('created_at', { ascending: false });
      if (refreshed.data) setAllDealFiles(refreshed.data);
      refreshDeal();

      // After all extractions are in, run the dedup pass so any new
      // personnel/vehicle rows that look like duplicates of existing rows
      // get flagged for the user to review.
      try {
        await supabase.rpc('dedup_deal_extractions', { p_deal_id: dealId });
        loadInfoRequest();
        loadDuplicates();
      } catch (e) {
        console.warn('dedup_deal_extractions failed:', e);
      }

      if (!silent) {
        var ok = toAnalyze.length - failures.length;
        if (failures.length === 0) {
          showToast('Analyzed ' + ok + ' file(s)');
        } else {
          // Surface the actual reason for the first failure so the user knows why
          var first = failures[0];
          showToast(
            'Analyzed ' + ok + ' of ' + toAnalyze.length + ' · ' + failures.length + ' failed (' + first.name + ': ' + first.reason + ')',
            failures.length === toAnalyze.length ? 'error' : 'warning'
          );
          console.warn('Analyze failures:', failures);
        }
      }
    } catch (err) {
      console.error('Analyze uncategorized failed:', err);
      if (!silent) showToast('Analyze failed: ' + err.message, 'error');
    } finally {
      setAnalyzingFiles(false);
      setAnalyzeProgress(null);
    }
  };

  // Manual retry for a single file that previously failed AI analysis.
  // Clears the error stamp and re-runs the analyzer (which now sees the
  // file as a "needs analysis" candidate again).
  const handleRetryFile = async (file) => {
    if (!file?.id || retryingFileId) return;
    setRetryingFileId(file.id);
    try {
      const { error } = await supabase
        .from('files')
        .update({ ai_error_reason: null, ai_error_at: null })
        .eq('id', file.id);
      if (error) throw error;
      setAllDealFiles(prev => prev.map(f =>
        f.id === file.id ? { ...f, ai_error_reason: null, ai_error_at: null } : f
      ));
      showToast('Retrying ' + (file.original_name || file.name || 'file') + '…');
      await handleAnalyzeUncategorized({ silent: true });
    } catch (e) {
      console.error('Retry failed:', e);
      showToast('Retry failed: ' + (e.message || 'unknown error'), 'error');
    } finally {
      setRetryingFileId(null);
    }
  };

  // Bulk retry — clear ai_error_reason on every failed file on this deal,
  // then kick off handleAnalyzeUncategorized which now picks them all up.
  // Surfaces a "Retry N failed" button next to the Analyze button when
  // failures exist, so the user doesn't have to click each row's Retry.
  const handleRetryAllFailed = async () => {
    if (!deal || analyzingFiles) return;
    const failed = (allDealFiles || []).filter(f => !f.category_id && f.ai_error_reason);
    if (failed.length === 0) {
      showToast('No failed files to retry');
      return;
    }
    try {
      const ids = failed.map(f => f.id);
      const { error } = await supabase
        .from('files')
        .update({ ai_error_reason: null, ai_error_at: null })
        .in('id', ids);
      if (error) throw error;
      setAllDealFiles(prev => prev.map(f =>
        ids.includes(f.id) ? { ...f, ai_error_reason: null, ai_error_at: null } : f
      ));
      showToast('Retrying ' + failed.length + ' file' + (failed.length === 1 ? '' : 's') + '…');
      await handleAnalyzeUncategorized({ silent: true });
    } catch (e) {
      console.error('Retry all failed:', e);
      showToast('Retry all failed: ' + (e.message || 'unknown error'), 'error');
    }
  };

  // Schedule visit to Outlook calendar
  const handleScheduleVisit = async () => {
    if (!visitForm.date) { showToast('Please select a date', 'error'); return; }
    setCreatingEvent(true);
    try {
      var locIdx = parseInt(visitForm.locationIdx || '0');
      var selLoc = dealLocations && dealLocations.length > locIdx ? dealLocations[locIdx] : null;
      var locationStr = selLoc
        ? selLoc.name + (selLoc.city ? ', ' + selLoc.city : '') + (selLoc.state ? ', ' + selLoc.state : '')
        : '';
      var description = 'Site visit for ' + deal.name
        + (visitForm.notes ? '\nNotes: ' + visitForm.notes : '')
        + (deal.primary_contact ? '\nContact: ' + deal.primary_contact : '')
        + (deal.primary_email ? '\nEmail: ' + deal.primary_email : '')
        + (deal.primary_phone ? '\nPhone: ' + deal.primary_phone : '')
        + '\n\nDeal link: ' + window.location.href;

      var startDateTime = visitForm.date + 'T' + visitForm.startTime + ':00';
      var endDateTime = visitForm.date + 'T' + visitForm.endTime + ':00';

      var result = await createCalendarEvent({
        subject: 'Site Visit: ' + deal.name,
        start: startDateTime,
        end: endDateTime,
        location: locationStr,
        body: description
      });

      if (result.success) {
        // Save visit to database
        await supabase.from('deal_visits').insert({
          deal_id: dealId,
          organization_id: deal.organization_id,
          title: 'Site Visit: ' + deal.name,
          visit_date: visitForm.date,
          start_time: visitForm.startTime + ':00',
          end_time: visitForm.endTime + ':00',
          notes: visitForm.notes || null,
          location: locationStr || null,
          m365_event_id: result.eventId || null,
          created_by: userProfile?.id,
          created_by_name: userProfile?.full_name || userProfile?.email || 'Unknown',
          status: 'scheduled'
        });
        showToast('Visit scheduled in Outlook');
        setShowScheduleVisit(false);
        setVisitForm({ date: '', startTime: '09:00', endTime: '10:00', notes: '' });
        loadVisits();
      } else {
        throw new Error(result.error);
      }
    } catch (err) {
      showToast(err.message || 'Failed to schedule visit', 'error');
    } finally {
      setCreatingEvent(false);
    }
  };

  // Cancel a scheduled visit
  const handleCancelVisit = async (visit) => {
    if (!confirm('Cancel visit on ' + new Date(visit.visit_date).toLocaleDateString() + '?')) return;
    try {
      var result = await cancelCalendarEvent(visit.id, visit.m365_event_id, userProfile?.id);
      if (result.success) { showToast('Visit canceled'); loadVisits(); }
      else throw new Error(result.error);
    } catch (err) {
      showToast(err.message || 'Failed to cancel visit', 'error');
    }
  };

  if (loading || !deal) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin mb-4" style={{ color: displayPrimary }} />
        <p className="text-sm" style={{ color: displaySecondary }}>Loading deal...</p>
      </div>
    );
  }

  const phase = PHASES.find(p => p.id === deal.stage);
  const isCompletedStage = ['complete', 'integration-61-90'].includes(deal.stage);
  
  const stats = {
    total: tasks.length,
    complete: tasks.filter(t => t.status === 'Complete' || t.status === 'N/A').length,
    requested: tasks.filter(t => t.status === 'Requested').length,
    docRequests: tasks.filter(t => t.is_doc_request).length,
    docsComplete: tasks.filter(t => t.is_doc_request && t.status === 'Complete').length,
    totalFiles: tasks.reduce((sum, t) => sum + (t.files?.length || 0), 0)
  };

  // All unrequested doc tasks for request flow
  var pendingDocTasks = tasks.filter(function(t) { return t.is_doc_request && (t.status === 'Not Started' || t.status === 'Requested'); });
  var pendingDocIds = pendingDocTasks.map(function(t) { return t.id; });

  const toggleDocSelect = (taskId) => {
    setSelectedDocIds(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const selectAllDocs = () => setSelectedDocIds(new Set(pendingDocIds));
  const deselectAllDocs = () => setSelectedDocIds(new Set());

  const toggle = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const handleUpdateDeal = async (field, value) => {
    if (isViewer) return;
    try { await updateDeal({ [field]: value }); }
    catch (err) { showToast(err.message, 'error'); }
  };

  // Phase C.5/C.6 — resolves one entry in deals.pending_field_updates.
  // "Update" applies the newer parse's value; "Keep current" just drops
  // the suggestion. Either way it's logged — including who decided — so
  // the decision is visible in the deal timeline.
  const handleResolvePendingFieldUpdate = async (entry, accept) => {
    if (!entry || isViewer || loiUpdateBusy) return;
    setLoiUpdateBusy(true);
    try {
      const remaining = (deal?.pending_field_updates || []).filter(e => e.field !== entry.field);
      if (accept) {
        await updateDeal({ [entry.field]: entry.suggested, pending_field_updates: remaining });
        await supabase.from('activity_log').insert({
          organization_id: organization.id, deal_id: dealId, user_id: userProfile?.id || null,
          activity_type: 'deal_updated',
          description: `${userProfile?.full_name || 'Someone'} confirmed updating ${entry.label} to ${entry.suggested} from ${entry.file_name}`,
          metadata: { file_id: entry.file_id, file_name: entry.file_name, field: entry.field, source: 'pending_field_update_confirmation' },
        });
        showToast(`${entry.label} updated`);
      } else {
        await updateDeal({ pending_field_updates: remaining });
        await supabase.from('activity_log').insert({
          organization_id: organization.id, deal_id: dealId, user_id: userProfile?.id || null,
          activity_type: 'deal_updated',
          description: `${userProfile?.full_name || 'Someone'} kept the existing ${entry.label} (${entry.current}) despite ${entry.file_name} suggesting ${entry.suggested}`,
          metadata: { file_id: entry.file_id, file_name: entry.file_name, field: entry.field, source: 'pending_field_update_confirmation' },
        });
        showToast(`Kept current ${entry.label}`);
      }
    } catch (err) {
      showToast(err.message || 'Failed to update', 'error');
    } finally {
      setLoiUpdateBusy(false);
    }
  };

  // Phase C.6 — confirms or rejects an AI-proposed (unconfirmed) location.
  // Confirming records who did it and makes it show up on the map/exports;
  // rejecting deletes the row outright (it was never a real location).
  const handleResolvePendingLocation = async (location, accept) => {
    if (!location || isViewer || loiUpdateBusy) return;
    setLoiUpdateBusy(true);
    try {
      const where = [location.address, location.city, location.state].filter(Boolean).join(', ') || location.name || 'location';
      if (accept) {
        await confirmDealLocation(location.id, userProfile);
        await supabase.from('activity_log').insert({
          organization_id: organization.id, deal_id: dealId, user_id: userProfile?.id || null,
          activity_type: 'deal_updated',
          description: `${userProfile?.full_name || 'Someone'} confirmed location "${where}"${location.source_note ? ` (${location.source_note})` : ''}`,
          metadata: { location_id: location.id, source_file_id: location.source_file_id, source: 'pending_location_confirmation' },
        });
        showToast('Location confirmed');
      } else {
        await rejectDealLocation(location.id);
        await supabase.from('activity_log').insert({
          organization_id: organization.id, deal_id: dealId, user_id: userProfile?.id || null,
          activity_type: 'deal_updated',
          description: `${userProfile?.full_name || 'Someone'} rejected proposed location "${where}"${location.source_note ? ` (${location.source_note})` : ''}`,
          metadata: { source_file_id: location.source_file_id, source: 'pending_location_confirmation' },
        });
        showToast('Location rejected');
      }
    } catch (err) {
      showToast(err.message || 'Failed to update', 'error');
    } finally {
      setLoiUpdateBusy(false);
    }
  };

  // Bulk "grant by email" — same grantAccessByEmail as the single-entry
  // flow, just run once per pasted address instead of making the user
  // enter deal advisors one at a time. Sequential (not parallel) so the
  // progress counter is meaningful and so a burst of invite emails/RPC
  // calls doesn't all fire at once.
  const handleBulkGrantAccess = async () => {
    if (isViewer || grantingBulkAccess) return;
    const input = document.getElementById('grant-email-bulk-input');
    const emails = [...new Set(
      (input?.value || '').split(/[,;\s]+/).map(s => s.trim().toLowerCase()).filter(Boolean)
    )];
    if (emails.length === 0) return;
    setGrantingBulkAccess(true);
    setBulkGrantResults(null);
    setBulkGrantProgress({ done: 0, total: emails.length });
    const results = [];
    for (const email of emails) {
      try {
        const r = await grantAccessByEmail(email, '', deal?.organization_id);
        results.push({ email, ok: true, message: r.message });
      } catch (err) {
        results.push({ email, ok: false, message: err.message || 'Failed' });
      }
      setBulkGrantProgress(prev => ({ done: (prev?.done || 0) + 1, total: emails.length }));
    }
    setGrantingBulkAccess(false);
    setBulkGrantResults(results);
    const failedEmails = results.filter(r => !r.ok).map(r => r.email);
    if (input) input.value = failedEmails.join('\n');
    const succeededCount = results.length - failedEmails.length;
    showToast(
      failedEmails.length === 0
        ? `Granted/invited ${succeededCount} advisor${succeededCount === 1 ? '' : 's'}`
        : `${succeededCount} succeeded, ${failedEmails.length} failed — see details below`,
      failedEmails.length === 0 ? 'success' : 'warning'
    );
  };

  const handleBlurSave = async (field, localValue) => {
    if (isViewer) return;
    if (localValue !== (deal[field] || '')) {
      try { await updateDeal({ [field]: localValue }); }
      catch (err) { showToast(err.message, 'error'); }
    }
  };

  const handleFinancialChange = (field, value) => {
    // Allow typing: strip non-numeric except digits, dots, minus
    var clean = String(value).replace(/[^0-9.\-]/g, '');
    setLocalFinancials(prev => ({ ...prev, [field]: clean }));
  };

  // Format financial field on blur (add commas) and save
  const handleFinancialBlur = async (field, parseType) => {
    if (isViewer) return;
    var raw = localFinancials[field];
    var parsed = null;
    if (raw !== '' && raw !== null && raw !== undefined) {
      parsed = parseType === 'int' ? parseInt(raw) : parseFloat(raw);
      if (isNaN(parsed)) parsed = null;
    }
    // Update display with formatted value
    if (parsed !== null && parseType !== 'int') {
      setLocalFinancials(prev => ({ ...prev, [field]: formatDollar(parsed) }));
    } else if (parsed !== null && parseType === 'int') {
      setLocalFinancials(prev => ({ ...prev, [field]: parsed.toLocaleString('en-US') }));
    }
    if (parsed !== (deal[field] || null)) {
      try {
        var updateObj = {}; updateObj[field] = parsed; updateObj['updated_at'] = new Date().toISOString();
        var res = await supabase.from('deals').update(updateObj).eq('id', dealId);
        if (res.error) throw res.error;
      } catch (err) { showToast(err.message, 'error'); }
    }
  };

  // When focusing a financial field, show raw number for editing
  const handleFinancialFocus = (field) => {
    var val = localFinancials[field];
    if (val) {
      setLocalFinancials(prev => ({ ...prev, [field]: String(val).replace(/,/g, '') }));
    }
  };

  const handleNotesBlur = async () => {
    if (isViewer) return;
    if (localFinancials.deal_structure_notes !== (deal.deal_structure_notes || '')) {
      try {
        const { error } = await supabase
          .from('deals')
          .update({ deal_structure_notes: localFinancials.deal_structure_notes, updated_at: new Date().toISOString() })
          .eq('id', dealId);
        if (error) throw error;
      } catch (err) { showToast(err.message, 'error'); }
    }
  };

  const handleUpdateTask = async (taskId, field, value) => {
    if (isViewer) return;
    try { await updateTask(taskId, { [field]: value }); } 
    catch (err) { showToast(err.message, 'error'); }
  };

  const handleApplyDates = async () => {
    if (isViewer) return;
    try { await applyDueDates(); showToast('Due dates applied successfully'); } 
    catch (err) { showToast(err.message, 'error'); }
  };

  const handleFileUpload = async (taskId, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(prev => ({ ...prev, [taskId]: true }));
    try {
      await uploadFile(taskId, file, { id: userProfile?.id, name: userProfile?.full_name || 'User', type: 'user' });
      // Auto-complete doc-request tasks on upload
      const task = tasks.find(t => t.id === taskId);
      if (task?.is_doc_request && task.status !== 'Complete') {
        await updateTask(taskId, { status: 'Complete' });
        showToast('File uploaded — task marked complete');
      } else {
        showToast('File uploaded successfully');
      }
      // Sync to SharePoint (non-blocking)
      try {
        const syncTask = tasks.find(t => t.id === taskId);
        await uploadToSharePoint(file, deal.name, file.name, syncTask?.category_id || null);
      } catch (spErr) { console.error('SharePoint sync failed:', spErr); }
    } catch (err) { showToast(err.message, 'error'); }
    finally {
      setUploading(prev => ({ ...prev, [taskId]: false }));
      if (fileInputRefs.current[taskId]) fileInputRefs.current[taskId].value = '';
    }
  };

  const handleDeleteFile = async (taskId, fileId, storagePath) => {
    if (isViewer || !confirm('Delete this file?')) return;
    try { await deleteFile(taskId, fileId, storagePath); showToast('File deleted'); } 
    catch (err) { showToast(err.message, 'error'); }
  };

  const handleDownloadFile = async (storagePath, fileName) => {
    try {
      // SharePoint-only files — always open in new tab (SP blocks iframes via CSP)
      if (storagePath && storagePath.startsWith('sharepoint://')) {
        // Search task files first, then allDealFiles
        var spFile = null;
        for (var t = 0; t < tasks.length && !spFile; t++) {
          if (tasks[t].files) {
            spFile = tasks[t].files.find(function(f) { return f.storage_path === storagePath && f.sharepoint_url; });
          }
        }
        if (!spFile) {
          spFile = allDealFiles.find(function(f) { return f.storage_path === storagePath && f.sharepoint_url; });
        }
        if (spFile && spFile.sharepoint_url) {
          window.open(spFile.sharepoint_url, '_blank');
        } else {
          showToast('This file is in SharePoint — use the SharePoint Files button to access it.');
        }
        return;
      }
      // Supabase storage files — view in modal
      const url = await getFileUrl(storagePath);
      var ext = (fileName || '').split('.').pop()?.toLowerCase();
      setViewingFile({ url, name: fileName, type: ext });
    } catch (err) { showToast(err.message, 'error'); }
  };

  // Cross-origin Supabase storage URLs ignore the <a download> attribute's
  // suggested filename (browsers only honor it same-origin) and fall back
  // to the storage key, which is a randomized string, not the real name.
  // Fetch to a blob first so the download URL is same-origin.
  const forceDownloadFile = async (url, fileName) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl; a.download = fileName;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) { showToast(err.message, 'error'); }
  };

  const handleAddTask = async () => {
    if (isViewer || !newTask.name.trim() || !newTask.phase) return;
    try {
      await addTask({
        name: newTask.name,
        phase: newTask.phase,
        is_doc_request: newTask.is_doc_request,
        is_custom: true,
        relative_days: newTask.relative_days,
        base_date: newTask.base_date
      });
      setNewTask({ phase: '', name: '', is_doc_request: false, relative_days: 0, base_date: 'loi' });
      setShowModal(null);
      showToast('Task added');
      refreshDeal();
    } catch (err) { showToast(err.message, 'error'); }
  };

  const handleDeleteTask = async (taskId) => {
    if (isViewer || !confirm('Delete this task?')) return;
    try { await deleteTask(taskId); showToast('Task deleted'); refreshDeal(); } 
    catch (err) { showToast(err.message, 'error'); }
  };

  const handleRequestDocs = () => {
    var docTasks = tasks.filter(function(t) { return selectedDocIds.has(t.id); });
    if (docTasks.length === 0) {
      showToast('No documents selected to request', 'error');
      return;
    }
    var msg = 'Dear ' + (localContact || deal.primary_contact || 'Seller') + ',\n\nAs part of our partnership process for ' + deal.name + ', we need the following documents:\n\n';
    docTasks.forEach(function(task) {
      msg += '\u2022 ' + task.name + '\n';
    });
    msg += '\nPlease upload these documents at your earliest convenience.';
    setRequestMsg(msg);
    setSelectedDocEmail(localEmail || deal?.primary_email || '');
    setShowModal('request');
  };

  const handleSendRequest = async () => {
    const emailTo = selectedDocEmail || localEmail || deal.primary_email;
    if (!emailTo) {
      showToast('No email address set', 'error');
      return;
    }

    // Derive the portal scope from the recipient email. Matches are
    // case-insensitive and trimmed. Anything that doesn't match accountant
    // or broker falls back to 'seller' (same as today's behavior).
    const normEmail = (emailTo || '').trim().toLowerCase();
    let scope = 'seller';
    let scopeName = '';
    let recipientLabel = localContact || deal.primary_contact || 'Seller';
    let recipientRolePhrase = '';
    if (localAccountantEmail && normEmail === localAccountantEmail.trim().toLowerCase()) {
      scope = 'accountant';
      scopeName = localAccountantName || 'Accountant';
      recipientLabel = scopeName;
      recipientRolePhrase = 'As accountant/CPA for the seller in the ' + deal.name + ' transaction, ';
    } else if (localBrokerEmail && normEmail === localBrokerEmail.trim().toLowerCase()) {
      scope = 'broker';
      scopeName = localBrokerName || 'Broker';
      recipientLabel = scopeName;
      recipientRolePhrase = 'As broker/representative for the seller in the ' + deal.name + ' transaction, ';
    } else if (localCounselEmail && normEmail === localCounselEmail.trim().toLowerCase()) {
      scope = 'counsel';
      scopeName = localCounselName || 'Counsel';
      recipientLabel = scopeName;
      recipientRolePhrase = 'As counsel for the seller in the ' + deal.name + ' transaction, ';
    }

    setSendingRequest(true);
    try {
      const selectedIds = [...selectedDocIds];

      // For scoped (third-party) sends, assign any selected tasks that aren't
      // yet routed to this contact. This is what makes them visible through
      // the scoped portal token. Seller-scope sends skip this — seller sees
      // everything regardless of assignment.
      if (scope !== 'seller' && selectedIds.length > 0) {
        const toAssign = selectedIds.filter(id => {
          const t = tasks.find(x => x.id === id);
          return t && t.assigned_to_contact !== scope;
        });
        if (toAssign.length > 0) {
          const { error: assignErr } = await supabase
            .from('tasks')
            .update({ assigned_to_contact: scope })
            .in('id', toAssign);
          if (assignErr) throw assignErr;
        }
      }

      await markTasksRequested(selectedIds);

      // Build the portal link — scoped for third-party, full-access for seller.
      let sellerPortalLink = '';
      try {
        const result = scope === 'seller'
          ? await generateSellerToken(dealId)
          : await generateScopedSellerToken(dealId, scope, emailTo, scopeName);
        const token = Array.isArray(result) ? result[0].token : result.token;
        if (token) sellerPortalLink = buildSellerPortalUrl(token);
      } catch (e) { console.warn('Could not generate portal link:', e); }

      // Build HTML doc list grouped by phase
      let docListHtml = '';
      PHASES.forEach(phase => {
        const items = [...selectedDocIds]
          .map(id => tasks.find(t => t.id === id))
          .filter(t => t && t.phase === phase.id);
        if (items.length) {
          docListHtml += `<p style="margin: 15px 0 5px 0; font-weight: bold; color: #1A3665;">${phase.name}:</p><ul style="margin: 0; padding-left: 20px;">`;
          items.forEach(t => { docListHtml += `<li style="margin-bottom: 5px;">${t.name}</li>`; });
          docListHtml += '</ul>';
        }
      });

      // If no phase grouping matched, flat list
      if (!docListHtml) {
        docListHtml = '<ul style="margin: 0; padding-left: 20px;">';
        [...selectedDocIds].forEach(id => {
          const task = tasks.find(t => t.id === id);
          if (task) docListHtml += `<li style="margin-bottom: 5px;">${task.name}</li>`;
        });
        docListHtml += '</ul>';
      }

      const buttonHtml = sellerPortalLink ? `
        <p style="margin-top: 20px;">Please use the button below to securely upload your documents:</p>
        <div style="text-align: center; margin: 30px 0;">
          <a href="${sellerPortalLink}" 
             style="display: inline-block; background-color: #1A3665; color: white; padding: 14px 28px; text-decoration: none; border-radius: 6px; font-weight: bold; font-size: 16px;">
            Upload Documents
          </a>
        </div>
        <p style="text-align: center; color: #707578; font-size: 12px;">Or copy this link: <a href="${sellerPortalLink}" style="color: #1A3665;">${sellerPortalLink}</a></p>
      ` : '';

      // Tailor the intro line based on scope. Seller gets the original copy;
      // accountant/broker gets a line that names their role and the deal.
      const introParagraph = scope === 'seller'
        ? `<p>As part of our partnership process for <strong>${deal.name}</strong>, we need the following documents:</p>`
        : `<p>${recipientRolePhrase}we're requesting the following documents from you directly:</p>`;

      const html = `
    <h2 style="color: #1A3665; margin-bottom: 20px;">Document Request from LFG Documents Portal</h2>
    
    <p>Dear ${recipientLabel},</p>
    
    ${introParagraph}
    
    <div style="background-color: #f8f9fa; border-left: 4px solid #1A3665; padding: 15px; margin: 20px 0;">
      ${docListHtml}
    </div>
    
    ${buttonHtml}
      `.trim();

      const emailResult = await sendEmail({
        to: emailTo,
        subject: `Document Request - ${deal.name}`,
        text: requestMsg,
        html: html,
        replyTo: userProfile?.email
      });
      if (!emailResult.success) throw new Error(emailResult.error || 'Failed to send email');
      
      // Show success state in modal before closing
      setRequestSent(true);
      setTimeout(() => {
        setShowModal(null);
        setRequestSent(false);
        setSelected([]);
        showToast(`Document request sent to ${emailTo}`);
      }, 1800);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSendingRequest(false);
    }
  };

  return (
    <div
      className="w-full min-h-screen relative"
      style={{
        // Single continuous gradient from the top of the header all the way
        // to the bottom of the page. Deep navy-tinted start, long tail of
        // progressively lighter tones so content sections below the hero
        // still sit on a perceptible wash instead of flat white.
        background: 'linear-gradient(165deg, #A8C0DE 0%, #C2D4EC 8%, #D6E2F2 18%, #E5ECF6 32%, #EFF2F8 55%, #F6F8FB 80%, #FAFBFD 100%)',
      }}
    >
      {/* Header — floating action bar that blends into the hero gradient.
          Deal name, stage, and distance live in the hero (no duplicates).
          Button styles unified: identical h-9 secondary chips + one navy
          primary on the right. */}
      <div className="px-4 sm:px-6 lg:px-8 pt-4 pb-2 relative z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2 flex-wrap">
          {/* Left: back + stage selector + priority + cluster */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={onBack}
              className="h-9 w-9 inline-flex items-center justify-center rounded-lg transition-all hover:bg-white/60"
              style={{ color: '#475569' }}
              title="Back to deals"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            {!isViewer ? (
              <select
                value={deal.stage}
                onChange={(e) => {
                  // Phase R.3 — auto-stamp the entered-this-stage timestamp
                  // for on-hold + canceled so reporting/digest views can
                  // tell when a deal stalled. Reason text gets captured by
                  // a separate input that appears below.
                  const next = e.target.value;
                  const patch = { stage: next };
                  if (next === 'canceled' && !deal.canceled_at) patch.canceled_at = new Date().toISOString();
                  if (next === 'on-hold' && !deal.on_hold_at) patch.on_hold_at = new Date().toISOString();
                  updateDeal(patch).catch(err => showToast(err.message, 'error'));
                  // Soft nudge, not a block — save proceeds regardless.
                  // Ray: "I should have to assign a priority once I move
                  // the deal to an Active pipeline category."
                  if (['diligence', 'offer-made', 'under-loi'].includes(next) && !deal.priority) {
                    showToast('This deal has no priority set yet — consider assigning one.');
                  }
                }}
                className="h-9 pl-3 pr-8 text-sm font-medium rounded-lg cursor-pointer transition-all border-0 focus:outline-none focus:ring-2 focus:ring-white/60"
                style={{
                  backgroundColor: phase?.hex || '#6b7280',
                  color: 'white',
                  appearance: 'none',
                  backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'white\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2.5\' d=\'M19 9l-7 7-7-7\'%3E%3C/path%3E%3C/svg%3E")',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 8px center',
                  backgroundSize: '12px',
                  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
                }}
              >
                {PHASES.map(p => <option key={p.id} value={p.id} className="text-black bg-white">{p.name}</option>)}
              </select>
            ) : (
              <span className="h-9 inline-flex items-center px-3 text-sm font-medium rounded-lg text-white"
                    style={{ backgroundColor: phase?.hex || '#6b7280' }}>
                {phase?.name}
              </span>
            )}
            {/* Phase R — Priority selector (heat-mapping for which deals need
                focus). Critical/High/Medium/Low; manual ranking by Alfie. */}
            {!isViewer && (() => {
              const PRIORITY_STYLES = {
                critical: { bg: '#FEE2E2', fg: '#B91C1C' },
                high:     { bg: '#FFEDD5', fg: '#C2410C' },
                medium:   { bg: '#FEF3C7', fg: '#A16207' },
                low:      { bg: '#F1F5F9', fg: '#64748B' },
              };
              const cur = PRIORITY_STYLES[deal.priority] || null;
              return (
                <select
                  value={deal.priority || ''}
                  onChange={(e) => handleUpdateDeal('priority', e.target.value || null)}
                  className="h-9 pl-3 pr-8 text-sm font-semibold rounded-lg cursor-pointer transition-all border-0 focus:outline-none focus:ring-2 focus:ring-offset-1"
                  style={{
                    backgroundColor: cur?.bg || '#F8FAFC',
                    color: cur?.fg || '#94A3B8',
                    appearance: 'none',
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='${encodeURIComponent(cur?.fg || '#94A3B8')}'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2.5' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 8px center',
                    backgroundSize: '12px',
                    border: '1px solid #E5E9F0',
                  }}
                  title="Priority — how critical is this deal?"
                >
                  <option value="">— Priority —</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              );
            })()}
            {/* Phase R.2 — Cluster (free-text tag for roll-up grouping).
                Saves on blur so typing doesn't trigger a re-fetch per
                keystroke. Empty string → NULL. */}
            {!isViewer && (
              <input
                type="text"
                defaultValue={deal.cluster || ''}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v !== (deal.cluster || '')) handleUpdateDeal('cluster', v || null);
                }}
                placeholder="Cluster…"
                className="hidden sm:block h-9 px-3 text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-1"
                style={{
                  backgroundColor: deal.cluster ? 'rgba(124, 58, 237, 0.08)' : '#F8FAFC',
                  color: deal.cluster ? '#5B21B6' : '#0F172A',
                  border: '1px solid #E5E9F0',
                  width: '140px',
                  fontWeight: deal.cluster ? 600 : 400,
                }}
                title="Cluster — strategic grouping (e.g. 'Ohio roll-up')"
              />
            )}
          </div>

          {/* Right: action buttons — unified visual treatment */}
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {dealLocations.length > 0 && !isViewer && userProfile?.role === 'admin' && (
              allLocationsInLfg ? (
                <span className="h-9 inline-flex items-center gap-1.5 px-3 text-sm rounded-lg"
                      style={{ backgroundColor: 'rgba(133, 189, 102, 0.15)', color: '#15803D', border: '1px solid rgba(133, 189, 102, 0.4)' }}>
                  <CheckCircle className="w-4 h-4" /> In LFG
                </span>
              ) : (
                <button type="button"
                  onClick={handleConvertToLfgLocations}
                  disabled={converting}
                  className="h-9 inline-flex items-center gap-1.5 px-3 text-sm rounded-lg transition-all hover:bg-white disabled:opacity-50"
                  style={{ backgroundColor: 'rgba(255,255,255,0.7)', color: '#15803D', border: '1px solid rgba(133, 189, 102, 0.5)' }}
                  title={someLocationsInLfg ? `Add ${locationsNotInLfg.length} remaining location(s) to LFG` : 'Add all locations to LFG Locations'}
                >
                  {converting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4" />}
                  {someLocationsInLfg ? `Add ${locationsNotInLfg.length} to LFG` : `Add to LFG`}
                </button>
              )
            )}
            {!isViewer && (
              <HeaderActionBtn
                onClick={handleCopySellerLink}
                disabled={generatingLink}
                title="Generate a secure link for the seller to upload documents"
              >
                {generatingLink ? <Loader2 className="w-4 h-4 animate-spin" /> : sellerLinkCopied ? <Check className="w-4 h-4" style={{ color: '#10B981' }} /> : <Link className="w-4 h-4" />}
                {sellerLinkCopied ? 'Copied!' : 'Seller Link'}
              </HeaderActionBtn>
            )}
            {!isViewer && (
              <HeaderActionBtn
                onClick={async () => {
                  try {
                    const result = await generateSellerToken(dealId);
                    const token = Array.isArray(result) ? result[0].token : result.token;
                    const url = buildSellerPortalUrl(token) + '?internal=1';
                    window.open(url, '_blank');
                  } catch (err) {
                    showToast('Failed to open seller portal: ' + err.message, 'error');
                  }
                }}
                title="Open the seller portal in a new tab"
              >
                <ExternalLink className="w-4 h-4" /> Portal
              </HeaderActionBtn>
            )}
            {/* Upload available to anyone with edit access on the deal —
                including integration_viewer, who handles utility bills,
                vendor docs, and other operational uploads as part of
                post-close work. Only viewer / limited_viewer (truly
                read-only roles) are blocked. */}
            {!isLimitedViewer && !isViewer && (
              <>
                <HeaderActionBtn
                  onClick={() => internalUploadInputRef.current?.click()}
                  disabled={internalUploading}
                  title="Upload one or more files to this deal — runs AI categorization and DD matching, syncs to SharePoint"
                >
                  {internalUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {internalUploading
                    ? (internalUploadProgress ? 'Uploading ' + internalUploadProgress.done + '/' + internalUploadProgress.total : 'Uploading…')
                    : 'Upload'}
                </HeaderActionBtn>
                <input
                  ref={internalUploadInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.jpg,.jpeg,.png,.gif,.webp"
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      handleInternalFilesUpload(e.target.files);
                    }
                  }}
                />
              </>
            )}
            {!isLimitedViewer && !isIntegration && !isDealAdvisor && (<>
            <HeaderActionBtn
              onClick={handleCreateSharePointFolders}
              disabled={creatingSPFolders}
              title={isViewer ? "Open deal folder in SharePoint" : "Create deal folder structure in SharePoint and open it"}
              minWidth="125px"
            >
              {creatingSPFolders ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
              {creatingSPFolders ? 'Opening…' : 'SharePoint'}
            </HeaderActionBtn>
            <HeaderActionBtn
              onClick={handleScanSharePoint}
              disabled={scanningSharePoint}
              title="Scan SharePoint folder and import any untracked files"
              minWidth="115px"
            >
              {scanningSharePoint ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {scanningSharePoint ? 'Scanning…' : 'Scan SP'}
            </HeaderActionBtn>
            {(() => {
              var pendingCount = (allDealFiles || []).filter(function(f) { return !f.category_id && !f.ai_error_reason; }).length;
              var failedCount = (allDealFiles || []).filter(function(f) { return !f.category_id && f.ai_error_reason; }).length;
              if (pendingCount === 0 && failedCount === 0 && !analyzingFiles) return null;
              var titleParts = [];
              if (pendingCount > 0) titleParts.push(pendingCount + ' uncategorized');
              if (failedCount > 0) titleParts.push(failedCount + ' previously failed');
              return (
                <>
                  <HeaderActionBtn
                    onClick={function() { handleAnalyzeUncategorized({ silent: false }); }}
                    disabled={analyzingFiles || pendingCount === 0}
                    title={titleParts.join(' · ') || 'Run AI categorization on uncategorized files'}
                    minWidth="160px"
                  >
                    {analyzingFiles ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {analyzingFiles
                      ? (analyzeProgress ? 'Analyzing ' + analyzeProgress.done + '/' + analyzeProgress.total : 'Analyzing…')
                      : (pendingCount > 0
                          ? 'Analyze ' + pendingCount + (failedCount > 0 ? ' · ' + failedCount + ' failed' : '')
                          : failedCount + ' failed')}
                  </HeaderActionBtn>
                  {/* Bulk retry — only shows when there are failed files and
                      nothing is currently analyzing. Clears every failed
                      file's ai_error stamp and re-runs the analyzer. */}
                  {failedCount > 0 && !analyzingFiles && (
                    <HeaderActionBtn
                      onClick={handleRetryAllFailed}
                      title={'Clear errors on all ' + failedCount + ' failed file(s) and re-run analysis'}
                      minWidth="140px"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Retry {failedCount} failed
                    </HeaderActionBtn>
                  )}
                </>
              );
            })()}
            </>)}
            {!isViewer && (
              <HeaderActionBtn
                onClick={() => setShowScheduleVisit(true)}
                title="Schedule a site visit and sync to Outlook calendar"
              >
                <Calendar className="w-4 h-4" /> Visit
              </HeaderActionBtn>
            )}
            {(userProfile?.role === 'admin' || userProfile?.role === 'member') && (
              <HeaderActionBtn
                onClick={() => setShowManageAccess(true)}
                title="Manage who has access to this deal"
              >
                <Users className="w-4 h-4" />
                {accessList.length > 0 ? `Access (${accessList.length})` : 'Access'}
              </HeaderActionBtn>
            )}
            {!isViewer && (
              <button
                type="button"
                onClick={handleApplyDates}
                className="h-9 inline-flex items-center px-3.5 text-sm font-medium rounded-lg text-white transition-all hover:shadow-md"
                style={{
                  background: `linear-gradient(135deg, ${primaryColor} 0%, #2E4B7C 100%)`,
                  boxShadow: '0 1px 2px rgba(26, 54, 101, 0.25), 0 2px 4px rgba(26, 54, 101, 0.15)',
                }}
                title="Auto-calculate due dates for all tasks based on LOI and close dates"
              >
                Apply Due Dates
              </button>
            )}
          </div>
        </div>

        {/* Phase R.3 — Reason input for on-hold + canceled deals. Only
            renders when the current stage is one of those two; saves on
            blur to the matching column (on_hold_reason / canceled_reason).
            Pre-populates from whichever column is already set so users can
            edit a prior reason. Read-only role just sees the text. */}
        {(deal.stage === 'on-hold' || deal.stage === 'canceled') && (
          <div className="max-w-7xl mx-auto mt-2 px-1">
            {(() => {
              const isOnHold = deal.stage === 'on-hold';
              const label = isOnHold ? 'Reason for hold' : 'Reason for cancellation';
              const field = isOnHold ? 'on_hold_reason' : 'canceled_reason';
              const accent = isOnHold ? '#A855F7' : '#EF4444';
              const current = deal[field] || '';
              // Cancellation reasons get a structured category (Ray's ask:
              // force a category, then let free text add color) — on-hold
              // keeps the existing plain free-text reason as-is.
              const categoryLabel = !isOnHold
                ? (cancellationCategories.find(c => c.id === deal.canceled_reason_category_id)?.label || null)
                : null;
              if (isViewer) {
                if (!current && !categoryLabel) return null;
                return (
                  <div className="rounded-lg px-3 py-2 text-sm" style={{ backgroundColor: 'rgba(255,255,255,0.6)', border: `1px solid ${accent}40`, color: '#0F172A' }}>
                    <span className="text-[10px] font-semibold uppercase tracking-wider mr-2" style={{ color: accent }}>{label}</span>
                    {categoryLabel && <span className="font-medium mr-2">{categoryLabel}</span>}
                    {current}
                  </div>
                );
              }
              return (
                <div className="flex items-start gap-2 rounded-lg px-3 py-2"
                     style={{ backgroundColor: 'rgba(255,255,255,0.6)', border: `1px solid ${accent}40` }}>
                  <span className="text-[10px] font-semibold uppercase tracking-wider mt-1.5 flex-shrink-0" style={{ color: accent, minWidth: '88px' }}>
                    {label}
                  </span>
                  <div className="flex-1 flex flex-col gap-1.5">
                    {!isOnHold && (
                      <select
                        value={deal.canceled_reason_category_id || ''}
                        onChange={(e) => handleUpdateDeal('canceled_reason_category_id', e.target.value || null)}
                        className="text-sm bg-transparent border-0 outline-none font-medium w-full sm:w-auto"
                        style={{ color: deal.canceled_reason_category_id ? '#0F172A' : '#94A3B8' }}
                      >
                        <option value="">— Select a reason category —</option>
                        {cancellationCategories.map(c => (
                          <option key={c.id} value={c.id} className="text-black">{c.label}</option>
                        ))}
                      </select>
                    )}
                    <textarea
                      key={`${deal.id}-${field}`}
                      defaultValue={current}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        if (v !== current) handleUpdateDeal(field, v || null);
                      }}
                      placeholder={isOnHold ? 'Why is this deal paused? When should we revisit it?' : 'Add color / details (optional)'}
                      rows={1}
                      className="bg-transparent border-0 outline-none resize-y min-h-[28px]"
                      style={{ color: '#0F172A' }}
                    />
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>{/* end header */}

      {/* Phase C.4j — Hero sits transparently over the page-level gradient
          and adds its own radial washes for dimension. No base gradient of
          its own so the page gradient flows unbroken into the tabs below. */}
      <div className="relative pt-8 pb-14 overflow-hidden">
        {/* Warm glow upper-left — large, soft, adds atmosphere */}
        <div
          className="absolute pointer-events-none"
          style={{
            top: '-25%', left: '-10%', width: '70%', height: '150%',
            background: 'radial-gradient(ellipse at center, rgba(255, 255, 255, 0.65) 0%, rgba(255, 255, 255, 0.15) 40%, transparent 65%)',
            filter: 'blur(20px)',
          }}
        />
        {/* Navy wash lower-right — deeper tint, makes the corner feel substantial */}
        <div
          className="absolute pointer-events-none"
          style={{
            top: '-15%', right: '-15%', width: '65%', height: '140%',
            background: 'radial-gradient(ellipse at center, rgba(26, 54, 101, 0.18) 0%, rgba(26, 54, 101, 0.08) 35%, transparent 65%)',
            filter: 'blur(50px)',
          }}
        />
        {/* Secondary accent lower-left — subtle teal/cyan hint for chromatic depth */}
        <div
          className="absolute pointer-events-none"
          style={{
            bottom: '-30%', left: '20%', width: '50%', height: '100%',
            background: 'radial-gradient(ellipse at center, rgba(8, 145, 178, 0.08) 0%, transparent 60%)',
            filter: 'blur(60px)',
          }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            {editingName ? (
              <input
                autoFocus
                value={editingName}
                onChange={e => setEditingName(e.target.value)}
                onBlur={() => {
                  const s = sanitizeDealName(editingName);
                  if (s.name && s.name !== deal.name) {
                    if (s.changed) showToast(`Slashes aren't allowed (SharePoint compat). Saved as "${s.name}".`);
                    handleUpdateDeal('name', s.name);
                  }
                  setEditingName(null);
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    const s = sanitizeDealName(editingName);
                    if (s.name && s.name !== deal.name) {
                      if (s.changed) showToast(`Slashes aren't allowed (SharePoint compat). Saved as "${s.name}".`);
                      handleUpdateDeal('name', s.name);
                    }
                    setEditingName(null);
                  } else if (e.key === 'Escape') {
                    setEditingName(null);
                  }
                }}
                className="text-4xl font-semibold tracking-tight leading-[1.05] outline-none bg-transparent border-b-2 border-blue-400 w-full max-w-2xl"
                style={{ color: '#0F172A', letterSpacing: '-0.03em' }}
              />
            ) : (
              <h1
                className={`text-4xl font-semibold tracking-tight leading-[1.05] ${!isViewer ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
                onClick={() => !isViewer && setEditingName(deal.name)}
                title={isViewer ? '' : 'Click to rename'}
                style={{
                  background: 'linear-gradient(135deg, #0F172A 0%, #1A3665 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  letterSpacing: '-0.03em',
                }}
              >
                {deal.name}
              </h1>
            )}
            <div className="flex items-center gap-3 mt-2.5 text-sm" style={{ color: '#475569' }}>
              {dealLocations[0] && (dealLocations[0].city || dealLocations[0].state) && (
                <span>{[dealLocations[0].city, dealLocations[0].state].filter(Boolean).join(', ')}</span>
              )}
              {dealLocations[0] && (dealLocations[0].city || dealLocations[0].state) && nearestLfgDistance !== null && (
                <span style={{ color: '#CBD5E1' }}>·</span>
              )}
              {nearestLfgDistance !== null && (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" /> {nearestLfgDistance} mi to nearest LFG
                </span>
              )}
            </div>
          </div>
          {/* Metric tiles — deal advisors don't need financials, stage,
              calls, or close-date context; they only work the DD checklist. */}
          {!isDealAdvisor && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <DealStageMetricTile value={phase?.name || 'Prospect'} accent={phase?.hex} />
            {!isIntegration && (
              <DealMetricTile label="Annual Revenue" value={deal.annual_revenue ? '$' + formatDollar(deal.annual_revenue) : '—'} />
            )}
            {!isIntegration && (
              <DealMetricTile label="EBITDA" value={deal.ebitda ? '$' + formatDollar(deal.ebitda) : '—'} />
            )}
            <DealMetricTile label="Annual Calls"   value={deal.annual_calls ? Number(deal.annual_calls).toLocaleString() : '—'} />
            <DealMetricTile label="Target Close"   value={deal.target_close_date ? new Date(deal.target_close_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'} />
          </div>
          )}
        </div>
      </div>

      {/* Phase C.5/C.6 — pending field updates. AI parsed a newer document
          that disagrees with an already-set field; nothing is overwritten
          until the user confirms which value is right. Each proposal
          (could be several at once, from different documents/fields)
          resolves independently. */}
      {Array.isArray(deal.pending_field_updates) && deal.pending_field_updates.length > 0 && (
        <div className="mx-6 mb-4 rounded-xl border px-4 py-3 flex items-start gap-3" style={{ borderColor: '#FDE68A', background: '#FFFBEB' }}>
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#B45309' }} />
          <div className="flex-1 min-w-0 space-y-3">
            {deal.pending_field_updates.map((entry) => (
              <div key={entry.field} className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm" style={{ color: '#92400E' }}>
                  <span className="font-medium">{entry.label}</span>
                  {': '}
                  <span className="line-through opacity-70">{formatPendingValue(entry.current, entry.format)}</span>
                  {' → '}
                  <span className="font-semibold">{formatPendingValue(entry.suggested, entry.format)}</span>
                  <span className="opacity-70"> — from "{entry.file_name}"</span>
                  {typeof entry.confidence === 'number' && (
                    <span className="opacity-70"> ({Math.round(entry.confidence * 100)}% confidence)</span>
                  )}
                  {entry.warning && (
                    <div className="flex items-center gap-1 mt-1 text-xs font-medium" style={{ color: '#B91C1C' }}>
                      <AlertTriangle className="w-3 h-3 flex-shrink-0" /> {entry.warning}
                    </div>
                  )}
                  {entry.note && (
                    <div className="mt-1 text-xs italic opacity-80">{entry.note}</div>
                  )}
                </div>
                {!isViewer && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleResolvePendingFieldUpdate(entry, true)}
                      disabled={loiUpdateBusy}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white disabled:opacity-50"
                      style={{ background: '#B45309' }}
                    >
                      {loiUpdateBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Update
                    </button>
                    <button
                      onClick={() => handleResolvePendingFieldUpdate(entry, false)}
                      disabled={loiUpdateBusy}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border disabled:opacity-50"
                      style={{ borderColor: '#FDE68A', color: '#92400E', background: 'transparent' }}
                    >
                      <X className="w-3.5 h-3.5" /> Keep Current
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Phase C.6 — AI-proposed locations (web search re-check, or found
          in a document's real-estate schedule). Inserted immediately as
          unconfirmed so they never block anything, but hidden from the
          map/exports until a human confirms them here. */}
      {pendingLocations.length > 0 && (
        <div className="mx-6 mb-4 rounded-xl border px-4 py-3 flex items-start gap-3" style={{ borderColor: '#FDE68A', background: '#FFFBEB' }}>
          <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#B45309' }} />
          <div className="flex-1 min-w-0 space-y-3">
            <div className="text-sm font-medium" style={{ color: '#92400E' }}>
              {pendingLocations.length} possible location{pendingLocations.length === 1 ? '' : 's'} found — not shown on the map or in exports until confirmed
            </div>
            {pendingLocations.map((loc) => (
              <div key={loc.id} className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm" style={{ color: '#92400E' }}>
                  <span className="font-medium">{[loc.address, loc.city, loc.state].filter(Boolean).join(', ') || loc.name || 'Unnamed location'}</span>
                  <span className="opacity-70"> — {loc.source === 'ai_web_search' ? 'found via AI web search' : loc.source === 'ai_document_extraction' ? 'found in an uploaded document' : loc.source}</span>
                  {loc.is_crematory && <span className="opacity-70"> · has crematory</span>}
                  {loc.source_note && (
                    <div className="mt-0.5 text-xs italic opacity-80">{loc.source_note}</div>
                  )}
                </div>
                {!isViewer && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleResolvePendingLocation(loc, true)}
                      disabled={loiUpdateBusy}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white disabled:opacity-50"
                      style={{ background: '#B45309' }}
                    >
                      {loiUpdateBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Confirm
                    </button>
                    <button
                      onClick={() => handleResolvePendingLocation(loc, false)}
                      disabled={loiUpdateBusy}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border disabled:opacity-50"
                      style={{ borderColor: '#FDE68A', color: '#92400E', background: 'transparent' }}
                    >
                      <X className="w-3.5 h-3.5" /> Not a match
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Phase C.4 — Sticky tab bar. Hidden for integration and deal
          advisors since they only see one consolidated view (no point
          rendering a 1-tab nav). */}
      {!isIntegration && !isDealAdvisor && (
      <div
        className="sticky top-0 z-30 border-b"
        style={{
          backgroundColor: 'rgba(250, 251, 253, 0.85)',
          backdropFilter: 'saturate(140%) blur(10px)',
          WebkitBackdropFilter: 'saturate(140%) blur(10px)',
          borderColor: '#E5E9F0',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 overflow-x-auto overflow-y-hidden">
          <nav className="flex gap-5 sm:gap-7 min-w-max">
            {DEAL_TABS.map(t => {
              const isActive = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setActiveTab(t.id)}
                  className="py-4 text-sm relative transition-colors"
                  style={{
                    color: isActive ? '#0F172A' : '#94A3B8',
                    fontWeight: isActive ? 600 : 500,
                    letterSpacing: '-0.005em',
                  }}
                >
                  {t.label}
                  {isActive && (
                    <div
                      className="absolute -bottom-px left-0 right-0 h-0.5 rounded-full"
                      style={{ backgroundColor: displayNavy }}
                    />
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </div>
      )}

      {/* Tab content container — gives breathing room around all sections below */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-12">

      {/* === CONTACTS TAB — clean card grid === */}
      {activeTab === 'team' && (
      <div className="mb-6 space-y-4">
        {/* Contact cards — 2×2 grid on desktop */}
        {!isLimitedViewer && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Seller — primary contact */}
            <ContactCard
              icon={User}
              title="Seller"
              subtitle="Primary contact"
              accent="#1A3665"
              accentBg="rgba(26, 54, 101, 0.1)"
            >
              <ContactField label="Contact Name" value={localContact}
                onChange={setLocalContact}
                onBlur={() => handleBlurSave('primary_contact', localContact)}
                disabled={isViewer} placeholder="Contact name" />
              <ContactField label="Phone" type="tel" value={localPhone}
                onChange={v => setLocalPhone(formatPhone(v))}
                onBlur={() => handleBlurSave('primary_phone', localPhone)}
                disabled={isViewer} placeholder="(555) 123-4567" />
              <ContactField label="Business Email" type="email" value={localEmail}
                onChange={setLocalEmail}
                onBlur={() => handleBlurSave('primary_email', localEmail)}
                disabled={isViewer} placeholder="business@example.com" />
              <ContactField label="Personal Email" type="email" value={localPersonalEmail}
                onChange={setLocalPersonalEmail}
                onBlur={() => handleBlurSave('personal_email', localPersonalEmail)}
                disabled={isViewer} placeholder="personal@example.com" />
            </ContactCard>

            {/* Counsel */}
            <ContactCard
              icon={Scale}
              title="Seller's Counsel"
              subtitle={localCounselFirm || 'Attorney representing the seller'}
              accent="#7C3AED"
              accentBg="rgba(124, 58, 237, 0.1)"
            >
              <ContactField label="Attorney Name" value={localCounselName}
                onChange={setLocalCounselName}
                onBlur={() => handleBlurSave('counsel_name', localCounselName)}
                disabled={isViewer} placeholder="Attorney name" />
              <ContactField label="Firm" value={localCounselFirm}
                onChange={setLocalCounselFirm}
                onBlur={() => handleBlurSave('counsel_firm', localCounselFirm)}
                disabled={isViewer} placeholder="Law firm name" />
              <ContactField label="Email" type="email" value={localCounselEmail}
                onChange={setLocalCounselEmail}
                onBlur={() => handleBlurSave('counsel_email', localCounselEmail)}
                disabled={isViewer} placeholder="attorney@firm.com" />
              <ContactField label="Phone" type="tel" value={localCounselPhone}
                onChange={v => setLocalCounselPhone(formatPhone(v))}
                onBlur={() => handleBlurSave('counsel_phone', localCounselPhone)}
                disabled={isViewer} placeholder="(555) 123-4567" />
            </ContactCard>

            {/* Accountant */}
            <ContactCard
              icon={Calculator}
              title="Accountant / CPA"
              subtitle={localAccountantFirm || "Seller's financial advisor"}
              accent="#10B981"
              accentBg="rgba(16, 185, 129, 0.1)"
            >
              <ContactField label="Name" value={localAccountantName}
                onChange={setLocalAccountantName}
                onBlur={() => handleBlurSave('accountant_name', localAccountantName)}
                disabled={isViewer} placeholder="CPA name" />
              <ContactField label="Firm" value={localAccountantFirm}
                onChange={setLocalAccountantFirm}
                onBlur={() => handleBlurSave('accountant_firm', localAccountantFirm)}
                disabled={isViewer} placeholder="Accounting firm" />
              <ContactField label="Email" type="email" value={localAccountantEmail}
                onChange={setLocalAccountantEmail}
                onBlur={() => handleBlurSave('accountant_email', localAccountantEmail)}
                disabled={isViewer} placeholder="cpa@firm.com" />
              <ContactField label="Phone" type="tel" value={localAccountantPhone}
                onChange={v => setLocalAccountantPhone(formatPhone(v))}
                onBlur={() => handleBlurSave('accountant_phone', localAccountantPhone)}
                disabled={isViewer} placeholder="(555) 123-4567" />
            </ContactCard>

            {/* Broker */}
            <ContactCard
              icon={Briefcase}
              title="Broker / Representative"
              subtitle={localBrokerFirm || "Deal intermediary"}
              accent="#EA580C"
              accentBg="rgba(234, 88, 12, 0.1)"
            >
              <ContactField label="Name" value={localBrokerName}
                onChange={setLocalBrokerName}
                onBlur={() => handleBlurSave('broker_name', localBrokerName)}
                disabled={isViewer} placeholder="Broker name" />
              <ContactField label="Firm" value={localBrokerFirm}
                onChange={setLocalBrokerFirm}
                onBlur={() => handleBlurSave('broker_firm', localBrokerFirm)}
                disabled={isViewer} placeholder="Brokerage firm" />
              <ContactField label="Email" type="email" value={localBrokerEmail}
                onChange={setLocalBrokerEmail}
                onBlur={() => handleBlurSave('broker_email', localBrokerEmail)}
                disabled={isViewer} placeholder="broker@firm.com" />
              <ContactField label="Phone" type="tel" value={localBrokerPhone}
                onChange={v => setLocalBrokerPhone(formatPhone(v))}
                onBlur={() => handleBlurSave('broker_phone', localBrokerPhone)}
                disabled={isViewer} placeholder="(555) 123-4567" />
            </ContactCard>
          </div>
        )}

        {/* Key Dates — legacy reference, will be retired as milestone timeline takes over.
            Kept here for now so the data in deal date fields stays editable. */}
        <div
          className="rounded-xl overflow-hidden"
          style={{ backgroundColor: '#ffffff', border: '1px solid #E5E9F0', boxShadow: '0 1px 2px rgba(15, 23, 42, 0.03)' }}
        >
          <div className="flex items-center gap-2.5 px-4 py-3" style={{ borderBottom: '1px solid #F0F2F6' }}>
            <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
                 style={{ backgroundColor: 'rgba(26, 54, 101, 0.1)', color: '#1A3665' }}>
              <Calendar className="w-4 h-4" strokeWidth={2.2} />
            </div>
            <div>
              <div className="text-xs font-semibold" style={{ color: '#0F172A' }}>Key Dates</div>
              <div className="text-[11px]" style={{ color: '#94A3B8' }}>Legacy date fields — most now auto-flow from the Deal Workflow timeline on Overview</div>
            </div>
          </div>
          <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { k: 'first_visit_date',   l: 'First Visit / Call' },
              { k: 'nda_sent_date',      l: 'NDA Sent' },
              { k: 'nda_executed_date',  l: 'NDA Executed' },
              { k: 'prelim_request_date',l: 'Prelim Request' },
              { k: 'prelim_info_date',   l: 'Prelim Info Received' },
              { k: 'loi_date',           l: 'LOI Date' },
              { k: 'target_close_date',  l: 'Target Close' },
              { k: 'actual_close_date',  l: 'Actual Close' },
            ].map(f => (
              <div key={f.k}>
                <label className="text-[10px] font-semibold uppercase tracking-wider mb-1 block" style={{ color: '#94A3B8' }}>
                  {f.l}
                </label>
                <input
                  type="date"
                  value={localDates[f.k] ?? ''}
                  onChange={e => setLocalDates(p => ({ ...p, [f.k]: e.target.value }))}
                  onBlur={e => handleBlurSave(f.k, e.target.value)}
                  disabled={isViewer}
                  className="w-full h-9 text-sm rounded-lg transition-colors focus:outline-none disabled:bg-slate-50 disabled:cursor-not-allowed tabular-nums"
                  style={{ border: '1px solid #E5E9F0', padding: '0 0.75rem', color: '#0F172A' }}
                  onFocusCapture={e => { e.target.style.borderColor = '#1A3665'; e.target.style.boxShadow = '0 0 0 3px rgba(26, 54, 101, 0.1)'; }}
                  onBlurCapture={e => { e.target.style.borderColor = '#E5E9F0'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Internal Documents upload */}
        {!isViewer && (
          <div
            className="rounded-xl overflow-hidden"
            style={{ backgroundColor: '#ffffff', border: '1px solid #E5E9F0', boxShadow: '0 1px 2px rgba(15, 23, 42, 0.03)' }}
          >
            <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid #F0F2F6' }}>
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
                     style={{ backgroundColor: 'rgba(100, 116, 139, 0.1)', color: '#475569' }}>
                  <FileText className="w-4 h-4" strokeWidth={2.2} />
                </div>
                <div>
                  <div className="text-xs font-semibold" style={{ color: '#0F172A' }}>Internal Documents</div>
                  <div className="text-[11px]" style={{ color: '#94A3B8' }}>LOI, NDA, internal memos — filed to SharePoint "99 Internal LFG Only"</div>
                </div>
              </div>
              <label
                className="h-8 inline-flex items-center gap-1.5 px-3 text-xs font-medium rounded-lg cursor-pointer transition-all hover:shadow-md text-white"
                style={{
                  background: 'linear-gradient(135deg, #1A3665 0%, #2E4B7C 100%)',
                  boxShadow: '0 1px 2px rgba(26, 54, 101, 0.3)',
                }}
              >
                {uploading['internal-doc']
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Upload className="w-3.5 h-3.5" />}
                {uploading['internal-doc'] ? 'Uploading…' : 'Upload'}
                <input
                  ref={el => fileInputRefs.current['internal-doc'] = el}
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.gif,.txt"
                  onChange={async (e) => {
                        var file = e.target.files && e.target.files[0];
                        if (!file || !deal) return;
                        setUploading(prev => ({ ...prev, 'internal-doc': true }));
                        try {
                          // Upload to Supabase storage
                          var storagePath = deal.organization_id + '/' + dealId + '/internal/' + Date.now() + '-' + file.name;
                          var storageRes = await supabase.storage.from('deal-documents').upload(storagePath, file);
                          if (storageRes.error) throw storageRes.error;
                          // Record in files table (no task_id, internal doc)
                          var internalFileName = storagePath.split('/').pop();
                          var { error: filesInsertError } = await supabase.from('files').insert({
                            task_id: null,
                            deal_id: dealId,
                            organization_id: deal.organization_id,
                            name: internalFileName,
                            original_name: file.name,
                            storage_path: storagePath,
                            size_bytes: file.size || null,
                            mime_type: file.type || null,
                            uploaded_by_id: userProfile?.id || null,
                            uploaded_by_name: userProfile?.full_name || 'Unknown',
                            uploaded_by_type: 'user',
                          });
                          if (filesInsertError) {
                            console.error('Files insert error:', JSON.stringify(filesInsertError));
                            throw new Error(filesInsertError.message || filesInsertError.details || 'Failed to record file');
                          }
                          // Sync to SharePoint - goes to 99 Internal LFG Only folder
                          try {
                            await createSharePointDealStructure(deal.name, deal.organization_id);
                            await uploadToSharePoint(file, deal.name, file.name, '5071f078-fab9-4504-9c1b-a5c1bde346f0');
                          } catch (spErr) {
                            console.error('SharePoint sync failed:', spErr);
                            showToast('File saved, but SharePoint sync failed: ' + (spErr?.message || 'unknown error'), 'error');
                          }
                          showToast('Document uploaded');
                          refreshDeal();
                        } catch (err) { showToast(err.message, 'error'); }
                        finally {
                          setUploading(prev => ({ ...prev, 'internal-doc': false }));
                          if (fileInputRefs.current['internal-doc']) fileInputRefs.current['internal-doc'].value = '';
                        }
                      }}
                />
              </label>
            </div>
          </div>
        )}
      </div>
      )}{/* end CONTACTS TAB */}

      {/* === OVERVIEW TAB — timeline, AI intelligence, financials === */}
      {activeTab === 'overview' && (
      <>
      {/* Phase C.2 — Deal Timeline (primary spine of the workflow) */}
      {!isLimitedViewer && (
        <div>
          <MilestoneTimeline
            milestones={milestones}
            loading={milestonesLoading}
            updateMilestone={updateMilestone}
            primaryColor={primaryColor}
            isViewer={isViewer}
            showToast={showToast}
            onKickoff={({ role }) => setPendingDdSendScope(role)}
            dealCreatedAt={deal?.created_at}
          />
        </div>
      )}

      {/* Integration users get a focused single-view: timeline above + just
          their integration-phase tasks here. Everything else (AI, BI,
          Financials, Documents, Diligence, Activity feed) is hidden. */}
      {isIntegration && (
        <IntegrationTasksPanel
          tasks={tasks}
          updateTask={updateTask}
          primaryColor={primaryColor}
        />
      )}

      {/* All remaining Overview content (AI, BI, Financials, etc.) is
          suppressed for integration users — they see only the timeline
          + integration tasks above. */}
      {!isIntegration && (<>
      {/* AI Deal Intelligence — full width between header and tasks */}
      {deal?.ai_summary && !isLimitedViewer && (
        <div
          className="mb-6 overflow-hidden rounded-2xl relative"
          style={{ backgroundColor: '#fff', border: '1px solid #E5E9F0', boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.03)' }}
        >
          {/* Accent strip — purple/violet for AI sections */}
          <div
            className="absolute top-0 left-0 right-0 h-[3px]"
            style={{
              background: 'linear-gradient(90deg, #8B5CF6 0%, #A78BFA 30%, #C4B5FD 70%, transparent 100%)',
            }}
          />
          <div className="px-6 py-5">
            {/* Header row */}
            {(() => {
              // Live counts from allDealFiles, not the stale ai_summary
              // fields — total_expected is stamped once at deal creation
              // and never grows as more files get added later (seller
              // uploads, additional internal uploads, etc.), so a deal
              // created with 2 files but now holding 12 would otherwise
              // clamp to "2 of 2 analyzed" forever, hiding the other 10.
              var liveTotal = (allDealFiles || []).length;
              var liveFailed = (allDealFiles || []).filter(function(f) { return f.ai_error_reason; }).length;
              var livePending = (allDealFiles || []).filter(function(f) { return !f.category_id && !f.ai_error_reason; }).length;
              var liveAnalyzed = liveTotal - liveFailed - livePending;
              return (
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{
                    background: 'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)',
                    boxShadow: '0 2px 6px rgba(139, 92, 246, 0.3)',
                  }}
                >
                  <Sparkles className="w-4 h-4 text-white" strokeWidth={2.2} />
                </div>
                <div>
                  <h2 className="text-base font-semibold tracking-tight" style={{ color: '#0F172A', letterSpacing: '-0.01em' }}>
                    AI Intelligence
                  </h2>
                  <div className="flex items-center gap-2 text-xs mt-0.5">
                    <span style={{ color: '#64748B' }}>
                      {liveAnalyzed} of {liveTotal} documents analyzed
                    </span>
                    {livePending > 0 && (
                      <span className="inline-flex items-center gap-1" style={{ color: '#8B5CF6' }}>
                        <Loader2 className="w-3 h-3 animate-spin" /> Processing
                      </span>
                    )}
                    {livePending > 0 && (
                      <span style={{ color: '#A16207' }}>· {livePending} pending</span>
                    )}
                    {liveFailed > 0 && (
                      <span style={{ color: '#EF4444' }}>· {liveFailed} failed</span>
                    )}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setAiSummaryExpanded(!aiSummaryExpanded)}
                className="h-8 inline-flex items-center gap-1.5 px-3 text-xs font-medium rounded-lg transition-colors hover:bg-purple-50"
                style={{ color: '#7C3AED', border: '1px solid #E9D5FF' }}
              >
                {aiSummaryExpanded ? 'Hide details' : 'View details'}
                {aiSummaryExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              </button>
            </div>
              );
            })()}

            {/* Failure breakdown — surfaces files that didn't categorize so the
                user doesn't have to flip to the Documents tab to see what's wrong. */}
            {(() => {
              var failed = (allDealFiles || []).filter(function(f) { return f.ai_error_reason; });
              if (failed.length === 0) return null;
              return (
                <div
                  className="mb-3 rounded-xl overflow-hidden"
                  style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA' }}
                >
                  <div
                    className="flex items-center gap-2 px-3 py-2 text-[11px] font-semibold uppercase"
                    style={{ color: '#B91C1C', letterSpacing: '0.06em', borderBottom: '1px solid #FECACA' }}
                  >
                    <AlertTriangle className="w-3.5 h-3.5" />
                    {failed.length} file{failed.length === 1 ? '' : 's'} couldn't be analyzed
                  </div>
                  <div className="divide-y" style={{ borderColor: '#FECACA' }}>
                    {failed.map(function(f) {
                      var isRetrying = retryingFileId === f.id;
                      return (
                        <div key={f.id} className="flex items-start gap-3 px-3 py-2 text-[12px]">
                          <FileText className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: '#B91C1C' }} />
                          <div className="flex-1 min-w-0">
                            <div className="truncate font-medium" style={{ color: '#0F172A' }} title={f.original_name}>
                              {f.original_name}
                            </div>
                            <div className="text-[11px] mt-0.5 break-words" style={{ color: '#7F1D1D' }}>
                              {f.ai_error_reason}
                            </div>
                          </div>
                          {!isViewer && !isLimitedViewer && (
                            <button
                              type="button"
                              onClick={function() { handleRetryFile(f); }}
                              disabled={isRetrying || analyzingFiles}
                              className="flex-shrink-0 inline-flex items-center gap-1 h-6 px-2 text-[10px] font-semibold uppercase rounded-md transition-colors hover:bg-red-100 disabled:opacity-50"
                              style={{ color: '#B91C1C', border: '1px solid #FECACA', letterSpacing: '0.04em' }}
                              title="Re-run AI analysis on this file"
                            >
                              {isRetrying
                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                : <RefreshCw className="w-3 h-3" />}
                              {isRetrying ? 'Retrying' : 'Retry'}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Unified stat chips (replaces 12-color rainbow). Narrative + per-category
                detail breakdowns live behind "View details" — pills are the at-a-glance
                view, expansion is the drill-down. */}
            <div className="flex flex-wrap gap-2">
              {/* Financial chips — hidden for integration_viewer who shouldn't
                  see deal economics. Operational chips below stay visible. */}
              {!isIntegration && deal.ai_summary.financial?.revenue && (
                <AiStatChip icon={TrendingUp} label="Revenue" accent="#10B981"
                  value={'$' + Number(deal.ai_summary.financial.revenue).toLocaleString()} />
              )}
              {!isIntegration && deal.ai_summary.financial?.ebitda && (
                <AiStatChip icon={DollarSign} label="EBITDA" accent="#10B981"
                  value={'$' + Number(deal.ai_summary.financial.ebitda).toLocaleString()} />
              )}
              {!isIntegration && deal.ai_summary.financial?.net_income && (
                <AiStatChip icon={DollarSign} label="Net Income" accent="#059669"
                  value={'$' + Number(deal.ai_summary.financial.net_income).toLocaleString()} />
              )}
              {deal.ai_summary.financial?.annual_calls && (
                <AiStatChip icon={PhoneCall} label="Calls / Year" accent="#0EA5E9"
                  value={Number(deal.ai_summary.financial.annual_calls).toLocaleString()} />
              )}
              {deal.ai_summary.personnel && (
                <AiStatChip icon={Users} label="Employees" accent="#1A3665"
                  onClick={() => jumpToBiSection('bi-personnel')}
                  value={isIntegration
                    ? `${deal.ai_summary.personnel.count}`
                    : `${deal.ai_summary.personnel.count} · $${(deal.ai_summary.personnel.total_compensation || 0).toLocaleString()}`} />
              )}
              {deal.ai_summary.vehicles && (
                <AiStatChip icon={Car} label="Vehicles" accent="#1A3665"
                  onClick={() => jumpToBiSection('bi-vehicles')}
                  value={deal.ai_summary.vehicles.total_estimated_value > 0
                    ? `${deal.ai_summary.vehicles.count} · ~$${deal.ai_summary.vehicles.total_estimated_value.toLocaleString()}`
                    : `${deal.ai_summary.vehicles.count}`} />
              )}
              {Array.isArray(deal.ai_summary.real_estate) && deal.ai_summary.real_estate.length > 0 && (() => {
                const total = deal.ai_summary.real_estate.reduce((s, p) => s + (p.assessed_value ? parseFloat(p.assessed_value) : 0), 0);
                return (
                  <AiStatChip icon={Home} label="Properties" accent="#1A3665"
                    value={total > 0 ? `${deal.ai_summary.real_estate.length} · $${total.toLocaleString()}` : `${deal.ai_summary.real_estate.length}`} />
                );
              })()}
              {deal.ai_summary.preneed && (
                <AiStatChip icon={ClipboardList} label="Pre-Need" accent="#8B5CF6"
                  onClick={() => jumpToBiSection('bi-preneed')}
                  value={deal.ai_summary.preneed.total_face_value > 0
                    ? `${deal.ai_summary.preneed.total_contracts || 0} · $${deal.ai_summary.preneed.total_face_value.toLocaleString()}`
                    : `${deal.ai_summary.preneed.total_contracts || 0} contracts`} />
              )}
              {Array.isArray(deal.ai_summary.insurance) && deal.ai_summary.insurance.length > 0 && (
                <AiStatChip icon={Shield} label="Insurance" accent="#1A3665"
                  value={`${deal.ai_summary.insurance.length} polic${deal.ai_summary.insurance.length === 1 ? 'y' : 'ies'}`} />
              )}
              {deal.ai_summary.utilities && (
                <AiStatChip icon={Zap} label="Utilities" accent="#0891B2"
                  onClick={() => jumpToBiSection('bi-utilities')}
                  value={deal.ai_summary.utilities.total_monthly > 0
                    ? `${deal.ai_summary.utilities.count} · $${deal.ai_summary.utilities.total_monthly.toLocaleString()}/mo`
                    : `${deal.ai_summary.utilities.count}`} />
              )}
              {deal.ai_summary.crematory?.has_crematory && (
                <AiStatChip icon={Flame} label="Crematory" accent="#EA580C"
                  onClick={() => jumpToBiSection('bi-crematory')}
                  value={deal.ai_summary.crematory.machine ? deal.ai_summary.crematory.machine.split('(')[0].trim().substring(0, 22) : 'On-site'} />
              )}
              {deal.ai_summary.facilities && (
                <AiStatChip icon={Wrench} label="Facility Contracts" accent="#64748B"
                  onClick={() => jumpToBiSection('bi-facilities')}
                  value={deal.ai_summary.facilities.count} />
              )}
              {deal.ai_summary.technology && (() => {
                var t = deal.ai_summary.technology;
                var bits = [];
                if (t.computers) bits.push(t.computers + ' comp');
                if (t.peripherals) bits.push(t.peripherals + ' periph');
                if (t.software) bits.push(t.software + ' sw');
                var label = bits.join(' · ');
                if (t.total_monthly_software > 0) label += ' · $' + Number(t.total_monthly_software).toLocaleString() + '/mo';
                return (
                  <AiStatChip icon={Monitor} label="Technology" accent="#0EA5E9" value={label}
                    onClick={() => jumpToBiSection('bi-technology')} />
                );
              })()}
            </div>
          </div>

          {/* Expandable: narrative summary + per-category breakdowns + document list */}
          {aiSummaryExpanded && (
            <div className="px-6 py-5 space-y-5" style={{ borderTop: '1px solid #F0F2F6', backgroundColor: '#FAFBFD' }}>

              {/* Narrative — the AI's plain-English summary of what it found */}
              {deal.ai_summary.narrative && (
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: '#94A3B8' }}>
                    Summary
                  </div>
                  <p className="text-sm leading-relaxed" style={{ color: '#334155' }}>
                    {deal.ai_summary.narrative}
                  </p>
                </div>
              )}

              {/* Per-category detail cards — only show categories with extracted detail */}
              {(() => {
                const s = deal.ai_summary;
                const cards = [];

                if (s.personnel?.positions?.length > 0) {
                  cards.push({
                    key: 'personnel', icon: Users, label: 'Employees', accent: '#1A3665',
                    headline: isIntegration
                      ? `${s.personnel.count}`
                      : `${s.personnel.count} · $${(s.personnel.total_compensation || 0).toLocaleString()} total comp`,
                    items: s.personnel.positions,
                  });
                }
                if (s.vehicles?.list?.length > 0) {
                  cards.push({
                    key: 'vehicles', icon: Car, label: 'Vehicles', accent: '#1A3665',
                    headline: s.vehicles.total_estimated_value > 0
                      ? `${s.vehicles.count} · ~$${s.vehicles.total_estimated_value.toLocaleString()} est.`
                      : `${s.vehicles.count}`,
                    items: s.vehicles.list,
                  });
                }
                if (Array.isArray(s.real_estate) && s.real_estate.length > 0) {
                  cards.push({
                    key: 'real_estate', icon: Home, label: 'Properties', accent: '#1A3665',
                    headline: `${s.real_estate.length} propert${s.real_estate.length === 1 ? 'y' : 'ies'}`,
                    items: s.real_estate.map(p => {
                      const parts = [p.property_address || 'Unnamed'];
                      if (p.assessed_value) parts.push('$' + Number(p.assessed_value).toLocaleString());
                      if (p.type) parts.push(p.type);
                      return parts.join(' · ');
                    }),
                  });
                }
                if (Array.isArray(s.insurance) && s.insurance.length > 0) {
                  cards.push({
                    key: 'insurance', icon: Shield, label: 'Insurance', accent: '#1A3665',
                    headline: `${s.insurance.length} polic${s.insurance.length === 1 ? 'y' : 'ies'}`,
                    items: s.insurance.map(p => {
                      const parts = [p.carrier || 'Unknown carrier'];
                      if (p.coverage_type) parts.push(p.coverage_type);
                      if (p.annual_premium) parts.push('$' + Number(p.annual_premium).toLocaleString() + '/yr');
                      return parts.join(' · ');
                    }),
                  });
                }
                if (s.utilities?.providers?.length > 0) {
                  cards.push({
                    key: 'utilities', icon: Zap, label: 'Utilities', accent: '#0891B2',
                    headline: s.utilities.total_monthly > 0
                      ? `${s.utilities.count} · $${s.utilities.total_monthly.toLocaleString()}/mo`
                      : `${s.utilities.count}`,
                    items: s.utilities.providers,
                  });
                }
                if (s.preneed?.companies?.length > 0) {
                  cards.push({
                    key: 'preneed', icon: ClipboardList, label: 'Pre-Need Providers', accent: '#8B5CF6',
                    headline: s.preneed.total_face_value > 0
                      ? `${s.preneed.total_contracts || 0} contracts · $${s.preneed.total_face_value.toLocaleString()}`
                      : `${s.preneed.total_contracts || 0} contracts`,
                    items: s.preneed.companies,
                  });
                }
                if (s.facilities?.types?.length > 0) {
                  cards.push({
                    key: 'facilities', icon: Wrench, label: 'Facility Contracts', accent: '#64748B',
                    headline: `${s.facilities.count} contract${s.facilities.count === 1 ? '' : 's'}`,
                    items: s.facilities.types,
                  });
                }
                if (s.crematory?.has_crematory) {
                  const items = [];
                  if (s.crematory.machine) items.push('Machine: ' + s.crematory.machine);
                  if (s.crematory.capacity) items.push('Capacity: ' + s.crematory.capacity);
                  if (items.length > 0) {
                    cards.push({
                      key: 'crematory', icon: Flame, label: 'Crematory', accent: '#EA580C',
                      headline: 'On-site',
                      items,
                    });
                  }
                }
                if (!isIntegration && s.financial && (s.financial.revenue || s.financial.ebitda || s.financial.net_income)) {
                  const items = [];
                  if (s.financial.revenue)              items.push('Revenue: $' + Number(s.financial.revenue).toLocaleString());
                  if (s.financial.cost_of_goods)        items.push('COGS: $' + Number(s.financial.cost_of_goods).toLocaleString());
                  if (s.financial.gross_profit)         items.push('Gross Profit: $' + Number(s.financial.gross_profit).toLocaleString());
                  if (s.financial.ebitda)               items.push('EBITDA: $' + Number(s.financial.ebitda).toLocaleString());
                  if (s.financial.net_income)           items.push('Net Income: $' + Number(s.financial.net_income).toLocaleString());
                  if (s.financial.officer_compensation) items.push('Officer Comp: $' + Number(s.financial.officer_compensation).toLocaleString());
                  if (s.financial.annual_calls)         items.push(Number(s.financial.annual_calls).toLocaleString() + ' annual calls');
                  if (s.financial.average_sale)         items.push('Avg Sale: $' + Number(s.financial.average_sale).toLocaleString());
                  if (s.financial.burial_pct != null)   items.push('Burial: ' + s.financial.burial_pct + '%');
                  if (s.financial.cremation_pct != null)items.push('Cremation: ' + s.financial.cremation_pct + '%');
                  if (items.length > 0) {
                    cards.push({
                      key: 'financial', icon: TrendingUp, label: 'Financial Detail',
                      accent: '#10B981',
                      headline: s.financial.period ? 'Period ' + s.financial.period : 'Latest period',
                      items,
                    });
                  }
                }
                if (s.technology) {
                  const items = [];
                  if (s.technology.computers) items.push(`${s.technology.computers} computer${s.technology.computers === 1 ? '' : 's'}`);
                  if (s.technology.peripherals) items.push(`${s.technology.peripherals} peripheral${s.technology.peripherals === 1 ? '' : 's'} (printers/copiers/etc.)`);
                  if (s.technology.software) items.push(`${s.technology.software} software subscription${s.technology.software === 1 ? '' : 's'}`);
                  if (s.technology.total_monthly_software > 0) {
                    items.push(`$${s.technology.total_monthly_software.toLocaleString()}/mo software spend`);
                  }
                  if (items.length > 0) {
                    cards.push({
                      key: 'technology', icon: Monitor, label: 'Technology', accent: '#0EA5E9',
                      headline: '',
                      items,
                    });
                  }
                }

                if (cards.length === 0) return null;
                return (
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider mb-3" style={{ color: '#94A3B8' }}>
                      Extracted Detail
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {cards.map(c => (
                        <AiDetailCard
                          key={c.key}
                          icon={c.icon}
                          label={c.label}
                          accent={c.accent}
                          headline={c.headline}
                          items={c.items}
                        />
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Document list — for integration_viewer, drop docs whose
                  source file is in a restricted category so the SP-deeplink
                  click handler can't reach them. */}
              {deal.ai_summary.documents?.length > 0 && (() => {
                const docs = isIntegration
                  ? deal.ai_summary.documents.filter(d => {
                      if (!d.file_id) return true;
                      const f = (allDealFiles || []).find(x => x.id === d.file_id);
                      return !f || !RESTRICTED_CATEGORY_SLUGS.has(f.category_slug);
                    })
                  : deal.ai_summary.documents;
                if (docs.length === 0) return null;
                return (
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wider mb-3" style={{ color: '#94A3B8' }}>
                    Processed Documents
                  </div>
                  <div className="space-y-1.5">
                    {docs.map((doc, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg transition-colors hover:bg-white group"
                      >
                        <FileText className="w-4 h-4 flex-shrink-0" style={{ color: '#94A3B8' }} />
                        <button
                          type="button"
                          className="text-sm text-left truncate flex-1 min-w-0 transition-colors"
                          style={{ color: '#0F172A' }}
                          onMouseEnter={e => { e.currentTarget.style.color = '#1A3665'; }}
                          onMouseLeave={e => { e.currentTarget.style.color = '#0F172A'; }}
                          title={doc.summary || doc.file_name}
                          onClick={async () => {
                            try {
                              if (doc.storage_path?.startsWith('sharepoint://') && doc.sharepoint_url) {
                                window.open(doc.sharepoint_url, '_blank');
                              } else if (doc.storage_path && !doc.storage_path.startsWith('sharepoint://')) {
                                const url = await getFileUrl(doc.storage_path);
                                window.open(url, '_blank');
                              } else {
                                showToast('File path not available', 'error');
                              }
                            } catch { showToast('Could not open file', 'error'); }
                          }}
                        >
                          {doc.file_name}
                        </button>
                        {doc.category && (
                          <span
                            className="text-[11px] font-medium px-2 py-0.5 rounded-md flex-shrink-0"
                            style={{ backgroundColor: '#F3E8FF', color: '#7C3AED' }}
                          >
                            {doc.category.split('|')[0]?.trim() || ''}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                );
              })()}

              {deal.ai_summary.last_updated && (
                <div className="text-[11px] text-right" style={{ color: '#94A3B8' }}>
                  Last updated {new Date(deal.ai_summary.last_updated).toLocaleString()}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {!isLimitedViewer && !isIntegration && (
        <AskAIPanel
          dealId={dealId}
          dealName={deal?.name}
          allDealFiles={visibleDealFiles}
          onOpenFile={(file) => {
            if (!file) return;
            handleDownloadFile(file.storage_path, file.original_name);
          }}
        />
      )}
      </>
      )}{/* end !isIntegration wrap */}
      </>
      )}{/* end OVERVIEW (timeline + AI block) */}

      {/* === DOCUMENTS TAB — Files Section === */}
      {activeTab === 'documents' && (
      <>
      {/* Files Section - all deal files */}
      {!isLimitedViewer && (
      <div
        className="mb-6 overflow-hidden rounded-2xl relative"
        style={{ backgroundColor: '#fff', border: '1px solid #E5E9F0', boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.03)' }}
      >
        {/* Accent strip — slate gradient for the Documents hub */}
        <div
          className="absolute top-0 left-0 right-0 h-[3px]"
          style={{ background: 'linear-gradient(90deg, #64748B 0%, #94A3B8 30%, #CBD5E1 70%, transparent 100%)' }}
        />
        <button type="button"
          onClick={() => setFilesExpanded(!filesExpanded)}
          className="w-full flex items-center justify-between px-6 py-5 hover:bg-[#FAFBFD] transition-colors text-left"
        >
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{
                background: 'linear-gradient(135deg, #475569 0%, #64748B 100%)',
                boxShadow: '0 2px 6px rgba(71, 85, 105, 0.25)',
              }}
            >
              <FileText className="w-4 h-4 text-white" strokeWidth={2.2} />
            </div>
            <div>
              <h2 className="text-base font-semibold tracking-tight" style={{ color: '#0F172A', letterSpacing: '-0.01em' }}>
                Documents
              </h2>
              <div className="text-xs mt-0.5 tabular-nums" style={{ color: '#64748B' }}>
                {visibleDealFiles.length > 0
                  ? `${visibleDealFiles.length} file${visibleDealFiles.length === 1 ? '' : 's'} across deal folders and SharePoint`
                  : 'No files yet — uploads from the seller portal or internal uploads will appear here'}
              </div>
            </div>
          </div>
          {filesExpanded ? <ChevronDown className="w-4 h-4" style={{ color: '#94A3B8' }} /> : <ChevronRight className="w-4 h-4" style={{ color: '#94A3B8' }} />}
        </button>
        {filesExpanded && (
          <div style={{ borderTop: '1px solid #F0F2F6' }}>
            {visibleDealFiles.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <div className="w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: '#F1F5F9' }}>
                  <FileText className="w-6 h-6" style={{ color: '#94A3B8' }} />
                </div>
                <p className="text-sm font-medium mb-1" style={{ color: '#0F172A' }}>No documents yet</p>
                <p className="text-xs" style={{ color: '#64748B' }}>
                  Files uploaded via the Diligence Checklist, seller portal, or SharePoint scan will appear here.
                </p>
              </div>
            ) : (
              <div className="max-h-[32rem] overflow-y-auto">
                {visibleDealFiles.map(function(file) {
                  var ext = (file.original_name || '').split('.').pop()?.toLowerCase();
                  var folderPath = '';
                  if (file.storage_path?.startsWith('sharepoint://')) {
                    var parts = file.storage_path.replace('sharepoint://Acquisitions/' + deal.name + '/', '').split('/');
                    parts.pop();
                    folderPath = parts.join('/');
                  }
                  var fileTypeColor = ext === 'pdf' ? '#DC2626'
                    : ['xls','xlsx','csv'].includes(ext) ? '#059669'
                    : ['doc','docx'].includes(ext) ? '#1D4ED8'
                    : ['jpg','jpeg','png','gif','webp','svg'].includes(ext) ? '#7C3AED'
                    : '#64748B';
                  return (
                    <div
                      key={file.id}
                      className="flex items-center gap-3 px-6 py-3 transition-colors hover:bg-[#FAFBFD] group cursor-pointer"
                      style={{ borderBottom: '1px solid #F0F2F6' }}
                      onClick={function() { handleDownloadFile(file.storage_path, file.original_name); }}
                    >
                      <div
                        className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 text-[9px] font-bold uppercase tabular-nums"
                        style={{ backgroundColor: `${fileTypeColor}15`, color: fileTypeColor }}
                      >
                        {ext ? ext.slice(0, 4) : <FileText className="w-4 h-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate flex items-center gap-2" style={{ color: '#0F172A' }}>
                          <span className="truncate">{file.original_name}</span>
                          {file.ai_error_reason ? (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded-md flex-shrink-0"
                              style={{ backgroundColor: '#FEE2E2', color: '#B91C1C', letterSpacing: '0.04em' }}
                              title={file.ai_error_reason}
                            >
                              <AlertTriangle className="w-3 h-3" />
                              AI failed
                            </span>
                          ) : !file.category_id ? (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded-md flex-shrink-0"
                              style={{ backgroundColor: '#FEF3C7', color: '#A16207', letterSpacing: '0.04em' }}
                              title="Click Analyze to run AI categorization on this file"
                            >
                              <Clock className="w-3 h-3" />
                              Pending AI
                            </span>
                          ) : null}
                        </div>
                        <div className="flex items-center gap-2 text-[11px] mt-0.5" style={{ color: '#94A3B8' }}>
                          {folderPath && (
                            <span className="truncate max-w-[240px]" title={folderPath}>{folderPath}</span>
                          )}
                          {folderPath && <span style={{ color: '#CBD5E1' }}>·</span>}
                          <span className="tabular-nums">
                            {file.size_bytes ? (file.size_bytes > 1048576 ? (file.size_bytes / 1048576).toFixed(1) + ' MB' : Math.round(file.size_bytes / 1024) + ' KB') : '—'}
                          </span>
                          <span style={{ color: '#CBD5E1' }}>·</span>
                          <span className="tabular-nums">
                            {file.created_at ? new Date(file.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                          </span>
                          {file.uploaded_by_type && (
                            <>
                              <span style={{ color: '#CBD5E1' }}>·</span>
                              <span className="capitalize">
                                {file.uploaded_by_type}
                                {file.uploaded_by_name ? ` (${file.uploaded_by_name})` : ''}
                              </span>
                            </>
                          )}
                          {file.ai_error_reason && (
                            <>
                              <span style={{ color: '#CBD5E1' }}>·</span>
                              <span className="truncate max-w-[300px]" style={{ color: '#B91C1C' }} title={file.ai_error_reason}>
                                {file.ai_error_reason}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      {file.ai_error_reason && !isViewer && !isLimitedViewer && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleRetryFile(file); }}
                          disabled={retryingFileId === file.id || analyzingFiles}
                          className="flex-shrink-0 inline-flex items-center gap-1 h-7 px-2 text-[10px] font-semibold uppercase rounded-md transition-colors hover:bg-red-50 disabled:opacity-50"
                          style={{ color: '#B91C1C', border: '1px solid #FECACA', letterSpacing: '0.04em' }}
                          title="Re-run AI analysis on this file"
                        >
                          {retryingFileId === file.id
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <RefreshCw className="w-3 h-3" />}
                          {retryingFileId === file.id ? 'Retrying' : 'Retry'}
                        </button>
                      )}
                      {!file.ai_error_reason && file.category_id && !isViewer && !isLimitedViewer && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleReanalyzeFile(file); }}
                          disabled={reanalyzingFileId === file.id}
                          className="flex-shrink-0 inline-flex items-center justify-center h-7 w-7 rounded-md transition-colors hover:bg-gray-100 disabled:opacity-50"
                          style={{ color: '#94A3B8' }}
                          title="Re-analyze this file (get a fresh AI read — e.g. after an extraction fix, or to re-check for updated field proposals)"
                        >
                          {reanalyzingFileId === file.id
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : <RefreshCw className="w-3.5 h-3.5" />}
                        </button>
                      )}
                      {!isViewer && !isLimitedViewer && !isIntegration && fileCategories.length > 0 && (
                        <div onClick={(e) => e.stopPropagation()} className="flex-shrink-0">
                          <FileCategoryMenu
                            file={file}
                            categories={fileCategories}
                            onChange={(newId) => handleRecategorizeFile(file, newId)}
                          />
                        </div>
                      )}
                      <Download className="w-4 h-4 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: '#64748B' }} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
      )}
      </>
      )}{/* end DOCUMENTS */}

      {/* === DILIGENCE TAB — Business Info + DD Checklist === */}
      {activeTab === 'diligence' && (
      <>
      {/* Business Information Section - always visible. Deal advisors only
          get the DD checklist below, not operational rosters/vehicles/etc. */}
      {!isLimitedViewer && !isDealAdvisor && (
      <div
        className="mb-6 overflow-hidden rounded-2xl relative"
        style={{ backgroundColor: '#fff', border: '1px solid #E5E9F0', boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.03)' }}
      >
          {/* Accent strip — cyan for operational/info-collection sections */}
          <div
            className="absolute top-0 left-0 right-0 h-[3px]"
            style={{ background: 'linear-gradient(90deg, #0891B2 0%, #06B6D4 30%, #67E8F9 70%, transparent 100%)' }}
          />
          <button type="button"
            onClick={() => setInfoExpanded(!infoExpanded)}
            className="w-full flex items-center justify-between px-6 py-5 hover:bg-[#FAFBFD] transition-colors text-left"
          >
            <div className="flex items-center gap-3 flex-wrap">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{
                  background: 'linear-gradient(135deg, #0891B2 0%, #06B6D4 100%)',
                  boxShadow: '0 2px 6px rgba(8, 145, 178, 0.25)',
                }}
              >
                <ClipboardList className="w-4 h-4 text-white" strokeWidth={2.2} />
              </div>
              <div>
                <h2 className="text-base font-semibold tracking-tight" style={{ color: '#0F172A', letterSpacing: '-0.01em' }}>
                  Business Information
                </h2>
                <div className="flex items-center gap-2 text-xs mt-0.5" style={{ color: '#64748B' }}>
                  {infoRequest ? (
                    infoRequest.status === 'complete' ? (
                      <span className="inline-flex items-center gap-1 font-medium" style={{ color: '#059669' }}>
                        <CheckCircle className="w-3.5 h-3.5" /> All 7 sections complete
                      </span>
                    ) : infoRequest.status === 'partial' ? (
                      <span className="tabular-nums">
                        <span className="font-medium" style={{ color: '#1A3665' }}>
                          {(infoRequest.categories_submitted || []).length} of 7
                        </span> sections submitted
                      </span>
                    ) : (
                      <span style={{ color: '#A16207' }}>Pending — awaiting seller response</span>
                    )
                  ) : (
                    <span>Rosters, vehicles, utilities, tech, pre-need, facilities, crematory</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {infoData && (infoRequest?.categories_submitted?.length > 0) && (
                <div className="relative group" onClick={(e) => e.stopPropagation()}>
                  <button type="button"
                    className="h-8 inline-flex items-center gap-1.5 px-3 text-xs font-medium rounded-lg transition-colors hover:bg-slate-50"
                    style={{ backgroundColor: 'white', color: '#475569', border: '1px solid #E5E9F0' }}
                    title="Export to CSV"
                  >
                    <Download className="w-3.5 h-3.5" /> Export <ChevronDown className="w-3 h-3" />
                  </button>
                  <div className="absolute right-0 top-full pt-1 z-10 hidden group-hover:block">
                    <div
                      className="bg-white rounded-lg py-1 min-w-[180px]"
                      style={{ border: '1px solid #E5E9F0', boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)' }}
                    >
                      <button type="button" onClick={() => handleExportBusinessInfo()} className="block w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50" style={{ color: '#334155' }}>Export All (Excel)</button>
                      {infoData.personnel?.length > 0 && <button type="button" onClick={() => handleExportBusinessInfo('personnel')} className="block w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50" style={{ color: '#334155' }}>Personnel Only (CSV)</button>}
                      {infoData.vehicles?.length > 0 && <button type="button" onClick={() => handleExportBusinessInfo('vehicles')} className="block w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50" style={{ color: '#334155' }}>Vehicles Only (CSV)</button>}
                    </div>
                  </div>
                </div>
              )}
              {!isViewer && (
                <button type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setHrSurveyEmail(localEmail || deal?.primary_email || '');
                    setHrSurveyName(deal?.primary_contact || '');
                    setShowHrSurveyModal(true);
                  }}
                  disabled={sendingHrSurvey}
                  className="h-8 inline-flex items-center gap-1.5 px-3 text-xs font-medium rounded-lg text-white transition-all hover:shadow-md disabled:opacity-50"
                  style={{
                    background: 'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)',
                    boxShadow: '0 1px 2px rgba(124, 58, 237, 0.3)',
                  }}
                  title="Send seller a link to fill out just the HR survey"
                >
                  {sendingHrSurvey ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ClipboardList className="w-3.5 h-3.5" />}
                  HR Survey
                </button>
              )}
              {!isViewer && !infoRequest && (localEmail || deal?.primary_email) && (
                <button type="button"
                  onClick={(e) => { e.stopPropagation(); setSelectedInfoEmail(localEmail || deal?.primary_email || ''); setShowInfoEmailModal(true); }}
                  disabled={sendingInfoRequest}
                  className="h-8 inline-flex items-center gap-1.5 px-3 text-xs font-medium rounded-lg text-white transition-all hover:shadow-md disabled:opacity-50"
                  style={{
                    background: 'linear-gradient(135deg, #0891B2 0%, #06B6D4 100%)',
                    boxShadow: '0 1px 2px rgba(8, 145, 178, 0.3)',
                  }}
                  title="Send seller a link to fill out business information"
                >
                  {sendingInfoRequest ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  Request Info
                </button>
              )}
              {!isViewer && (
                <button type="button"
                  onClick={(e) => { e.stopPropagation(); setEditingInfo(true); }}
                  className="h-8 inline-flex items-center gap-1.5 px-3 text-xs font-medium rounded-lg transition-colors hover:bg-slate-50"
                  style={{ backgroundColor: 'white', color: '#475569', border: '1px solid #E5E9F0' }}
                  title="Enter or edit business information on behalf of the seller"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  {infoData && (infoRequest?.categories_submitted?.length > 0) ? 'Edit' : 'Enter Manually'}
                </button>
              )}
              {infoExpanded ? <ChevronDown className="w-4 h-4" style={{ color: '#94A3B8' }} /> : <ChevronRight className="w-4 h-4" style={{ color: '#94A3B8' }} />}
            </div>
          </button>

          {infoExpanded && infoData && (
            <div className="px-6 py-6 space-y-6" style={{ borderTop: '1px solid #F0F2F6', backgroundColor: '#FAFBFD' }}>

              {/* Re-scan button — visible whenever Business Info has data,
                  even if there are currently no flagged dups. Lets the user
                  manually check for duplicates after editing rows by hand or
                  if they think the system missed something. */}
              {(infoData.personnel?.length > 0 || infoData.vehicles?.length > 0) && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleRunDedup}
                    className="h-7 inline-flex items-center gap-1 px-2.5 text-[11px] font-medium rounded-md transition-colors hover:bg-slate-50"
                    style={{ backgroundColor: 'white', color: '#475569', border: '1px solid #E5E9F0' }}
                    title="Re-scan personnel and vehicles for possible duplicates"
                  >
                    <Search className="w-3 h-3" /> Re-scan for duplicates
                  </button>
                </div>
              )}

              {/* Possible duplicates panel — only renders when something is
                  flagged. Each kind (personnel, vehicles) is its own
                  collapsible group so a deal with 30+ flags doesn't dump
                  everything onto the user at once. */}
              {(duplicatePairs.personnel.length > 0 || duplicatePairs.vehicles.length > 0) && (
                <div
                  className="rounded-xl overflow-hidden"
                  style={{ backgroundColor: '#FFFBEB', border: '1px solid #FDE68A', boxShadow: '0 1px 2px rgba(15, 23, 42, 0.03)' }}
                >
                  <div
                    className="flex items-center gap-2 px-4 py-3 flex-wrap"
                    style={{ borderBottom: '1px solid #FDE68A' }}
                  >
                    <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FEF3C7', color: '#A16207' }}>
                      <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2.2} />
                    </div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#92400E' }}>
                      Possible Duplicates
                    </div>
                    <span className="text-[10px] tabular-nums px-1.5 py-0.5 rounded font-semibold" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
                      {duplicatePairs.personnel.length + duplicatePairs.vehicles.length}
                    </span>
                    <span className="text-[11px] ml-auto" style={{ color: '#92400E' }}>
                      AI flagged these as likely matches — confirm or merge.
                    </span>
                  </div>
                  <div className="p-3 space-y-2">
                    {duplicatePairs.personnel.length > 0 && (
                      <DuplicateGroup
                        kind="personnel"
                        title="Personnel"
                        icon={Users}
                        pairs={duplicatePairs.personnel}
                        files={duplicatePairs.files || {}}
                        onResolve={handleResolveDuplicate}
                      />
                    )}
                    {duplicatePairs.vehicles.length > 0 && (
                      <DuplicateGroup
                        kind="vehicles"
                        title="Vehicles"
                        icon={Car}
                        pairs={duplicatePairs.vehicles}
                        files={duplicatePairs.files || {}}
                        onResolve={handleResolveDuplicate}
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Personnel */}
              {infoData.personnel?.length > 0 && (
                <InfoSection title="Personnel" icon={Users} count={infoData.personnel?.length} anchorId="bi-personnel">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left p-2 border font-medium w-6"></th>
                        <th className="text-left p-2 border font-medium">Name</th>
                        <th className="text-left p-2 border font-medium">Position</th>
                        <th className="text-left p-2 border font-medium">Type</th>
                        {!isIntegration && <th className="text-left p-2 border font-medium">Rate</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {infoData.personnel.map((p, i) => {
                        const isExpanded = expandedPersonnelId === i;
                        return (
                          <React.Fragment key={i}>
                            <tr
                              className="hover:bg-blue-50 cursor-pointer transition-colors"
                              onClick={() => setExpandedPersonnelId(isExpanded ? null : i)}
                            >
                              <td className="p-2 border text-gray-400">{isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}</td>
                              <td className="p-2 border font-medium">
                                <span className="inline-flex items-center gap-1.5">
                                  {[p.first_name, p.middle_initial, p.last_name].filter(Boolean).join(' ') || '—'}
                                  {p.possible_duplicate_of && (
                                    <span
                                      className="inline-flex items-center gap-0.5 text-[9px] font-semibold uppercase px-1 py-0.5 rounded"
                                      style={{ backgroundColor: '#FEF3C7', color: '#92400E', letterSpacing: '0.04em' }}
                                      title={`Flagged as a possible duplicate (${Math.round((p.dup_confidence || 0) * 100)}% match) — see Possible Duplicates above`}
                                    >
                                      <AlertTriangle className="w-2.5 h-2.5" /> Dup
                                    </span>
                                  )}
                                </span>
                              </td>
                              <td className="p-2 border">{p.position || '—'}</td>
                              <td className="p-2 border">{p.employment_type || '—'}</td>
                              {!isIntegration && (
                                <td className="p-2 border">
                                  {p.hourly_rate ? `$${Number(p.hourly_rate).toLocaleString()}/hr` : p.salary_rate ? `$${Number(p.salary_rate).toLocaleString()}/yr` : '—'}
                                </td>
                              )}
                            </tr>
                            {isExpanded && (
                              <tr>
                                <td colSpan={isIntegration ? 4 : 5} className="border p-0">
                                  <div className="bg-blue-50/50 p-3">
                                    <div className="grid grid-cols-4 gap-x-4 gap-y-2 text-xs">
                                      {p.phone_number && <div><span className="text-gray-500">Phone</span><div>{p.phone_number}</div></div>}
                                      {p.email_address && <div><span className="text-gray-500">Email</span><div>{p.email_address}</div></div>}
                                      {p.hire_date && <div><span className="text-gray-500">Hire Date</span><div>{new Date(p.hire_date).toLocaleDateString()}</div></div>}
                                      {p.department && <div><span className="text-gray-500">Department</span><div>{p.department}</div></div>}
                                      {p.hours_per_year && <div><span className="text-gray-500">Hours/Year</span><div>{p.hours_per_year}</div></div>}
                                      {!isIntegration && p.hourly_rate && <div><span className="text-gray-500">Hourly Rate</span><div>${Number(p.hourly_rate).toLocaleString()}</div></div>}
                                      {!isIntegration && p.salary_rate && <div><span className="text-gray-500">Salary</span><div>${Number(p.salary_rate).toLocaleString()}/yr</div></div>}
                                      {!isIntegration && p.ytd_pto_accrued && <div><span className="text-gray-500">YTD PTO</span><div>{p.ytd_pto_accrued}</div></div>}
                                      {!isIntegration && p.pto_entitlement && <div><span className="text-gray-500">PTO Entitlement</span><div>{p.pto_entitlement}</div></div>}
                                      {(p.address || p.city) && <div><span className="text-gray-500">Address</span><div>{[p.address, p.city, p.state, p.zip].filter(Boolean).join(', ')}</div></div>}
                                      {p.birth_date && <div><span className="text-gray-500">DOB</span><div>{new Date(p.birth_date).toLocaleDateString()}</div></div>}
                                      {p.drivers_license && <div><span className="text-gray-500">License</span><div>{p.drivers_license}</div></div>}
                                    </div>
                                    {/* Benefits row */}
                                    {(p.has_401k || p.company_credit_card || p.phone_stipend || p.company_vehicle || p.life_insurance || p.clothing_allowance) && (
                                      <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-blue-200">
                                        {p.has_401k && <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px] font-medium">401(k)</span>}
                                        {p.company_credit_card && <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-medium">Co. Credit Card</span>}
                                        {p.phone_stipend != null && Number(p.phone_stipend) > 0 && <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-[10px] font-medium">Phone ${Number(p.phone_stipend)}/mo</span>}
                                        {p.company_vehicle && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-medium">Co. Vehicle</span>}
                                        {p.life_insurance && <span className="px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-medium">Life Insurance</span>}
                                        {p.clothing_allowance != null && Number(p.clothing_allowance) > 0 && <span className="px-2 py-0.5 rounded-full bg-pink-100 text-pink-700 text-[10px] font-medium">Clothing ${Number(p.clothing_allowance)}/yr</span>}
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </InfoSection>
              )}

              {/* Vehicles */}
              {infoData.vehicles?.length > 0 && (
                <InfoSection title="Vehicles" icon={Car} count={infoData.vehicles?.length} anchorId="bi-vehicles">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left p-2 border font-medium w-6"></th>
                        <th className="text-left p-2 border font-medium">Vehicle</th>
                        <th className="text-left p-2 border font-medium">VIN</th>
                        <th className="text-left p-2 border font-medium">Mileage</th>
                        <th className="text-left p-2 border font-medium">Est. Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {infoData.vehicles.map((v, i) => {
                        const isVExpanded = expandedVehicleId === i;
                        return (
                          <React.Fragment key={i}>
                            <tr
                              className="hover:bg-green-50 cursor-pointer transition-colors"
                              onClick={() => setExpandedVehicleId(isVExpanded ? null : i)}
                            >
                              <td className="p-2 border text-gray-400">{isVExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}</td>
                              <td className="p-2 border font-medium">
                                <span className="inline-flex items-center gap-1.5">
                                  {[v.year, v.make, v.model].filter(Boolean).join(' ') || '—'}
                                  {v.possible_duplicate_of && (
                                    <span
                                      className="inline-flex items-center gap-0.5 text-[9px] font-semibold uppercase px-1 py-0.5 rounded"
                                      style={{ backgroundColor: '#FEF3C7', color: '#92400E', letterSpacing: '0.04em' }}
                                      title={`Flagged as a possible duplicate (${Math.round((v.dup_confidence || 0) * 100)}% match) — see Possible Duplicates above`}
                                    >
                                      <AlertTriangle className="w-2.5 h-2.5" /> Dup
                                    </span>
                                  )}
                                </span>
                              </td>
                              <td className="p-2 border font-mono text-gray-500">{v.vin || '—'}</td>
                              <td className="p-2 border">{v.mileage ? Number(v.mileage).toLocaleString() : '—'}</td>
                              <td className="p-2 border">{v.kelly_blue_book ? `$${Number(v.kelly_blue_book).toLocaleString()}` : '—'}</td>
                            </tr>
                            {isVExpanded && (
                              <tr>
                                <td colSpan={5} className="border p-0">
                                  <div className="bg-green-50/50 p-3">
                                    <div className="grid grid-cols-4 gap-x-4 gap-y-2 text-xs">
                                      {v.vin && <div><span className="text-gray-500">VIN</span><div className="font-mono">{v.vin}</div></div>}
                                      {v.mileage && <div><span className="text-gray-500">Mileage</span><div>{Number(v.mileage).toLocaleString()}</div></div>}
                                      <div><span className="text-gray-500">Loan</span><div>{v.has_loan ? 'Yes' : 'No'}</div></div>
                                      {v.loan_holder && <div><span className="text-gray-500">Loan Holder</span><div>{v.loan_holder}</div></div>}
                                      {v.balance_owed && <div><span className="text-gray-500">Balance Owed</span><div>${Number(v.balance_owed).toLocaleString()}</div></div>}
                                      {v.kelly_blue_book && <div><span className="text-gray-500">Est. Value</span><div>${Number(v.kelly_blue_book).toLocaleString()}</div></div>}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </InfoSection>
              )}

              {/* Utilities */}
              {infoData.utilities?.length > 0 && (
                <InfoSection title="Utilities" icon={Zap} count={infoData.utilities?.length} anchorId="bi-utilities">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left p-2 border font-medium">Type</th>
                        <th className="text-left p-2 border font-medium">Vendor</th>
                        <th className="text-left p-2 border font-medium">Account #</th>
                      </tr>
                    </thead>
                    <tbody>
                      {infoData.utilities.map((u, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="p-2 border capitalize">{(u.utility_type || '').replace('_', ' ')}</td>
                          <td className="p-2 border">{u.vendor_name || '—'}</td>
                          <td className="p-2 border">{u.account_number || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </InfoSection>
              )}

              {/* Preneed */}
              {infoData.preneed?.length > 0 && (
                <InfoSection title="Preneed" icon={ClipboardList} count={infoData.preneed?.length} anchorId="bi-preneed">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left p-2 border font-medium">Company</th>
                        <th className="text-left p-2 border font-medium">Contract #</th>
                        <th className="text-left p-2 border font-medium">Contact</th>
                      </tr>
                    </thead>
                    <tbody>
                      {infoData.preneed.map((p, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="p-2 border">{p.company_name || '—'}</td>
                          <td className="p-2 border">{p.contract_number || '—'}</td>
                          <td className="p-2 border">{p.contact_name || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </InfoSection>
              )}

              {/* Facilities */}
              {infoData.facilities?.length > 0 && (
                <InfoSection title="Facilities" icon={Wrench} count={infoData.facilities?.length} anchorId="bi-facilities">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left p-2 border font-medium">Type</th>
                        <th className="text-left p-2 border font-medium">Vendor</th>
                        <th className="text-left p-2 border font-medium">Phone</th>
                      </tr>
                    </thead>
                    <tbody>
                      {infoData.facilities.map((f, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="p-2 border capitalize">{(f.facility_type || '').replace(/_/g, ' ')}</td>
                          <td className="p-2 border">{f.vendor_name || '—'}</td>
                          <td className="p-2 border">{f.vendor_phone || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </InfoSection>
              )}

              {/* Personnel Survey — HR / pay / benefits answers from seller. Renders via SurveyReview using the schema in survey_definitions. */}
              {infoData.personnel_survey && surveySchemas.personnel_survey && (
                <InfoSection title={surveySchemas.personnel_survey.title || 'HR Survey'} icon={Users} anchorId="bi-personnel-survey">
                  <SurveyReview schema={surveySchemas.personnel_survey} data={infoData.personnel_survey} />
                </InfoSection>
              )}

              {/* Crematory */}
              {infoData.crematory?.length > 0 && (
                <InfoSection title="Crematory" icon={Flame} anchorId="bi-crematory">
                  <div className="space-y-3">
                    {infoData.crematory.map((c, i) => (
                      <div key={i} className="border rounded p-2">
                        {infoData.crematory.length > 1 && <div className="text-xs font-medium text-gray-400 mb-1">Retort {i + 1}</div>}
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          {[
                            ['Machine', c.machine],
                            ['Installation Date', c.installation_date],
                            ['Last Service Date', c.last_service_date],
                            ['Capacity', c.capacity ? `${c.capacity}/day` : null],
                            ['EPA Certification', c.epa_certification],
                            ['Regulatory Info', c.regulatory_info],
                            ['Maintenance Program', c.maintenance_program],
                          ].filter(([, v]) => v).map(([label, value]) => (
                            <div key={label} className="bg-gray-50 rounded p-1.5">
                              <div className="text-gray-400 text-xs">{label}</div>
                              <div className="font-medium">{value}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </InfoSection>
              )}

              {/* Technology */}
              {(infoData.tech_computers?.length > 0 || infoData.tech_peripherals?.length > 0 || infoData.tech_software?.length > 0) && (
                <InfoSection title="Technology" icon={Monitor} anchorId="bi-technology">
                  {infoData.tech_computers?.length > 0 && (
                    <div className="mb-3">
                      <div className="text-xs font-medium text-gray-500 mb-1">Computers &amp; Servers ({infoData.tech_computers.length})</div>
                      <div className="space-y-1">
                        {infoData.tech_computers.map((c, i) => (
                          <div key={i} className="text-xs bg-gray-50 rounded px-2 py-1.5">
                            {[c.computer_type, c.make, c.model].filter(Boolean).join(' · ')}
                            {c.serial_number && <span className="text-gray-400 ml-2 font-mono">#{c.serial_number}</span>}
                            {c.notes && <div className="text-[11px] text-gray-500 mt-0.5">{c.notes}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {infoData.tech_peripherals?.length > 0 && (
                    <div className="mb-3">
                      <div className="text-xs font-medium text-gray-500 mb-1">Peripherals ({infoData.tech_peripherals.length})</div>
                      <div className="space-y-1">
                        {infoData.tech_peripherals.map((c, i) => (
                          <div key={i} className="text-xs bg-gray-50 rounded px-2 py-1.5">
                            {[c.computer_type, c.make, c.model].filter(Boolean).join(' · ')}
                            {c.serial_number && <span className="text-gray-400 ml-2 font-mono">#{c.serial_number}</span>}
                            {c.notes && <div className="text-[11px] text-gray-500 mt-0.5">{c.notes}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {infoData.tech_software?.length > 0 && (
                    <div>
                      <div className="text-xs font-medium text-gray-500 mb-1">Software Subscriptions</div>
                      <table className="w-full text-xs border-collapse">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="text-left p-2 border font-medium">Name</th>
                            <th className="text-left p-2 border font-medium">Monthly</th>
                            <th className="text-left p-2 border font-medium">Annual</th>
                          </tr>
                        </thead>
                        <tbody>
                          {infoData.tech_software.map((s, i) => (
                            <tr key={i} className="hover:bg-gray-50">
                              <td className="p-2 border">{s.name || '—'}</td>
                              <td className="p-2 border">{s.monthly_cost ? `$${s.monthly_cost}` : '—'}</td>
                              <td className="p-2 border">{s.annual_cost ? `$${Number(s.annual_cost).toLocaleString()}` : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </InfoSection>
              )}

              {infoRequest?.categories_submitted?.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-4">
                  Info request sent — waiting for seller to complete sections.
                </p>
              )}
            </div>
          )}
        </div>
      )}
      {/* end Business Information panel */}

      {/* Diligence Checklist — Phase B+C */}
      {!isLimitedViewer && (
        <div>
          <DiligenceSection
            dealId={dealId}
            organizationId={deal?.organization_id || organization?.id}
            primaryColor={primaryColor}
            isViewer={isViewer}
            isAdmin={userProfile?.role === 'admin'}
            isDealAdvisor={isDealAdvisor}
            showToast={showToast}
            dealName={deal?.name}
            userProfile={userProfile}
            contacts={{
              seller:     { name: localContact || deal?.primary_contact, email: localEmail || deal?.primary_email },
              counsel:    { name: localCounselName,    email: localCounselEmail },
              accountant: { name: localAccountantName, email: localAccountantEmail },
              broker:     { name: localBrokerName,     email: localBrokerEmail },
            }}
            pendingSendScope={pendingDdSendScope}
            onConsumePendingSendScope={() => setPendingDdSendScope(null)}
          />
        </div>
      )}
      </>
      )}{/* end DILIGENCE */}

      {/* === OVERVIEW TAB (continued) — Financials === */}
      {activeTab === 'overview' && (
      <>
      {/* Financials Section - collapsible. Hidden for integration_viewer
          (no asking price / deal value / structure access). */}
      {!isLimitedViewer && !isIntegration && (
      <div
        className="mb-6 overflow-hidden rounded-2xl relative"
        style={{ backgroundColor: '#fff', border: '1px solid #E5E9F0', boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.03)' }}
      >
        {/* Accent strip — green for financials */}
        <div
          className="absolute top-0 left-0 right-0 h-[3px]"
          style={{
            background: 'linear-gradient(90deg, #10B981 0%, #34D399 30%, #6EE7B7 70%, transparent 100%)',
          }}
        />
        <button type="button"
          onClick={() => setFinancialsExpanded(!financialsExpanded)}
          className="w-full flex items-center justify-between px-6 py-5 hover:bg-[#FAFBFD] transition-colors text-left"
        >
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                boxShadow: '0 2px 6px rgba(16, 185, 129, 0.25)',
              }}
            >
              <DollarSign className="w-4 h-4 text-white" strokeWidth={2.2} />
            </div>
            <div>
              <h2 className="text-base font-semibold tracking-tight" style={{ color: '#0F172A', letterSpacing: '-0.01em' }}>
                Financials
              </h2>
              {(deal.purchase_price || deal.ebitda || deal.annual_revenue) && (
                <div className="flex items-center gap-2 text-xs mt-0.5 tabular-nums" style={{ color: '#64748B' }}>
                  {deal.purchase_price ? <span>${Number(deal.purchase_price).toLocaleString()}</span> : null}
                  {deal.purchase_price && deal.ebitda ? <span style={{ color: '#CBD5E1' }}>·</span> : null}
                  {deal.ebitda ? <span>EBITDA ${Number(deal.ebitda).toLocaleString()}</span> : null}
                  {deal.ebitda && deal.purchase_price ? <span style={{ color: '#CBD5E1' }}>·</span> : null}
                  {deal.ebitda && deal.purchase_price ? <span className="font-semibold" style={{ color: '#10B981' }}>{(deal.purchase_price / deal.ebitda).toFixed(1)}x</span> : null}
                </div>
              )}
            </div>
          </div>
          {financialsExpanded ? <ChevronDown className="w-4 h-4" style={{ color: '#94A3B8' }} /> : <ChevronRight className="w-4 h-4" style={{ color: '#94A3B8' }} />}
        </button>
        {financialsExpanded && (
          <div className="px-6 pb-6" style={{ borderTop: '1px solid #F0F2F6' }}>
            {/* Pricing row */}
            <div className="pt-5">
              <div className="text-[11px] font-semibold uppercase tracking-wider mb-3" style={{ color: '#94A3B8' }}>Pricing</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <FinancialField
                  label="Asking Price" prefix="$" placeholder="0" disabled={isViewer}
                  value={localFinancials.asking_price}
                  onFocus={() => handleFinancialFocus('asking_price')}
                  onChange={v => handleFinancialChange('asking_price', v)}
                  onBlur={() => handleFinancialBlur('asking_price', 'float')}
                />
                <FinancialField
                  label="Purchase Price" prefix="$" placeholder="0" disabled={isViewer}
                  value={localFinancials.purchase_price}
                  onFocus={() => handleFinancialFocus('purchase_price')}
                  onChange={v => handleFinancialChange('purchase_price', v)}
                  onBlur={() => handleFinancialBlur('purchase_price', 'float')}
                />
                <ComputedField
                  label="EBITDA Multiple"
                  value={(() => {
                    // Strip commas before parseFloat — without this,
                    // "1,234,567" parses as 1 and the multiple is junk.
                    const pp = parseFloat(String(localFinancials.purchase_price || '').replace(/,/g, ''));
                    const eb = parseFloat(String(localFinancials.ebitda || '').replace(/,/g, ''));
                    if (!pp || !eb) return null;
                    return `${(pp / eb).toFixed(2)}x`;
                  })()}
                  hint="Purchase Price ÷ EBITDA"
                />
              </div>
            </div>

            {/* Performance row */}
            <div className="pt-5">
              <div className="text-[11px] font-semibold uppercase tracking-wider mb-3" style={{ color: '#94A3B8' }}>Performance</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <FinancialField
                  label="EBITDA" prefix="$" placeholder="0" disabled={isViewer}
                  value={localFinancials.ebitda}
                  onFocus={() => handleFinancialFocus('ebitda')}
                  onChange={v => handleFinancialChange('ebitda', v)}
                  onBlur={() => handleFinancialBlur('ebitda', 'float')}
                />
                <FinancialField
                  label="Annual Revenue" prefix="$" placeholder="0" disabled={isViewer}
                  value={localFinancials.annual_revenue}
                  onFocus={() => handleFinancialFocus('annual_revenue')}
                  onChange={v => handleFinancialChange('annual_revenue', v)}
                  onBlur={() => handleFinancialBlur('annual_revenue', 'float')}
                />
                <FinancialField
                  label="Annual Calls" placeholder="0" disabled={isViewer} inputMode="numeric"
                  value={localFinancials.annual_calls}
                  onFocus={() => handleFinancialFocus('annual_calls')}
                  onChange={v => handleFinancialChange('annual_calls', v)}
                  onBlur={() => handleFinancialBlur('annual_calls', 'int')}
                />
              </div>
            </div>

            {/* Real Estate row — defaults to "included"; if not, hide RE Value */}
            <div className="pt-5">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#94A3B8' }}>Real Estate</div>
                <label className="inline-flex items-center gap-2 text-xs cursor-pointer" style={{ color: '#334155' }}>
                  <input type="checkbox"
                         checked={deal.real_estate_included !== false}
                         onChange={e => handleUpdateDeal('real_estate_included', e.target.checked)}
                         disabled={isViewer} className="w-4 h-4 rounded accent-[#1A3665]" />
                  <span className="font-medium">Real Estate Included</span>
                </label>
              </div>
              {deal.real_estate_included !== false && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <FinancialField
                    label="RE Value" prefix="$" placeholder="0" disabled={isViewer}
                    value={localFinancials.real_estate_value}
                    onFocus={() => handleFinancialFocus('real_estate_value')}
                    onChange={v => handleFinancialChange('real_estate_value', v)}
                    onBlur={() => handleFinancialBlur('real_estate_value', 'float')}
                  />
                </div>
              )}
            </div>

            {/* Deal Structure Notes */}
            <div className="pt-5">
              <label className="text-[11px] font-semibold uppercase tracking-wider mb-1.5 block" style={{ color: '#94A3B8' }}>
                Deal Structure Notes
              </label>
              <textarea
                value={localFinancials.deal_structure_notes || ''}
                onChange={e => handleFinancialChange('deal_structure_notes', e.target.value)}
                onBlur={handleNotesBlur}
                disabled={isViewer}
                placeholder="Seller financing terms, earnout structure, deferred payments, non-compete details…"
                rows={3}
                className="w-full text-sm rounded-lg transition-colors resize-none focus:outline-none disabled:bg-slate-50"
                style={{
                  border: '1px solid #E5E9F0',
                  padding: '0.75rem 0.875rem',
                  color: '#0F172A',
                }}
                onFocusCapture={e => { e.target.style.borderColor = '#1A3665'; e.target.style.boxShadow = '0 0 0 3px rgba(26, 54, 101, 0.1)'; }}
                onBlurCapture={e => { e.target.style.borderColor = '#E5E9F0'; e.target.style.boxShadow = 'none'; }}
              />
            </div>
          </div>
        )}
      </div>
      )}

      {/* Locations Section — moved here from the Notes & Tasks tab, since
          it wasn't obviously an "activity" and reads more naturally as
          part of the deal's overview. */}
          <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#ffffff', border: '1px solid #E5E9F0', boxShadow: '0 1px 2px rgba(15, 23, 42, 0.03)' }}>
            <button type="button"
              onClick={() => setLocationsExpanded(!locationsExpanded)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#FAFBFD] transition-colors text-left"
            >
              <div className="flex items-center gap-2">
                {locationsExpanded
                  ? <ChevronDown className="w-4 h-4" style={{ color: '#94A3B8' }} />
                  : <ChevronRight className="w-4 h-4" style={{ color: '#94A3B8' }} />}
                <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
                     style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', color: '#10B981' }}>
                  <MapPin className="w-3.5 h-3.5" strokeWidth={2.2} />
                </div>
                <span className="text-sm font-semibold" style={{ color: '#0F172A' }}>Locations</span>
                <span className="text-[11px] tabular-nums" style={{ color: '#94A3B8' }}>{dealLocations.length}</span>
                {allLocationsInLfg && dealLocations.length > 0 && (
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded flex items-center gap-0.5"
                        style={{ backgroundColor: '#DCFCE7', color: '#15803D' }}>
                    <CheckCircle className="w-3 h-3" /> LFG
                  </span>
                )}
              </div>
              {!isViewer && (
                <div className="flex items-center gap-1">
                  <button type="button"
                    onClick={(e) => { e.stopPropagation(); setShowLocationForm(true); }}
                    className="px-2 py-1 text-xs rounded flex items-center gap-1 hover:bg-gray-100"
                    style={{ color: displayPrimary }}
                  >
                    <Plus className="w-3 h-3" /> Add
                  </button>
                </div>
              )}
            </button>

            {locationsExpanded && (
              <div className="border-t">
                {/* Location Form */}
                {showLocationForm && (
                  <div className="p-3 bg-gray-50 border-b">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium text-sm" style={{ color: displayPrimary }}>
                        {editingLocation ? 'Edit Location' : 'Add Location'}
                      </h4>
                      <button type="button" onClick={resetLocationForm} className="p-1 hover:bg-gray-200 rounded">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="space-y-2">
                      <input value={locationForm.name} onChange={e => setLocationForm(p => ({ ...p, name: e.target.value }))} placeholder="Location Name *" className="w-full border rounded px-2 py-1.5 text-sm" />
                      <div className="relative" ref={suggestionsRef}>
                        <input 
                          value={locationForm.address} 
                          onChange={e => {
                            const val = e.target.value;
                            setLocationForm(p => ({ ...p, address: val }));
                            setAddressQuery(val);
                            searchAddress(val);
                          }} 
                          onFocus={() => { if (addressSuggestions.length) setShowSuggestions(true); }}
                          placeholder="Street Address (type to search)" 
                          className="w-full border rounded px-2 py-1.5 text-sm" 
                          autoComplete="off"
                        />
                        {showSuggestions && addressSuggestions.length > 0 && (
                          <div className="absolute z-50 left-0 right-0 bg-white border rounded shadow-lg mt-0.5 max-h-48 overflow-y-auto">
                            {addressSuggestions.map((s, i) => (
                              <button type="button"
                                key={i}
                                className="w-full text-left px-2 py-1.5 text-xs hover:bg-blue-50 border-b last:border-b-0 flex items-start gap-1.5"
                                onClick={() => {
                                  setLocationForm(p => ({
                                    ...p,
                                    address: s.address,
                                    city: s.city,
                                    state: stateAbbrev(s.state),
                                    zip: s.zip,
                                    latitude: s.lat.toFixed(7),
                                    longitude: s.lon.toFixed(7),
                                  }));
                                  setShowSuggestions(false);
                                  setAddressSuggestions([]);
                                }}
                              >
                                <MapPin className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0" />
                                <span className="text-gray-700 leading-tight">{s.display}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <input value={locationForm.city} onChange={e => setLocationForm(p => ({ ...p, city: e.target.value }))} placeholder="City" className="border rounded px-2 py-1.5 text-sm" />
                        <input value={locationForm.state} onChange={e => setLocationForm(p => ({ ...p, state: e.target.value.toUpperCase().slice(0, 2) }))} placeholder="ST" maxLength={2} className="border rounded px-2 py-1.5 text-sm uppercase" />
                        <input value={locationForm.zip} onChange={e => setLocationForm(p => ({ ...p, zip: e.target.value }))} placeholder="ZIP" className="border rounded px-2 py-1.5 text-sm" />
                      </div>
                      <div className="flex gap-2">
                        <input value={locationForm.latitude} onChange={e => setLocationForm(p => ({ ...p, latitude: e.target.value }))} placeholder="Lat" className="flex-1 min-w-0 border rounded px-2 py-1.5 text-xs" />
                        <input value={locationForm.longitude} onChange={e => setLocationForm(p => ({ ...p, longitude: e.target.value }))} placeholder="Lng" className="flex-1 min-w-0 border rounded px-2 py-1.5 text-xs" />
                        <button type="button" onClick={handleGeocodeLocation} disabled={geocoding} className="px-2 py-1.5 border rounded text-sm hover:bg-white disabled:opacity-50" title="Lookup coordinates">
                          {geocoding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                        </button>
                      </div>
                      <label className="flex items-center gap-2">
                        <input type="checkbox" checked={locationForm.is_crematory} onChange={e => setLocationForm(p => ({ ...p, is_crematory: e.target.checked }))} className="rounded" />
                        <Flame className="w-4 h-4 text-orange-500" />
                        <span className="text-sm">Crematory</span>
                      </label>
                    </div>
                    <div className="flex justify-end gap-2 mt-2">
                      <button type="button" onClick={resetLocationForm} className="px-2 py-1 border rounded text-xs hover:bg-gray-50">Cancel</button>
                      <button type="button" onClick={handleSaveLocation} disabled={locationSaving || !locationForm.name.trim()} className="px-2 py-1 text-white rounded text-xs flex items-center gap-1 disabled:opacity-50" style={{ backgroundColor: primaryColor }}>
                        {locationSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                        {editingLocation ? 'Update' : 'Add'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Locations List */}
                {locationsLoading ? (
                  <div className="p-4 text-center">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto" style={{ color: displayPrimary }} />
                  </div>
                ) : dealLocations.length === 0 ? (
                  <div className="p-4 text-center text-sm" style={{ color: displaySecondary }}>
                    No locations yet.
                  </div>
                ) : (
                  <div>
                    {dealLocations.map((loc, idx) => (
                      <div key={loc.id} className={`flex items-center justify-between p-3 hover:bg-gray-50 ${idx > 0 ? 'border-t' : ''}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {loc.is_crematory && <Flame className="w-3.5 h-3.5 text-orange-500" />}
                            <span className="font-medium text-sm">{loc.name}</span>
                            {locationsInLfg.has(loc.id) && (
                              <span className="text-xs px-1 py-0.5 rounded bg-green-100 text-green-700 flex items-center gap-0.5">
                                <CheckCircle className="w-3 h-3" /> LFG
                              </span>
                            )}
                            {locationDistances[loc.id] !== undefined && (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">
                                📍 {locationDistances[loc.id]} mi
                              </span>
                            )}
                          </div>
                          {(loc.address || loc.city) && (
                            <div className="text-xs mt-0.5 truncate" style={{ color: displaySecondary }}>
                              {[loc.address, loc.city, loc.state, loc.zip].filter(Boolean).join(', ')}
                            </div>
                          )}
                        </div>
                        {!isViewer && (
                          <div className="flex items-center gap-0.5 ml-1">
                            <button type="button" onClick={() => handleEditLocation(loc)} className="p-1 rounded hover:bg-gray-100" title="Edit">
                              <Edit2 className="w-3.5 h-3.5" style={{ color: displaySecondary }} />
                            </button>
                            <button type="button" onClick={() => handleDeleteLocation(loc)} className="p-1 rounded hover:bg-red-50 text-red-500" title="Delete">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Reminders / Follow-ups Section */}
          <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#ffffff', border: '1px solid #E5E9F0', boxShadow: '0 1px 2px rgba(15, 23, 42, 0.03)' }}>
            <button type="button"
              onClick={() => setRemindersExpanded(!remindersExpanded)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#FAFBFD] transition-colors text-left"
            >
              <div className="flex items-center gap-2">
                {remindersExpanded
                  ? <ChevronDown className="w-4 h-4" style={{ color: '#94A3B8' }} />
                  : <ChevronRight className="w-4 h-4" style={{ color: '#94A3B8' }} />}
                <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
                     style={{ backgroundColor: 'rgba(161, 98, 7, 0.1)', color: '#A16207' }}>
                  <Bell className="w-3.5 h-3.5" strokeWidth={2.2} />
                </div>
                <span className="text-sm font-semibold" style={{ color: '#0F172A' }}>Reminders</span>
                {reminders.filter(r => r.status === 'pending').length > 0 && (
                  <span className="text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded"
                        style={{ backgroundColor: '#FEF3C7', color: '#A16207' }}>
                    {reminders.filter(r => r.status === 'pending').length}
                  </span>
                )}
              </div>
            </button>
            {remindersExpanded && (
              <div className="px-3 pb-3 border-t pt-2">
                {/* Add Reminder Button */}
                {!isViewer && !showReminderForm && (
                  <button type="button"
                    onClick={() => setShowReminderForm(true)}
                    className="w-full flex items-center justify-center gap-1 text-xs py-1.5 border border-dashed rounded hover:bg-gray-50 mb-2"
                    style={{ color: displayPrimary, borderColor: displayPrimary }}
                  >
                    <Plus className="w-3 h-3" /> Add Reminder
                  </button>
                )}

                {/* New Reminder Form */}
                {showReminderForm && (
                  <div className="border rounded p-2 mb-2 bg-gray-50 space-y-2">
                    <input
                      value={reminderForm.title}
                      onChange={e => setReminderForm(p => ({ ...p, title: e.target.value }))}
                      placeholder="Reminder title *"
                      className="w-full border rounded px-2 py-1 text-sm"
                      autoFocus
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] text-gray-500">Due Date *</label>
                        <input type="date" value={reminderForm.due_date} onChange={e => setReminderForm(p => ({ ...p, due_date: e.target.value }))} className="w-full border rounded px-2 py-1 text-sm" />
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-500">Amount ($)</label>
                        <input type="number" step="0.01" value={reminderForm.amount} onChange={e => setReminderForm(p => ({ ...p, amount: e.target.value }))} placeholder="0.00" className="w-full border rounded px-2 py-1 text-sm" />
                      </div>
                    </div>
                    <select value={reminderForm.reminder_type} onChange={e => setReminderForm(p => ({ ...p, reminder_type: e.target.value }))} className="w-full border rounded px-2 py-1 text-sm">
                      <option value="follow_up">Follow-up</option>
                      <option value="capital_outlay">Capital Outlay</option>
                      <option value="deferred_transaction">Deferred Transaction</option>
                      <option value="earnout">Earnout Payment</option>
                      <option value="real_estate">Real Estate Purchase</option>
                      <option value="seller_note">Seller Note Payment</option>
                      <option value="other">Other</option>
                    </select>
                    <textarea
                      value={reminderForm.description}
                      onChange={e => setReminderForm(p => ({ ...p, description: e.target.value }))}
                      placeholder="Details (optional)"
                      className="w-full border rounded px-2 py-1 text-sm resize-none"
                      rows={2}
                    />
                    <div className="flex items-center gap-1.5">
                      <input type="checkbox" checked={reminderForm.include_in_digest} onChange={e => setReminderForm(p => ({ ...p, include_in_digest: e.target.checked }))} className="w-3.5 h-3.5 rounded" />
                      <label className="text-[10px] text-gray-500">Include in daily digest</label>
                    </div>
                    <div className="flex justify-end gap-1.5">
                      <button type="button" onClick={() => { setShowReminderForm(false); setReminderForm({ title: '', description: '', due_date: '', amount: '', reminder_type: 'follow_up', include_in_digest: true }); }} className="px-2 py-1 border rounded text-xs hover:bg-gray-50">Cancel</button>
                      <button type="button"
                        onClick={async () => {
                          if (!reminderForm.title.trim() || !reminderForm.due_date) return;
                          try {
                            await addReminder({
                              organization_id: organization?.id,
                              title: reminderForm.title.trim(),
                              description: reminderForm.description.trim() || null,
                              due_date: reminderForm.due_date,
                              amount: reminderForm.amount ? parseFloat(reminderForm.amount) : null,
                              reminder_type: reminderForm.reminder_type,
                              include_in_digest: reminderForm.include_in_digest,
                              created_by_name: userProfile?.full_name || userProfile?.email || 'Unknown',
                            });
                            setShowReminderForm(false);
                            setReminderForm({ title: '', description: '', due_date: '', amount: '', reminder_type: 'follow_up', include_in_digest: true });
                            showToast('Reminder added');
                          } catch (err) { showToast(err.message, 'error'); }
                        }}
                        disabled={!reminderForm.title.trim() || !reminderForm.due_date}
                        className="px-2 py-1 text-white rounded text-xs disabled:opacity-50 hover:opacity-90"
                        style={{ backgroundColor: primaryColor }}
                      >
                        Add
                      </button>
                    </div>
                  </div>
                )}

                {/* Reminders List */}
                {reminders.length === 0 && !showReminderForm ? (
                  <div className="text-center text-xs text-gray-400 py-3">No reminders set</div>
                ) : (
                  <div className="space-y-1.5">
                    {reminders.map(rem => {
                      const isOverdue = rem.status === 'pending' && new Date(rem.due_date) < new Date();
                      const isDue = rem.status === 'pending' && !isOverdue && 
                        Math.ceil((new Date(rem.due_date) - new Date()) / 86400000) <= 7;
                      const typeLabels = {
                        capital_outlay: { label: 'Capital', color: 'bg-blue-100 text-blue-700' },
                        deferred_transaction: { label: 'Deferred', color: 'bg-purple-100 text-purple-700' },
                        earnout: { label: 'Earnout', color: 'bg-indigo-100 text-indigo-700' },
                        real_estate: { label: 'RE', color: 'bg-emerald-100 text-emerald-700' },
                        seller_note: { label: 'Note', color: 'bg-teal-100 text-teal-700' },
                        follow_up: { label: 'Follow-up', color: 'bg-gray-100 text-gray-600' },
                        other: { label: 'Other', color: 'bg-gray-100 text-gray-600' },
                      };
                      const typeInfo = typeLabels[rem.reminder_type] || typeLabels.other;

                      return (
                        <div
                          key={rem.id}
                          className={`border rounded p-2 text-xs ${rem.status === 'completed' ? 'opacity-50 bg-gray-50' : isOverdue ? 'border-red-200 bg-red-50' : isDue ? 'border-amber-200 bg-amber-50' : ''}`}
                        >
                          <div className="flex items-start justify-between gap-1">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1 flex-wrap">
                                <span className={`px-1 py-0.5 rounded text-[10px] font-medium ${typeInfo.color}`}>{typeInfo.label}</span>
                                {rem.status === 'completed' && <span className="px-1 py-0.5 rounded text-[10px] bg-green-100 text-green-700">Done</span>}
                                {isOverdue && <span className="px-1 py-0.5 rounded text-[10px] bg-red-100 text-red-700">Overdue</span>}
                              </div>
                              <div className={`font-medium mt-0.5 ${rem.status === 'completed' ? 'line-through text-gray-400' : ''}`}>{rem.title}</div>
                              {rem.description && <div className="text-gray-500 mt-0.5 line-clamp-2">{rem.description}</div>}
                              <div className="flex items-center gap-2 mt-1 text-gray-400">
                                <span className="flex items-center gap-0.5">
                                  <Calendar className="w-3 h-3" />
                                  {new Date(rem.due_date + 'T00:00:00').toLocaleDateString()}
                                </span>
                                {rem.amount && (
                                  <span className="font-medium text-gray-600">${Number(rem.amount).toLocaleString()}</span>
                                )}
                              </div>
                            </div>
                            {!isViewer && rem.status === 'pending' && (
                              <div className="flex items-center gap-0.5 flex-shrink-0">
                                <button type="button" onClick={() => { completeReminder(rem.id); showToast('Marked complete'); }} className="p-1 rounded hover:bg-green-100 text-green-600" title="Complete">
                                  <Check className="w-3.5 h-3.5" />
                                </button>
                                <button type="button" onClick={async () => { if (confirm('Delete this reminder?')) { await deleteReminder(rem.id); showToast('Reminder deleted'); } }} className="p-1 rounded hover:bg-red-50 text-red-400" title="Delete">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
      </>
      )}{/* end OVERVIEW (financials) */}

      {/* === ACTIVITY TAB — Tasks two-column with sidebar === */}
      {activeTab === 'activity' && (
      <>
      {/* Two-column layout: Tasks (left) | Sidebar (right). Stacks on mobile. */}
      {!isLimitedViewer && (
      <div className="flex flex-col lg:flex-row gap-6 pb-4">
        {/* Left Column - Tasks */}
        <div className="w-full lg:w-2/3 min-w-0">
          {/* Stats Bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <StatTile label="Tasks"      value={`${stats.complete}/${stats.total}`}         accent="#1A3665" />
            <StatTile label="Documents"  value={`${stats.docsComplete}/${stats.docRequests}`} accent="#1A3665" />
            <StatTile label="Requested"  value={stats.requested}                             accent="#A16207" />
            <StatTile label="Files"      value={stats.totalFiles}                            accent="#0891B2" />
          </div>

          {/* Action Bar */}
          <div
            className="flex justify-between items-center px-4 py-2.5 mb-4 rounded-xl"
            style={{ backgroundColor: 'white', border: '1px solid #E5E9F0', boxShadow: '0 1px 2px rgba(15, 23, 42, 0.03)' }}
          >
            <div className="flex items-center gap-3">
              {!isViewer && pendingDocTasks.length > 0 && (
                <>
                  <button type="button" onClick={handleRequestDocs} disabled={selectedDocIds.size === 0}
                    className="h-8 inline-flex items-center gap-1.5 px-3 text-xs font-medium rounded-lg text-white transition-all hover:shadow-md disabled:opacity-40 disabled:shadow-none"
                    style={{
                      background: 'linear-gradient(135deg, #A16207 0%, #CA8A04 100%)',
                      boxShadow: '0 1px 2px rgba(161, 98, 7, 0.3)',
                    }}
                    title="Email document requests to the seller (legacy)"
                  >
                    <Mail className="w-3.5 h-3.5" />
                    Request Docs{selectedDocIds.size > 0 ? ` (${selectedDocIds.size})` : ''}
                  </button>
                  <span className="text-[11px]" style={{ color: '#94A3B8' }}>
                    <button type="button" onClick={selectAllDocs} className="hover:underline font-medium" style={{ color: '#475569' }}>All</button>
                    <span className="mx-1.5">·</span>
                    <button type="button" onClick={deselectAllDocs} className="hover:underline font-medium" style={{ color: '#475569' }}>None</button>
                  </span>
                </>
              )}
              {!isViewer && pendingDocTasks.length === 0 && (
                <span className="text-xs" style={{ color: '#94A3B8' }}>
                  No legacy doc requests pending — new requests flow through the Diligence Checklist.
                </span>
              )}
            </div>
            {!isViewer && (
              <button type="button" onClick={() => setShowModal('addTask')}
                className="h-8 inline-flex items-center gap-1.5 px-3 text-xs font-medium rounded-lg text-white transition-all hover:shadow-md"
                style={{
                  background: `linear-gradient(135deg, ${primaryColor} 0%, #2E4B7C 100%)`,
                  boxShadow: `0 1px 2px ${primaryColor}40`,
                }}
              >
                <Plus className="w-3.5 h-3.5" /> Add Task
              </button>
            )}
          </div>

          {/* Tasks by Phase */}
          {PHASES.map(phaseItem => {
            const phaseTasks = tasks.filter(t => t.phase === phaseItem.id);
            if (phaseTasks.length === 0) return null;
            const isExpanded = expanded[phaseItem.id] !== false;
            const phaseComplete = phaseTasks.filter(t => t.status === 'Complete' || t.status === 'N/A').length;

            const phasePct = Math.round((phaseComplete / phaseTasks.length) * 100);
            return (
              <div
                key={phaseItem.id}
                className="rounded-xl mb-3 overflow-hidden relative"
                style={{
                  backgroundColor: 'white',
                  border: '1px solid #E5E9F0',
                  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.03)',
                }}
              >
                {/* Left accent stripe — phase color */}
                <div
                  className="absolute top-0 bottom-0 left-0 w-1"
                  style={{ backgroundColor: phaseItem.hex }}
                />
                <button type="button" onClick={() => toggle(phaseItem.id)}
                  className="w-full flex items-center justify-between px-5 py-3 hover:bg-[#FAFBFD] transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? <ChevronDown className="w-4 h-4" style={{ color: '#94A3B8' }} /> : <ChevronRight className="w-4 h-4" style={{ color: '#94A3B8' }} />}
                    <span className="text-sm font-semibold" style={{ color: '#0F172A' }}>{phaseItem.name}</span>
                    <span className="text-xs tabular-nums" style={{ color: '#94A3B8' }}>
                      {phaseComplete}/{phaseTasks.length}
                    </span>
                    {/* Mini progress bar */}
                    {phaseTasks.length > 0 && (
                      <div className="w-14 h-1 rounded-full overflow-hidden" style={{ backgroundColor: '#E5E9F0' }}>
                        <div
                          className="h-full transition-all"
                          style={{
                            width: `${phasePct}%`,
                            background: phasePct === 100
                              ? 'linear-gradient(90deg, #10B981, #059669)'
                              : phaseItem.hex,
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {phaseComplete === phaseTasks.length && (
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded"
                            style={{ backgroundColor: '#DCFCE7', color: '#15803D' }}>
                        Complete
                      </span>
                    )}
                    {phaseItem.id === 'under-loi' && !isViewer && (localEmail || deal?.primary_email) && (
                      <button type="button"
                        onClick={e => { e.stopPropagation(); setSelectedInfoEmail(localEmail || deal?.primary_email || ''); setShowInfoEmailModal(true); }}
                        disabled={sendingInfoRequest}
                        className="h-7 inline-flex items-center gap-1.5 px-2.5 text-[11px] font-medium rounded-md transition-colors hover:bg-slate-50 disabled:opacity-50"
                        style={{ backgroundColor: 'white', color: '#475569', border: '1px solid #E5E9F0' }}
                        title="Send seller a link to fill out structured business information"
                      >
                        {sendingInfoRequest
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : infoRequestSent
                            ? <Check className="w-3 h-3" style={{ color: '#10B981' }} />
                            : <ClipboardList className="w-3 h-3" />}
                        {infoRequestSent ? 'Sent!' : infoRequest ? 'Resend BI' : 'Request BI'}
                      </button>
                    )}
                  </div>
                </button>
                {isExpanded && (
                  <div className="border-t">
                    {phaseTasks.map(task => (
                      <TaskRow key={task.id} task={task} team={team} isViewer={isViewer}
                        onUpdateTask={handleUpdateTask} onDeleteTask={handleDeleteTask} onFileUpload={handleFileUpload} onDeleteFile={handleDeleteFile} onDownloadFile={handleDownloadFile}
                        uploading={uploading[task.id]} fileInputRef={el => fileInputRefs.current[task.id] = el} pc={primaryColor} sc={secondaryColor}
                        isSelected={selectedDocIds.has(task.id)} onToggleSelect={toggleDocSelect} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {/* End left column */}
        {/* Right Column - Sidebar */}
        <div className="w-full lg:w-1/3 space-y-3">
          {/* Sidebar Stats */}
          <div
            className="rounded-xl p-4"
            style={{
              background: 'linear-gradient(180deg, #FFFFFF 0%, #FAFBFD 100%)',
              border: '1px solid #E5E9F0',
              boxShadow: '0 1px 2px rgba(15, 23, 42, 0.03)',
            }}
          >
            <div>
              {/* Locations moved to Overview — see its section there */}
              <div className="text-[10px] font-semibold uppercase tracking-[0.1em]" style={{ color: '#94A3B8' }}>Notes</div>
              <div className="text-xl font-semibold mt-1 tabular-nums leading-none" style={{ color: '#0F172A', letterSpacing: '-0.015em' }}>{notes.length}</div>
            </div>
            {nearestLfgDistance !== null && (
              <div className="mt-3 pt-3" style={{ borderTop: '1px solid #F0F2F6' }}>
                <div className="text-[10px] font-semibold uppercase tracking-[0.1em]" style={{ color: '#94A3B8' }}>Nearest LFG</div>
                <div className="text-lg font-semibold mt-1 tabular-nums leading-none" style={{ color: '#1A3665', letterSpacing: '-0.01em' }}>
                  {nearestLfgDistance} <span className="text-xs font-normal" style={{ color: '#64748B' }}>mi</span>
                </div>
              </div>
            )}
          </div>

          {/* Notes Section */}
          <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#ffffff', border: '1px solid #E5E9F0', boxShadow: '0 1px 2px rgba(15, 23, 42, 0.03)' }}>
            <button type="button"
              onClick={() => setNotesExpanded(!notesExpanded)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#FAFBFD] transition-colors text-left"
            >
              <div className="flex items-center gap-2">
                {notesExpanded
                  ? <ChevronDown className="w-4 h-4" style={{ color: '#94A3B8' }} />
                  : <ChevronRight className="w-4 h-4" style={{ color: '#94A3B8' }} />}
                <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
                     style={{ backgroundColor: 'rgba(26, 54, 101, 0.1)', color: '#1A3665' }}>
                  <MessageSquare className="w-3.5 h-3.5" strokeWidth={2.2} />
                </div>
                <span className="text-sm font-semibold" style={{ color: '#0F172A' }}>Notes</span>
                <span className="text-[11px] tabular-nums" style={{ color: '#94A3B8' }}>{notes.length}</span>
              </div>
            </button>
            
            {notesExpanded && (
              <div className="border-t">
                {!isViewer && (
                  <div className="p-3 bg-gray-50 border-b">
                    <textarea
                      value={newNote}
                      onChange={e => setNewNote(e.target.value)}
                      placeholder="Add a note..."
                      rows={2}
                      className="w-full border rounded px-3 py-2 text-sm resize-none"
                    />
                    <div className="flex justify-end mt-2">
                      <button type="button"
                        onClick={handleAddNote}
                        disabled={noteSaving || !newNote.trim()}
                        className="px-3 py-1.5 text-white rounded text-sm flex items-center gap-1 disabled:opacity-50 hover:opacity-90"
                        style={{ backgroundColor: primaryColor }}
                      >
                        {noteSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        Add Note
                      </button>
                    </div>
                  </div>
                )}
                {notesLoading ? (
                  <div className="p-4 text-center">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto" style={{ color: displayPrimary }} />
                  </div>
                ) : notes.length === 0 ? (
                  <div className="p-4 text-center text-sm" style={{ color: displaySecondary }}>
                    No notes yet.
                  </div>
                ) : (
                  <div className="max-h-96 overflow-y-auto">
                    {notes.map((note, idx) => (
                      <div key={note.id} className={`p-3 hover:bg-gray-50 ${idx > 0 ? 'border-t' : ''}`}>
                        {editingNoteId === note.id ? (
                          <div>
                            <textarea
                              value={editingNoteContent}
                              onChange={e => setEditingNoteContent(e.target.value)}
                              rows={3}
                              className="w-full border rounded px-3 py-2 text-sm resize-none"
                            />
                            <div className="flex justify-end gap-2 mt-2">
                              <button type="button"
                                onClick={() => { setEditingNoteId(null); setEditingNoteContent(''); }}
                                className="px-2 py-1 border rounded text-xs hover:bg-gray-50"
                              >
                                Cancel
                              </button>
                              <button type="button"
                                onClick={() => handleUpdateNote(note.id)}
                                className="px-2 py-1 text-white rounded text-xs flex items-center gap-1"
                                style={{ backgroundColor: primaryColor }}
                              >
                                <Save className="w-3 h-3" /> Save
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div className="flex items-start justify-between">
                              <p className="text-sm whitespace-pre-wrap flex-1">{note.content}</p>
                              {!isViewer && (note.user_id === userProfile?.id || userProfile?.role === 'admin') && (
                                <div className="flex items-center gap-1 ml-2">
                                  <button type="button" onClick={() => startEditingNote(note)} className="p-1 rounded hover:bg-gray-100" title="Edit">
                                    <Edit2 className="w-3 h-3" style={{ color: displaySecondary }} />
                                  </button>
                                  <button type="button" onClick={() => handleDeleteNote(note.id)} className="p-1 rounded hover:bg-red-50 text-red-500" title="Delete">
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-1 text-xs" style={{ color: displaySecondary }}>
                              <User className="w-3 h-3" />
                              <span>{note.user_name}</span>
                              <span>{'\u2022'}</span>
                              <span>{new Date(note.created_at).toLocaleString()}</span>
                              {note.updated_at !== note.created_at && <span className="italic">(edited)</span>}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Liens & Contingencies (Obligations) */}
          <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#ffffff', border: '1px solid #E5E9F0', boxShadow: '0 1px 2px rgba(15, 23, 42, 0.03)' }}>
            <button type="button"
              onClick={() => setObligationsExpanded(!obligationsExpanded)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#FAFBFD] transition-colors text-left"
            >
              <div className="flex items-center gap-2">
                {obligationsExpanded
                  ? <ChevronDown className="w-4 h-4" style={{ color: '#94A3B8' }} />
                  : <ChevronRight className="w-4 h-4" style={{ color: '#94A3B8' }} />}
                <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
                     style={{ backgroundColor: 'rgba(194, 65, 12, 0.1)', color: '#C2410C' }}>
                  <Scale className="w-3.5 h-3.5" strokeWidth={2.2} />
                </div>
                <span className="text-sm font-semibold" style={{ color: '#0F172A' }}>Liens &amp; Contingencies</span>
                {obligations.filter(o => o.status === 'open').length > 0 && (
                  <span className="text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded"
                        style={{ backgroundColor: '#FFEDD5', color: '#C2410C' }}>
                    {obligations.filter(o => o.status === 'open').length} open
                  </span>
                )}
              </div>
            </button>

            {obligationsExpanded && (
              <div className="px-3 pb-3 border-t pt-2">
                {!isViewer && !showObligationForm && (
                  <button type="button"
                    onClick={() => {
                      setEditingObligation(null);
                      setObligationForm({ type: 'lien', description: '', amount: '', holder: '', status: 'open', resolved_date: '', notes: '' });
                      setShowObligationForm(true);
                    }}
                    className="w-full flex items-center justify-center gap-1 text-xs py-1.5 border border-dashed rounded hover:bg-gray-50 mb-2"
                    style={{ color: displayPrimary, borderColor: displayPrimary }}
                  >
                    <Plus className="w-3 h-3" /> Add Lien / Contingency
                  </button>
                )}

                {showObligationForm && (
                  <div className="border rounded p-2 mb-2 bg-gray-50 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] text-gray-500">Type</label>
                        <select
                          value={obligationForm.type}
                          onChange={e => setObligationForm(p => ({ ...p, type: e.target.value }))}
                          className="w-full border rounded px-2 py-1 text-xs"
                        >
                          <option value="lien">Lien</option>
                          <option value="contingency">Contingency</option>
                          <option value="encumbrance">Encumbrance</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-500">Status</label>
                        <select
                          value={obligationForm.status}
                          onChange={e => setObligationForm(p => ({ ...p, status: e.target.value }))}
                          className="w-full border rounded px-2 py-1 text-xs"
                        >
                          <option value="open">Open</option>
                          <option value="resolved">Resolved</option>
                          <option value="waived">Waived</option>
                        </select>
                      </div>
                    </div>
                    <input
                      value={obligationForm.description}
                      onChange={e => setObligationForm(p => ({ ...p, description: e.target.value }))}
                      placeholder="Description *"
                      className="w-full border rounded px-2 py-1 text-xs"
                      autoFocus
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] text-gray-500">Holder / Party</label>
                        <input
                          value={obligationForm.holder}
                          onChange={e => setObligationForm(p => ({ ...p, holder: e.target.value }))}
                          placeholder="e.g. First National Bank"
                          className="w-full border rounded px-2 py-1 text-xs"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-500">Amount ($)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={obligationForm.amount}
                          onChange={e => setObligationForm(p => ({ ...p, amount: e.target.value }))}
                          placeholder="0.00"
                          className="w-full border rounded px-2 py-1 text-xs"
                        />
                      </div>
                    </div>
                    {obligationForm.status !== 'open' && (
                      <div>
                        <label className="text-[10px] text-gray-500">Resolved Date</label>
                        <input
                          type="date"
                          value={obligationForm.resolved_date}
                          onChange={e => setObligationForm(p => ({ ...p, resolved_date: e.target.value }))}
                          className="w-full border rounded px-2 py-1 text-xs"
                        />
                      </div>
                    )}
                    <textarea
                      value={obligationForm.notes}
                      onChange={e => setObligationForm(p => ({ ...p, notes: e.target.value }))}
                      placeholder="Additional notes (optional)"
                      className="w-full border rounded px-2 py-1 text-xs resize-none"
                      rows={2}
                    />
                    <div className="flex justify-end gap-1.5">
                      <button
                        type="button"
                        onClick={() => { setShowObligationForm(false); setEditingObligation(null); }}
                        className="px-2 py-1 border rounded text-xs hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        disabled={!obligationForm.description.trim()}
                        onClick={async () => {
                          const payload = {
                            type: obligationForm.type,
                            description: obligationForm.description.trim(),
                            holder: obligationForm.holder.trim() || null,
                            amount: obligationForm.amount ? parseFloat(obligationForm.amount) : null,
                            status: obligationForm.status,
                            resolved_date: obligationForm.resolved_date || null,
                            notes: obligationForm.notes.trim() || null,
                          };
                          try {
                            if (editingObligation) {
                              await updateObligation(editingObligation.id, payload);
                              showToast('Updated');
                            } else {
                              await addObligation(payload);
                              showToast('Added');
                            }
                            setShowObligationForm(false);
                            setEditingObligation(null);
                          } catch (err) { showToast(err.message, 'error'); }
                        }}
                        className="px-2 py-1 text-white rounded text-xs disabled:opacity-50 hover:opacity-90 flex items-center gap-1"
                        style={{ backgroundColor: primaryColor }}
                      >
                        <Check className="w-3 h-3" />
                        {editingObligation ? 'Update' : 'Add'}
                      </button>
                    </div>
                  </div>
                )}

                {obligations.length === 0 && !showObligationForm ? (
                  <div className="text-center text-xs text-gray-400 py-3">No liens or contingencies recorded</div>
                ) : (
                  <div className="space-y-1.5">
                    {obligations.map(ob => {
                      const isOpen = ob.status === 'open';
                      const typeColors = {
                        lien:         'bg-red-100 text-red-700',
                        contingency:  'bg-amber-100 text-amber-700',
                        encumbrance:  'bg-orange-100 text-orange-700',
                        other:        'bg-gray-100 text-gray-600',
                      };
                      const statusColors = {
                        open:     'border-red-200',
                        resolved: 'bg-green-50 border-green-200 opacity-60',
                        waived:   'bg-gray-50 border-gray-200 opacity-60',
                      };
                      return (
                        <div key={ob.id} className={"border rounded p-2 text-xs " + (statusColors[ob.status] || '')}>
                          <div className="flex items-start justify-between gap-1">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1 flex-wrap">
                                <span className={"px-1 py-0.5 rounded text-[10px] font-medium " + (typeColors[ob.type] || typeColors.other)}>
                                  {ob.type.charAt(0).toUpperCase() + ob.type.slice(1)}
                                </span>
                                {ob.status !== 'open' && (
                                  <span className="px-1 py-0.5 rounded text-[10px] bg-green-100 text-green-700 capitalize">{ob.status}</span>
                                )}
                              </div>
                              <div className={"font-medium mt-0.5 " + (ob.status !== 'open' ? 'line-through text-gray-400' : '')}>
                                {ob.description}
                              </div>
                              {ob.holder && <div className="text-gray-500 mt-0.5">{ob.holder}</div>}
                              {ob.notes && <div className="text-gray-400 mt-0.5 italic">{ob.notes}</div>}
                              <div className="flex items-center gap-2 mt-1 text-gray-400">
                                {ob.amount && (
                                  <span className="font-medium text-gray-600">${Number(ob.amount).toLocaleString()}</span>
                                )}
                                {ob.resolved_date && (
                                  <span>Resolved {new Date(ob.resolved_date + 'T00:00:00').toLocaleDateString()}</span>
                                )}
                              </div>
                            </div>
                            {!isViewer && (
                              <div className="flex items-center gap-0.5 flex-shrink-0">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingObligation(ob);
                                    setObligationForm({
                                      type: ob.type,
                                      description: ob.description,
                                      amount: ob.amount || '',
                                      holder: ob.holder || '',
                                      status: ob.status,
                                      resolved_date: ob.resolved_date || '',
                                      notes: ob.notes || '',
                                    });
                                    setShowObligationForm(true);
                                  }}
                                  className="p-1 rounded hover:bg-gray-100"
                                  title="Edit"
                                >
                                  <Edit2 className="w-3.5 h-3.5" style={{ color: displaySecondary }} />
                                </button>
                                {isOpen && (
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      await updateObligation(ob.id, { status: 'resolved', resolved_date: new Date().toISOString().slice(0, 10) });
                                      showToast('Marked resolved');
                                    }}
                                    className="p-1 rounded hover:bg-green-100 text-green-600"
                                    title="Mark resolved"
                                  >
                                    <Check className="w-3.5 h-3.5" />
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={async () => { if (confirm('Delete this item?')) { await deleteObligation(ob.id); showToast('Deleted'); } }}
                                  className="p-1 rounded hover:bg-red-50 text-red-400"
                                  title="Delete"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Scheduled Visits */}
          <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#ffffff', border: '1px solid #E5E9F0', boxShadow: '0 1px 2px rgba(15, 23, 42, 0.03)' }}>
            <div className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
                     style={{ backgroundColor: 'rgba(14, 165, 233, 0.1)', color: '#0EA5E9' }}>
                  <Calendar className="w-3.5 h-3.5" strokeWidth={2.2} />
                </div>
                <span className="text-sm font-semibold" style={{ color: '#0F172A' }}>Visits</span>
                {visits.filter(function(v) { return v.status === 'scheduled'; }).length > 0 && (
                  <span className="text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded"
                        style={{ backgroundColor: '#E0F2FE', color: '#0369A1' }}>
                    {visits.filter(function(v) { return v.status === 'scheduled'; }).length}
                  </span>
                )}
              </div>
              {!isViewer && (
                <button type="button" onClick={function() { setShowScheduleVisit(true); }}
                  className="h-7 inline-flex items-center gap-1 px-2.5 text-[11px] font-medium rounded-md transition-colors hover:bg-slate-50"
                  style={{ backgroundColor: 'white', color: '#475569', border: '1px solid #E5E9F0' }}
                >
                  <Plus className="w-3 h-3" /> Schedule
                </button>
              )}
            </div>
            {visits.length > 0 && (
              <div className="border-t divide-y">
                {visits.filter(function(v) { return v.status === 'scheduled'; }).map(function(visit) {
                  return (
                    <div key={visit.id} className="px-3 py-2">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium">{new Date(visit.visit_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</div>
                          <div className="text-xs" style={{ color: displaySecondary }}>
                            {visit.start_time ? visit.start_time.slice(0, 5) : ''} - {visit.end_time ? visit.end_time.slice(0, 5) : ''}
                            {visit.location ? ' \u2022 ' + visit.location : ''}
                          </div>
                          {visit.notes && <div className="text-xs text-gray-500 mt-0.5 truncate">{visit.notes}</div>}
                        </div>
                        {!isViewer && (
                          <button type="button" onClick={function() { handleCancelVisit(visit); }} className="p-1 rounded hover:bg-red-50 text-red-400 ml-2 flex-shrink-0" title="Cancel visit">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
                {visits.filter(function(v) { return v.status === 'canceled'; }).length > 0 && (
                  <div className="px-3 py-2 bg-gray-50">
                    <div className="text-xs text-gray-400">{visits.filter(function(v) { return v.status === 'canceled'; }).length} canceled</div>
                  </div>
                )}
              </div>
            )}
          </div>

        </div>
        {/* End right sidebar */}
      </div>
      )}
      {/* End two-column layout */}
      </>
      )}{/* end ACTIVITY */}
      </div>{/* end tab content container */}

      {showModal === 'request' && (
        <Modal onClose={() => !sendingRequest && !requestSent && setShowModal(null)} title={requestSent ? '' : 'Request Documents'} maxWidth="max-w-lg" pc={primaryColor}>
          {requestSent ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: `${LFG_COLORS.green}15` }}>
                <CheckCircle className="w-10 h-10" style={{ color: displayGreen }} />
              </div>
              <p className="text-lg font-semibold" style={{ color: displayNavy }}>Document request sent!</p>
              <p className="text-sm mt-1" style={{ color: displaySecondary }}>{selectedDocIds.size} document(s) requested from {selectedDocEmail}</p>
            </div>
          ) : (
            <>
              {hasDdItems && (
                <div className="mb-3 bg-blue-50 border border-blue-200 rounded px-3 py-2 flex gap-2 items-start">
                  <ClipboardList className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: displayPrimary }} />
                  <div className="text-xs text-gray-700">
                    This deal has a <strong>Diligence Checklist</strong>. For new requests, prefer
                    the <strong>Send DD requests</strong> buttons in that section below — they
                    track status per item and give the recipient a checklist view in the portal.
                    The flow below is kept for legacy task-based requests and action items.
                  </div>
                </div>
              )}
              <div className="mb-3">
                <div className="text-sm font-medium mb-1.5" style={{ color: displaySecondary }}>Send to:</div>
                <div className="space-y-1.5">
                  {(localEmail || deal?.primary_email) && (
                    <label className="flex items-center gap-2 cursor-pointer p-2 rounded border hover:bg-gray-50 transition-colors">
                      <input
                        type="radio"
                        name="docEmail"
                        value={localEmail || deal?.primary_email}
                        checked={selectedDocEmail === (localEmail || deal?.primary_email)}
                        onChange={e => setSelectedDocEmail(e.target.value)}
                        className="accent-blue-600"
                      />
                      <div>
                        <div className="text-xs text-gray-400">Business Email</div>
                        <div className="text-sm font-medium">{localEmail || deal?.primary_email}</div>
                      </div>
                    </label>
                  )}
                  {(localPersonalEmail || deal?.personal_email) && (
                    <label className="flex items-center gap-2 cursor-pointer p-2 rounded border hover:bg-gray-50 transition-colors">
                      <input type="radio" name="docEmail" value={localPersonalEmail || deal?.personal_email} checked={selectedDocEmail === (localPersonalEmail || deal?.personal_email)} onChange={e => setSelectedDocEmail(e.target.value)} className="accent-blue-600" />
                      <div>
                        <div className="text-xs text-gray-400">Personal Email</div>
                        <div className="text-sm font-medium">{localPersonalEmail || deal?.personal_email}</div>
                      </div>
                    </label>
                  )}
                  {localCounselEmail && (
                    <label className="flex items-center gap-2 cursor-pointer p-2 rounded border hover:bg-gray-50 transition-colors">
                      <input type="radio" name="docEmail" value={localCounselEmail} checked={selectedDocEmail === localCounselEmail} onChange={e => setSelectedDocEmail(e.target.value)} className="accent-blue-600" />
                      <div>
                        <div className="text-xs text-gray-400">Seller's Counsel</div>
                        <div className="text-sm font-medium">{localCounselName || localCounselEmail}</div>
                        <div className="text-xs text-gray-400">{localCounselEmail}</div>
                      </div>
                    </label>
                  )}
                  {localAccountantEmail && (
                    <label className="flex items-center gap-2 cursor-pointer p-2 rounded border hover:bg-gray-50 transition-colors">
                      <input type="radio" name="docEmail" value={localAccountantEmail} checked={selectedDocEmail === localAccountantEmail} onChange={e => setSelectedDocEmail(e.target.value)} className="accent-blue-600" />
                      <div>
                        <div className="text-xs text-gray-400">Accountant / CPA</div>
                        <div className="text-sm font-medium">{localAccountantName || localAccountantEmail}</div>
                        <div className="text-xs text-gray-400">{localAccountantEmail}</div>
                      </div>
                    </label>
                  )}
                  {localBrokerEmail && (
                    <label className="flex items-center gap-2 cursor-pointer p-2 rounded border hover:bg-gray-50 transition-colors">
                      <input type="radio" name="docEmail" value={localBrokerEmail} checked={selectedDocEmail === localBrokerEmail} onChange={e => setSelectedDocEmail(e.target.value)} className="accent-blue-600" />
                      <div>
                        <div className="text-xs text-gray-400">Broker / Representative</div>
                        <div className="text-sm font-medium">{localBrokerName || localBrokerEmail}</div>
                        <div className="text-xs text-gray-400">{localBrokerEmail}</div>
                      </div>
                    </label>
                  )}
                  <label className="flex items-center gap-2 cursor-pointer p-2 rounded border hover:bg-gray-50 transition-colors">
                    <input type="radio" name="docEmail" checked={!!selectedDocEmail && ![localEmail || deal?.primary_email, localPersonalEmail || deal?.personal_email, localCounselEmail, localAccountantEmail, localBrokerEmail].filter(Boolean).includes(selectedDocEmail)} onChange={() => setSelectedDocEmail('')} className="accent-blue-600" />
                    <div className="flex-1">
                      <div className="text-xs text-gray-400">Other email</div>
                      <input
                        type="email"
                        placeholder="Enter email address..."
                        value={[localEmail || deal?.primary_email, localPersonalEmail || deal?.personal_email, localCounselEmail, localAccountantEmail, localBrokerEmail].filter(Boolean).includes(selectedDocEmail) ? '' : selectedDocEmail}
                        onChange={e => setSelectedDocEmail(e.target.value)}
                        onClick={e => e.stopPropagation()}
                        className="w-full border rounded px-2 py-1 text-xs mt-0.5 focus:outline-none focus:ring-1 focus:ring-blue-300"
                      />
                    </div>
                  </label>
                </div>
              </div>
              <textarea value={requestMsg} onChange={e => setRequestMsg(e.target.value)} className="w-full h-48 border rounded px-3 py-2 text-sm" disabled={sendingRequest} />
              <p className="text-xs mt-2" style={{ color: displaySecondary }}>This will mark {selectedDocIds.size} document(s) as &ldquo;Requested&rdquo; and send the email via Resend.</p>
              <div className="flex justify-end gap-2 mt-4">
                <button type="button" onClick={() => setShowModal(null)} disabled={sendingRequest} className="px-4 py-2 border rounded hover:bg-gray-50 transition-colors disabled:opacity-50">Cancel</button>
                <button type="button" onClick={handleSendRequest} disabled={sendingRequest || !selectedDocEmail} className="px-4 py-2 text-white rounded disabled:opacity-50 hover:opacity-90 transition-opacity flex items-center gap-2" style={{ backgroundColor: primaryColor }}>
                  {sendingRequest ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : 'Send Request'}
                </button>
              </div>
            </>
          )}
        </Modal>
      )}

      {/* Info Request Email Selection Modal */}
      {showInfoEmailModal && (
        <Modal onClose={() => setShowInfoEmailModal(false)} title={infoRequest ? 'Resend Info Request' : 'Send Info Request'} maxWidth="max-w-sm" pc={primaryColor}>
          <div className="mb-4">
            <div className="text-sm font-medium mb-2" style={{ color: displaySecondary }}>Send to:</div>
            <div className="space-y-1.5">
              {(localEmail || deal?.primary_email) && (
                <label className="flex items-center gap-2 cursor-pointer p-2 rounded border hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="infoEmail"
                    value={localEmail || deal?.primary_email}
                    checked={selectedInfoEmail === (localEmail || deal?.primary_email)}
                    onChange={e => setSelectedInfoEmail(e.target.value)}
                    className="accent-blue-600"
                  />
                  <div>
                    <div className="text-xs text-gray-400">Business Email</div>
                    <div className="text-sm font-medium">{localEmail || deal?.primary_email}</div>
                  </div>
                </label>
              )}
              {(localPersonalEmail || deal?.personal_email) && (
                <label className="flex items-center gap-2 cursor-pointer p-2 rounded border hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="infoEmail"
                    value={localPersonalEmail || deal?.personal_email}
                    checked={selectedInfoEmail === (localPersonalEmail || deal?.personal_email)}
                    onChange={e => setSelectedInfoEmail(e.target.value)}
                    className="accent-blue-600"
                  />
                  <div>
                    <div className="text-xs text-gray-400">Personal Email</div>
                    <div className="text-sm font-medium">{localPersonalEmail || deal?.personal_email}</div>
                  </div>
                </label>
              )}
            </div>
          </div>
          <p className="text-xs mb-4" style={{ color: displaySecondary }}>
            The seller will receive a link to fill out 7 sections of business information directly in their portal.
          </p>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setShowInfoEmailModal(false)} className="px-4 py-2 border rounded hover:bg-gray-50 transition-colors">Cancel</button>
            <button type="button"
              onClick={() => handleSendInfoRequest(selectedInfoEmail)}
              disabled={!selectedInfoEmail || sendingInfoRequest}
              className="px-4 py-2 text-white rounded disabled:opacity-50 hover:opacity-90 transition-opacity flex items-center gap-2"
              style={{ backgroundColor: primaryColor }}
            >
              {sendingInfoRequest ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : 'Send'}
            </button>
          </div>
        </Modal>
      )}

      {/* Phase U.2 — HR Survey send modal */}
      {showHrSurveyModal && (
        <Modal onClose={() => setShowHrSurveyModal(false)} title="Send HR Survey" maxWidth="max-w-sm" pc={primaryColor}>
          <p className="text-xs mb-3" style={{ color: displaySecondary }}>
            Sends the seller a private link that opens just the HR survey — workforce, pay, benefits — separate from any other business-info request. Link is good for 30 days.
          </p>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: displaySecondary }}>Recipient email</label>
              <input
                type="email"
                value={hrSurveyEmail}
                onChange={e => setHrSurveyEmail(e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm"
                placeholder="owner@business.com"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: displaySecondary }}>Recipient name (optional)</label>
              <input
                type="text"
                value={hrSurveyName}
                onChange={e => setHrSurveyName(e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm"
                placeholder="Owner first + last name"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button type="button" onClick={() => setShowHrSurveyModal(false)} className="px-4 py-2 border rounded hover:bg-gray-50 transition-colors">Cancel</button>
            <button type="button"
              onClick={handleSendHrSurvey}
              disabled={!hrSurveyEmail || !hrSurveyEmail.includes('@') || sendingHrSurvey}
              className="px-4 py-2 text-white rounded disabled:opacity-50 hover:opacity-90 transition-opacity flex items-center gap-2"
              style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)' }}
            >
              {sendingHrSurvey ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : 'Send HR Survey'}
            </button>
          </div>
        </Modal>
      )}

      {/* Add Task Modal */}
      {showModal === 'addTask' && (
        <Modal onClose={() => setShowModal(null)} title="Add Task" pc={primaryColor}>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">Phase *</label>
              <select value={newTask.phase} onChange={e => setNewTask(p => ({ ...p, phase: e.target.value }))} className="w-full border rounded px-3 py-2">
                <option value="">Select Phase</option>
                {PHASES.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Task Name *</label>
              <input value={newTask.name} onChange={e => setNewTask(p => ({ ...p, name: e.target.value }))} placeholder="e.g., Review financials" className="w-full border rounded px-3 py-2" />
            </div>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={newTask.is_doc_request} onChange={e => setNewTask(p => ({ ...p, is_doc_request: e.target.checked }))} className="rounded" />
              <span className="text-sm">This is a document request (can be requested from seller)</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Days Offset</label>
                <input type="number" value={newTask.relative_days} onChange={e => setNewTask(p => ({ ...p, relative_days: +e.target.value }))} className="w-full border rounded px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">From Date</label>
                <select value={newTask.base_date} onChange={e => setNewTask(p => ({ ...p, base_date: e.target.value }))} className="w-full border rounded px-3 py-2">
                  <option value="created">Deal Created</option>
                  <option value="loi">LOI Date</option>
                  <option value="close">Close Date</option>
                </select>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button type="button" onClick={() => setShowModal(null)} className="px-4 py-2 border rounded hover:bg-gray-50 transition-colors">Cancel</button>
            <button type="button" onClick={handleAddTask} disabled={!newTask.name.trim() || !newTask.phase} className="px-4 py-2 text-white rounded disabled:opacity-50 hover:opacity-90 transition-opacity" style={{ backgroundColor: primaryColor }}>Add Task</button>
          </div>
        </Modal>
      )}

      {/* Schedule Visit Modal */}
      {showScheduleVisit && (
        <Modal onClose={() => setShowScheduleVisit(false)} title="Schedule Site Visit" pc={primaryColor}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input type="date" value={visitForm.date} onChange={e => setVisitForm(f => ({ ...f, date: e.target.value }))} min={new Date().toISOString().split('T')[0]} className="w-full border rounded px-3 py-2" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                <input type="time" value={visitForm.startTime} onChange={e => setVisitForm(f => ({ ...f, startTime: e.target.value }))} className="w-full border rounded px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                <input type="time" value={visitForm.endTime} onChange={e => setVisitForm(f => ({ ...f, endTime: e.target.value }))} className="w-full border rounded px-3 py-2" />
              </div>
            </div>
            {dealLocations && dealLocations.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <select value={visitForm.locationIdx || '0'} onChange={e => setVisitForm(f => ({ ...f, locationIdx: e.target.value }))} className="w-full border rounded px-3 py-2">
                  {dealLocations.map(function(loc, idx) {
                    return <option key={loc.id} value={String(idx)}>{loc.name}{loc.city ? ', ' + loc.city : ''}{loc.state ? ', ' + loc.state : ''}</option>;
                  })}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
              <textarea value={visitForm.notes} onChange={e => setVisitForm(f => ({ ...f, notes: e.target.value }))} placeholder="Any notes for this visit..." className="w-full border rounded px-3 py-2 h-20 resize-none" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowScheduleVisit(false)} className="px-4 py-2 border rounded hover:bg-gray-50">Cancel</button>
              <button type="button" onClick={handleScheduleVisit} disabled={creatingEvent || !visitForm.date} className="px-4 py-2 text-white rounded hover:opacity-90 disabled:opacity-50 flex items-center gap-2" style={{ backgroundColor: primaryColor }}>
                {creatingEvent && <Loader2 className="w-4 h-4 animate-spin" />}
                {creatingEvent ? 'Scheduling...' : 'Schedule in Outlook'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── File Viewer Modal ── */}
      {viewingFile && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setViewingFile(null)}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50 rounded-t-lg">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <span className="text-sm font-medium truncate">{viewingFile.name}</span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => forceDownloadFile(viewingFile.url, viewingFile.name)} className="px-2 py-1 text-xs border rounded hover:bg-gray-100 flex items-center gap-1 text-gray-600">
                  <Download className="w-3 h-3" /> Download
                </button>
                <a href={viewingFile.url} target="_blank" rel="noopener noreferrer" className="px-2 py-1 text-xs border rounded hover:bg-gray-100 flex items-center gap-1 text-gray-600">
                  <ExternalLink className="w-3 h-3" /> New Tab
                </a>
                <button onClick={() => setViewingFile(null)} className="p-1 hover:bg-gray-200 rounded">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden bg-gray-100">
              {['pdf'].includes(viewingFile.type) ? (
                <iframe src={viewingFile.url} className="w-full h-full border-0" title={viewingFile.name} />
              ) : ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(viewingFile.type) ? (
                <div className="w-full h-full flex items-center justify-center p-4 overflow-auto">
                  <img src={viewingFile.url} alt={viewingFile.name} className="max-w-full max-h-full object-contain" />
                </div>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-gray-500">
                  <FileText className="w-16 h-16 text-gray-300 mb-4" />
                  <p className="text-lg font-medium mb-1">{viewingFile.name}</p>
                  <p className="text-sm mb-4">This file type can't be previewed in the browser.</p>
                  <div className="flex gap-2">
                    <button onClick={() => forceDownloadFile(viewingFile.url, viewingFile.name)} className="px-4 py-2 text-white rounded text-sm hover:opacity-90" style={{ backgroundColor: primaryColor }}>
                      <Download className="w-4 h-4 inline mr-1" /> Download
                    </button>
                    <a href={viewingFile.url} target="_blank" rel="noopener noreferrer" className="px-4 py-2 border rounded text-sm hover:bg-gray-50">
                      <ExternalLink className="w-4 h-4 inline mr-1" /> Open in New Tab
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Manage Access Modal ── */}
      {showManageAccess && (
        <Modal onClose={() => setShowManageAccess(false)} title="Manage Deal Access" maxWidth="max-w-lg" pc={primaryColor}>
          <p className="text-sm mb-4" style={{ color: '#64748B' }}>
            Grant access to this deal. Existing org members already have access; this is for outside parties (advisors, counsel, accountants, brokers) and any teammate with the "Deal Advisor" role.
          </p>

          {/* Currently with access */}
          <div className="mb-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: '#94A3B8' }}>
              Currently with access ({accessList.length})
            </div>
            {accessList.length > 0 ? (
              <div className="space-y-1.5">
                {accessList.map(a => (
                  <div key={a.id} className="flex items-center justify-between p-2 rounded-lg gap-2" style={{ backgroundColor: '#FAFBFD', border: '1px solid #E5E9F0' }}>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate" style={{ color: '#0F172A' }}>{a.user?.full_name || a.user?.email}</div>
                      <div className="text-xs truncate" style={{ color: '#64748B' }}>{a.user?.email}</div>
                    </div>
                    <button
                      type="button"
                      disabled={resendingAdvisorId === a.user?.id}
                      onClick={async () => {
                        setResendingAdvisorId(a.user?.id);
                        try {
                          const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-deal-advisor-request`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
                            },
                            body: JSON.stringify({
                              deal_id: dealId,
                              advisor_user_id: a.user?.id,
                              subject: `Your access link for ${deal?.name || 'this deal'}`,
                              message: "Here's a fresh link to open the deal — your previous one may have expired.",
                              create_task: false,
                            }),
                          });
                          const body = await res.json().catch(() => ({}));
                          if (!res.ok) throw new Error(body?.error || 'Failed to resend link');
                          showToast('Link resent — they\'ll get a fresh email');
                        } catch (err) {
                          showToast(err.message, 'error');
                        } finally {
                          setResendingAdvisorId(null);
                        }
                      }}
                      className="h-7 px-2.5 text-[11px] font-semibold rounded-md flex items-center gap-1 flex-shrink-0 hover:opacity-90 disabled:opacity-50"
                      style={{ color: '#1A3665', border: '1px solid #1A3665' }}
                      title="Email them a fresh one-click access link"
                    >
                      {resendingAdvisorId === a.user?.id ? 'Sending…' : 'Resend link'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setRequestAdvisor(a.user)}
                      className="h-7 px-2.5 text-[11px] font-semibold rounded-md flex items-center gap-1 flex-shrink-0 hover:opacity-90"
                      style={{ backgroundColor: '#1A3665', color: 'white' }}
                      title="Send this advisor a specific request"
                    >
                      Send request
                    </button>
                    <button
                      onClick={async () => {
                        if (!confirm(`Remove ${a.user?.full_name || a.user?.email} from this deal?`)) return;
                        try { await revokeAccess(a.id); } catch (err) { showToast(err.message, 'error'); }
                      }}
                      className="p-1 hover:bg-red-50 rounded text-red-500 flex-shrink-0"
                      title="Remove access"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-center py-2 rounded-lg" style={{ color: '#94A3B8', backgroundColor: '#FAFBFD', border: '1px dashed #E5E9F0' }}>
                Nobody scoped to this deal yet
              </div>
            )}
          </div>

          {/* Existing contacts on this deal — one click grants */}
          {(() => {
            const dealContacts = [
              // The seller/primary contact gets the full seller-portal
              // link (Business Info + uploads), not a deal_advisor grant
              // — that role is restricted to Diligence-only and would be
              // wrong for the actual seller. isSeller routes this row's
              // button to send-seller-welcome instead of grantAccessByEmail.
              { label: 'Primary contact', name: deal?.primary_contact, email: deal?.primary_email, accent: '#1A3665', isSeller: true },
              { label: 'Counsel', name: deal?.counsel_name, email: deal?.counsel_email, firm: deal?.counsel_firm, accent: '#7C3AED' },
              { label: 'Accountant', name: deal?.accountant_name, email: deal?.accountant_email, firm: deal?.accountant_firm, accent: '#0891B2' },
              { label: 'Broker', name: deal?.broker_name, email: deal?.broker_email, firm: deal?.broker_firm, accent: '#A16207' },
            ].filter(c => c.email && c.email.includes('@'));
            if (dealContacts.length === 0) return null;
            return (
              <div className="mb-4">
                <div className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: '#94A3B8' }}>
                  Contacts on this deal
                </div>
                <p className="text-xs mb-2" style={{ color: '#94A3B8' }}>
                  The primary contact gets emailed their full document-portal link. Counsel/accountant/broker get app access scoped to Diligence only.
                </p>
                <div className="space-y-1.5">
                  {dealContacts.map(c => {
                    const lowerEmail = c.email.toLowerCase();
                    const alreadyHasAccess = !c.isSeller && accessList.some(a => a.user?.email?.toLowerCase() === lowerEmail);
                    return (
                      <div key={c.label + c.email} className="flex items-center justify-between p-2 rounded-lg" style={{ backgroundColor: 'white', border: '1px solid #E5E9F0' }}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ backgroundColor: c.accent + '18', color: c.accent }}>
                              {c.label}
                            </span>
                            <span className="text-sm font-medium truncate" style={{ color: '#0F172A' }}>{c.name || c.email}</span>
                          </div>
                          <div className="text-xs mt-0.5 truncate" style={{ color: '#64748B' }}>
                            {c.email}{c.firm ? ` · ${c.firm}` : ''}
                          </div>
                        </div>
                        <button
                          disabled={c.isSeller ? sendingSellerWelcome : alreadyHasAccess}
                          onClick={async () => {
                            if (c.isSeller) {
                              setSendingSellerWelcome(true);
                              try {
                                const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-seller-welcome`, {
                                  method: 'POST',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
                                  },
                                  body: JSON.stringify({ deal_id: dealId, email: c.email, name: c.name || '' }),
                                });
                                const resBody = await res.json().catch(() => ({}));
                                if (!res.ok) throw new Error(resBody?.error || 'Failed to send portal link');
                                showToast(`Sent — ${c.name || c.email} will get the document portal link by email`);
                              } catch (err) {
                                showToast(err.message, 'error');
                              } finally {
                                setSendingSellerWelcome(false);
                              }
                              return;
                            }
                            try {
                              const r = await grantAccessByEmail(c.email, c.name || '', deal?.organization_id);
                              showToast(r.message, r.kind === 'noop' ? 'warning' : 'success');
                            } catch (err) {
                              showToast(err.message, 'error');
                            }
                          }}
                          className="ml-2 h-7 px-2.5 text-xs font-semibold rounded-md flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                          style={{ backgroundColor: (!c.isSeller && alreadyHasAccess) ? '#F1F5F9' : primaryColor, color: (!c.isSeller && alreadyHasAccess) ? '#64748B' : 'white' }}
                          title={c.isSeller ? 'Email them their full-access document portal link (no login needed)' : (alreadyHasAccess ? 'Already has access' : 'Grant deal access (sends invite if not yet a user)')}
                        >
                          {c.isSeller ? (sendingSellerWelcome ? 'Sending…' : 'Send Portal Link') : (alreadyHasAccess ? '✓ Granted' : 'Grant')}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Existing team deal_advisors not yet on this deal */}
          {(() => {
            const advisors = team.filter(m => m.role === 'deal_advisor' && !accessList.some(a => a.user_id === m.id));
            if (advisors.length === 0) return null;
            return (
              <div className="mb-4">
                <div className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: '#94A3B8' }}>
                  Existing team deal advisors
                </div>
                <div className="space-y-1.5">
                  {advisors.map(m => (
                    <div key={m.id} className="flex items-center justify-between p-2 rounded-lg" style={{ backgroundColor: 'white', border: '1px solid #E5E9F0' }}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate" style={{ color: '#0F172A' }}>{m.full_name || m.email}</div>
                        <div className="text-xs truncate" style={{ color: '#64748B' }}>{m.email}</div>
                      </div>
                      <button
                        onClick={async () => {
                          try { await grantAccess(m.id); showToast('Access granted'); }
                          catch (err) { showToast(err.message, 'error'); }
                        }}
                        className="ml-2 h-7 px-2.5 text-xs font-semibold rounded-md flex-shrink-0"
                        style={{ backgroundColor: primaryColor, color: 'white' }}
                      >
                        Grant
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Or by email — paste one or many at once */}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: '#94A3B8' }}>
              Or grant by email — paste one or several (comma, space, or newline separated)
            </div>
            <textarea
              id="grant-email-bulk-input"
              placeholder={'advisor1@example.com\nadvisor2@example.com\nadvisor3@example.com'}
              rows={3}
              disabled={grantingBulkAccess}
              className="w-full border rounded-lg px-3 py-1.5 text-sm resize-none disabled:opacity-50"
              style={{ borderColor: '#E5E9F0' }}
            />
            <div className="flex items-center justify-between mt-1.5 gap-2">
              <div className="text-[11px]" style={{ color: '#94A3B8' }}>
                Anyone already a user gets access immediately. Everyone else gets an invite email and is auto-added on signup.
              </div>
              <button
                onClick={handleBulkGrantAccess}
                disabled={grantingBulkAccess}
                className="flex-shrink-0 px-3 py-1.5 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5 hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: primaryColor, color: 'white' }}
              >
                {grantingBulkAccess
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Granting {bulkGrantProgress?.done ?? 0}/{bulkGrantProgress?.total ?? 0}…</>
                  : <><UserPlus className="w-4 h-4" /> Grant Access</>}
              </button>
            </div>
            {bulkGrantResults && (
              <div className="mt-2 space-y-0.5 max-h-32 overflow-y-auto">
                {bulkGrantResults.map(r => (
                  <div key={r.email} className="text-xs flex items-start gap-1.5" style={{ color: r.ok ? '#15803D' : '#B91C1C' }}>
                    {r.ok ? <Check className="w-3 h-3 flex-shrink-0 mt-0.5" /> : <X className="w-3 h-3 flex-shrink-0 mt-0.5" />}
                    <span><span className="font-medium">{r.email}</span> — {r.message}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* ── Phase Q.8: Send Request to Deal Advisor ── */}
      {requestAdvisor && (
        <SendAdvisorRequestModal
          advisor={requestAdvisor}
          dealId={dealId}
          dealName={deal?.name}
          primaryColor={primaryColor}
          onClose={() => setRequestAdvisor(null)}
          onSent={() => {
            setRequestAdvisor(null);
            showToast('Request sent — advisor notified by email');
          }}
          showToast={showToast}
        />
      )}

      {/* ── Business Info Edit Modal ── */}
      {editingInfo && (
        <BusinessInfoEditModal
          dealId={dealId}
          organizationId={deal?.organization_id}
          infoData={infoData}
          primaryColor={primaryColor}
          isIntegration={isIntegration}
          onClose={() => setEditingInfo(false)}
          onSaved={async () => { await loadInfoRequest(); }}
        />
      )}
    </div>
  );
}

function TaskRow({ task, team, isViewer, onUpdateTask, onDeleteTask, onFileUpload, onDeleteFile, onDownloadFile, uploading, fileInputRef, pc, sc, isSelected, onToggleSelect }) {
  const canSelect = task.is_doc_request && (task.status === 'Not Started' || task.status === 'Requested');
  const statusStyle = {
    backgroundColor:
      task.status === 'Complete'     ? '#DCFCE7' :
      task.status === 'In Progress'  ? '#DBEAFE' :
      task.status === 'Received'     ? '#EDE9FE' :
      task.status === 'Requested'    ? '#FEF3C7' :
      task.status === 'Under Review' ? '#FFEDD5' :
      task.status === 'N/A'          ? '#F1F5F9' :
                                       '#F1F5F9',
    color:
      task.status === 'Complete'     ? '#15803D' :
      task.status === 'In Progress'  ? '#1E40AF' :
      task.status === 'Received'     ? '#6D28D9' :
      task.status === 'Requested'    ? '#A16207' :
      task.status === 'Under Review' ? '#C2410C' :
      task.status === 'N/A'          ? '#64748B' :
                                       '#475569',
  };
  return (
    <div style={{ borderBottom: '1px solid #F0F2F6' }} className="last:border-b-0 transition-colors hover:bg-[#FAFBFD]">
      <div className="flex items-center gap-2.5 px-5 py-3">
        {canSelect && !isViewer ? (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect(task.id)}
            className="w-4 h-4 rounded cursor-pointer flex-shrink-0 accent-[#A16207]"
            title="Select for document request"
          />
        ) : task.is_doc_request ? (
          <div className="w-4 flex-shrink-0" />
        ) : null}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {task.is_doc_request && (
              <FileText className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#64748B' }} title="Document Request" />
            )}
            <span className="text-sm" style={{ color: '#0F172A' }}>{task.name}</span>
            {task.is_custom && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded"
                    style={{ backgroundColor: '#F1F5F9', color: '#64748B' }}>
                Custom
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1 text-[11px]" style={{ color: '#94A3B8' }}>
            {task.due_date && (
              <span className="inline-flex items-center gap-1 tabular-nums">
                <Calendar className="w-3 h-3" /> {task.due_date}
              </span>
            )}
            {task.assignee_name && (
              <span className="inline-flex items-center gap-1">
                <User className="w-3 h-3" /> {task.assignee_name}
              </span>
            )}
            {task.files?.length > 0 && (
              <span className="tabular-nums font-medium" style={{ color: '#1A3665' }}>
                {task.files.length} file{task.files.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
        {!isViewer ? (
          <>
            <select
              value={task.assignee_name || ''}
              onChange={e => onUpdateTask(task.id, 'assignee_name', e.target.value)}
              className="hidden sm:block h-7 w-28 text-xs rounded-md px-2 cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-0"
              style={{ border: '1px solid #E5E9F0', color: '#0F172A', backgroundColor: 'white' }}
            >
              <option value="">Assign</option>
              {team.map(m => <option key={m.id} value={m.full_name}>{m.full_name}</option>)}
            </select>
            <input
              type="date"
              value={task.due_date || ''}
              onChange={e => onUpdateTask(task.id, 'due_date', e.target.value)}
              className="hidden sm:block h-7 w-32 text-xs rounded-md px-2 tabular-nums focus:outline-none focus:ring-2"
              style={{ border: '1px solid #E5E9F0', color: '#0F172A', backgroundColor: 'white' }}
            />
            <select
              value={task.status}
              onChange={e => onUpdateTask(task.id, 'status', e.target.value)}
              className="h-7 w-24 sm:w-28 text-[11px] rounded-md px-2 font-semibold cursor-pointer border-0 focus:outline-none focus:ring-2 focus:ring-offset-1"
              style={{ ...statusStyle, boxShadow: 'inset 0 0 0 1px rgba(15, 23, 42, 0.04)' }}
            >
              {STATUSES.map(s => <option key={s}>{s}</option>)}
            </select>
          </>
        ) : (
          <span className="h-7 inline-flex items-center text-[11px] px-2 rounded-md font-semibold"
                style={statusStyle}>
            {task.status}
          </span>
        )}
        {task.is_doc_request && (
          <div className="relative">
            <input ref={fileInputRef} type="file" onChange={(e) => onFileUpload(task.id, e)} className="hidden" id={`file-${task.id}`} accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.gif,.txt" />
            <label
              htmlFor={`file-${task.id}`}
              className={`h-7 w-7 rounded-md flex items-center justify-center cursor-pointer transition-colors hover:bg-slate-100 ${uploading ? 'opacity-50' : ''}`}
              style={{ color: '#475569' }}
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            </label>
          </div>
        )}
        {!isViewer && task.is_custom && (
          <button
            type="button"
            onClick={() => onDeleteTask(task.id)}
            className="h-7 w-7 rounded-md flex items-center justify-center transition-colors hover:bg-red-50"
            style={{ color: '#EF4444' }}
            title="Delete task"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {task.files?.length > 0 && (
        <div className="pb-3 pl-10 pr-5">
          <div className="rounded-lg overflow-hidden" style={{ backgroundColor: '#FAFBFD', border: '1px solid #F0F2F6' }}>
            {task.files.map((file, i) => (
              <div
                key={file.id}
                className="flex items-center justify-between text-[11px] px-3 py-2 transition-colors hover:bg-white"
                style={{ borderTop: i === 0 ? 'none' : '1px solid #F0F2F6' }}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="w-3 h-3 flex-shrink-0" style={{ color: '#94A3B8' }} />
                  <button
                    type="button"
                    className="truncate"
                    style={{ color: '#0F172A' }}
                    onClick={() => onDownloadFile(file.storage_path, file.original_name)}
                  >
                    {file.original_name}
                  </button>
                  <span className="tabular-nums flex-shrink-0" style={{ color: '#94A3B8' }}>
                    {Math.round(file.size_bytes / 1024)} KB
                  </span>
                  <span className="flex-shrink-0" style={{ color: '#94A3B8' }}>
                    by {file.uploaded_by_name}
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="tabular-nums" style={{ color: '#94A3B8' }}>
                    {new Date(file.created_at).toLocaleDateString()}
                  </span>
                  <button type="button" onClick={() => onDownloadFile(file.storage_path, file.original_name)}
                          className="transition-opacity hover:opacity-70" style={{ color: '#475569' }}>
                    <Download className="w-3 h-3" />
                  </button>
                  {!isViewer && (
                    <button type="button" onClick={() => onDeleteFile(task.id, file.id, file.storage_path)}
                            className="transition-colors hover:opacity-70" style={{ color: '#EF4444' }}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Inline-render an answer string from ask-deal-ai. The function returns
// answers with [src:<file_id>] tokens embedded in the text; this splits the
// string on those tokens and renders each one as a clickable file chip.
function AnswerWithCitations({ answer, fileById, onOpenFile }) {
  if (!answer) return null;
  const re = /\[src:([0-9a-fA-F-]{8,})\]/g;
  const parts = [];
  let lastIdx = 0;
  let m;
  let key = 0;
  while ((m = re.exec(answer)) !== null) {
    if (m.index > lastIdx) parts.push(<span key={`t${key++}`}>{answer.slice(lastIdx, m.index)}</span>);
    const fileId = m[1];
    const file = fileById[fileId];
    if (file) {
      parts.push(
        <button
          key={`c${key++}`}
          type="button"
          onClick={() => onOpenFile(file)}
          className="inline-flex items-center gap-1 align-middle text-[11px] font-medium px-1.5 py-0.5 mx-0.5 rounded-md transition-colors hover:opacity-80"
          style={{ backgroundColor: '#EFF4FB', color: '#1A3665', border: '1px solid #DBE6F5' }}
          title={file.original_name}
        >
          <FileText className="w-3 h-3" />
          <span className="max-w-[200px] truncate">{file.original_name}</span>
        </button>
      );
    } else {
      // Unknown file id — render the raw token in muted gray
      parts.push(<span key={`u${key++}`} className="text-[11px]" style={{ color: '#94A3B8' }}>[missing source]</span>);
    }
    lastIdx = re.lastIndex;
  }
  if (lastIdx < answer.length) parts.push(<span key={`t${key++}`}>{answer.slice(lastIdx)}</span>);
  return <span style={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{parts}</span>;
}

const ASK_AI_SUGGESTIONS = [
  'What are the most concerning items I should know about this deal?',
  'How many vehicles, computers, and peripherals are there?',
  'List every employee earning over $50k/year.',
  'What does the seller pay annually for utilities?',
  'Are there any mentions of pre-need trust funding?',
];

function AskAIPanel({ dealId, dealName, allDealFiles, onOpenFile }) {
  const [history, setHistory] = useState([]);   // [{ role, content, citations? }]
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  const fileById = useMemo(() => {
    const map = {};
    (allDealFiles || []).forEach(f => { map[f.id] = f; });
    return map;
  }, [allDealFiles]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [history, loading]);

  const ask = useCallback(async (questionOverride) => {
    const q = (questionOverride !== undefined ? questionOverride : input).trim();
    if (!q || loading) return;
    setError(null);
    const nextHistory = [...history, { role: 'user', content: q }];
    setHistory(nextHistory);
    setInput('');
    setLoading(true);
    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      const r = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ask-deal-ai`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${token || ''}`,
        },
        body: JSON.stringify({
          deal_id: dealId,
          question: q,
          history: nextHistory.slice(0, -1).slice(-10), // exclude the just-added user msg
        }),
      });
      const body = await r.json();
      if (!r.ok || !body.success) throw new Error(body.error || `HTTP ${r.status}`);
      setHistory(h => [...h, { role: 'assistant', content: body.answer || '', citations: body.citations || [] }]);
    } catch (e) {
      setError(e.message || 'Failed to ask');
      setHistory(h => [...h, { role: 'assistant', content: '', error: e.message || 'Failed' }]);
    } finally {
      setLoading(false);
    }
  }, [input, history, loading, dealId]);

  return (
    <div
      className="mt-6 overflow-hidden rounded-2xl relative"
      style={{ backgroundColor: '#fff', border: '1px solid #E5E9F0', boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.03)' }}
    >
      <div
        className="absolute top-0 left-0 right-0 h-[3px]"
        style={{ background: 'linear-gradient(90deg, #1A3665 0%, #3B5C8C 30%, #6B85AB 70%, transparent 100%)' }}
      />
      <div className="px-6 py-5">
        <div className="flex items-center gap-3 mb-3">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #1A3665 0%, #2E4B7C 100%)', boxShadow: '0 2px 6px rgba(26, 54, 101, 0.3)' }}
          >
            <MessageSquare className="w-4 h-4 text-white" strokeWidth={2.2} />
          </div>
          <div>
            <h2 className="text-base font-semibold tracking-tight" style={{ color: '#0F172A', letterSpacing: '-0.01em' }}>
              Ask AI about {dealName || 'this deal'}
            </h2>
            <div className="text-xs mt-0.5" style={{ color: '#64748B' }}>
              Reads every extracted detail and document summary. Cites the source files inline.
            </div>
          </div>
        </div>

        {history.length === 0 && (
          <div className="mb-3">
            <div className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: '#94A3B8', letterSpacing: '0.08em' }}>
              Try asking
            </div>
            <div className="flex flex-wrap gap-2">
              {ASK_AI_SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => ask(s)}
                  disabled={loading}
                  className="text-[12px] px-2.5 py-1.5 rounded-md transition-colors hover:bg-slate-50 text-left"
                  style={{ backgroundColor: '#FAFBFD', color: '#334155', border: '1px solid #E5E9F0' }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {history.length > 0 && (
          <div
            ref={scrollRef}
            className="max-h-[480px] overflow-y-auto rounded-xl mb-3 p-3 space-y-3"
            style={{ backgroundColor: '#FAFBFD', border: '1px solid #F0F2F6' }}
          >
            {history.map((turn, i) => (
              <div key={i} className={`flex ${turn.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className="max-w-[85%] rounded-xl px-3 py-2"
                  style={turn.role === 'user'
                    ? { background: 'linear-gradient(135deg, #1A3665 0%, #2E4B7C 100%)', color: 'white' }
                    : { backgroundColor: 'white', color: '#0F172A', border: '1px solid #E5E9F0' }}
                >
                  {turn.role === 'user' ? (
                    <div className="text-[13px]" style={{ whiteSpace: 'pre-wrap' }}>{turn.content}</div>
                  ) : turn.error ? (
                    <div className="text-[12px]" style={{ color: '#B91C1C' }}>Error: {turn.error}</div>
                  ) : (
                    <div className="text-[13px]">
                      <AnswerWithCitations answer={turn.content} fileById={fileById} onOpenFile={onOpenFile} />
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="rounded-xl px-3 py-2 inline-flex items-center gap-2" style={{ backgroundColor: 'white', border: '1px solid #E5E9F0', color: '#64748B' }}>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span className="text-[12px]">Thinking…</span>
                </div>
              </div>
            )}
          </div>
        )}

        <form
          onSubmit={(e) => { e.preventDefault(); ask(); }}
          className="flex items-center gap-2"
        >
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask anything about this deal…"
            disabled={loading}
            className="flex-1 h-10 px-3 text-[13px] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A3665]/20"
            style={{ backgroundColor: '#FAFBFD', border: '1px solid #E5E9F0', color: '#0F172A' }}
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="h-10 inline-flex items-center gap-1.5 px-4 text-[13px] font-semibold rounded-lg text-white transition-all hover:shadow-md disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #1A3665 0%, #2E4B7C 100%)' }}
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Ask
          </button>
        </form>
        {error && <div className="text-[11px] mt-2" style={{ color: '#B91C1C' }}>{error}</div>}
      </div>
    </div>
  );
}

// sort_order is scoped per parent (each parent's children are numbered
// 1, 2, 3... independently of top-level categories' own 1-12 numbering),
// so a flat list sorted by sort_order alone puts e.g. "01 Bank Statements"
// (first child of "02 Financial") right next to "01 Legal" (first
// top-level category) with nothing to show they're not siblings. Group
// depth-first (parent immediately followed by its own children, each
// child's sort_order re-sorted within that parent) so the dropdown can
// indent children instead of looking like duplicate categories.
function flattenCategoryTree(categories) {
  const byParent = {};
  categories.forEach(c => {
    const key = c.parent_id || 'root';
    (byParent[key] = byParent[key] || []).push(c);
  });
  Object.values(byParent).forEach(list => list.sort((a, b) => a.sort_order - b.sort_order));
  const result = [];
  const visit = (parentKey, depth) => {
    (byParent[parentKey] || []).forEach(c => {
      result.push({ ...c, depth });
      visit(c.id, depth + 1);
    });
  };
  visit('root', 0);
  return result;
}

// Per-file-row pill that shows the current category and opens a dropdown
// to recategorize. Selecting a different category triggers
// handleRecategorizeFile in DealView, which writes to files.category_id +
// category_slug AND logs a row in category_corrections so future AI
// classifications learn from the fix.
function FileCategoryMenu({ file, categories, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const current = categories.find(c => c.id === file.category_id);
  const label = current ? current.display_name : 'Set category';

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium transition-colors hover:bg-white"
        style={{
          backgroundColor: current ? '#EFF4FB' : '#FEF3C7',
          color: current ? '#1A3665' : '#A16207',
          border: `1px solid ${current ? '#DBE6F5' : '#FDE68A'}`,
          maxWidth: '180px',
        }}
        title={current ? `Move "${file.original_name}" to a different category` : 'Set category'}
      >
        <span className="truncate">{label}</span>
        <ChevronDown className="w-3 h-3 opacity-70 flex-shrink-0" />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 rounded-lg overflow-hidden z-30 min-w-[240px] max-h-[320px] overflow-y-auto py-1"
          style={{
            backgroundColor: 'white',
            border: '1px solid #E5E9F0',
            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.12), 0 4px 10px rgba(15, 23, 42, 0.06)',
          }}
        >
          <div
            className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider"
            style={{ color: '#94A3B8', borderBottom: '1px solid #F0F2F6' }}
          >
            Move to category
          </div>
          {flattenCategoryTree(categories).map(cat => {
            const isCurrent = file.category_id === cat.id;
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => { setOpen(false); if (!isCurrent) onChange(cat.id); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-[13px] transition-colors hover:bg-[#FAFBFD]"
                style={{ color: cat.depth > 0 ? '#475569' : '#0F172A', paddingLeft: `${12 + cat.depth * 16}px` }}
              >
                <span className="flex-1 truncate">{cat.display_name}</span>
                {isCurrent && <Check className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#16A34A' }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DuplicateGroup({ kind, title, icon: Icon, pairs, files, onResolve }) {
  // Default open when there are only a few pairs; collapsed when there's a
  // lot to review, so the user isn't dumped into a wall of cards on first
  // render.
  const [open, setOpen] = useState(pairs.length <= 5);
  return (
    <div className="rounded-lg overflow-hidden" style={{ backgroundColor: 'white', border: '1px solid #FDE68A' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-[#FFFCEF]"
        style={{ borderBottom: open ? '1px solid #FDE68A' : 'none' }}
      >
        <ChevronDown
          className="w-3.5 h-3.5 flex-shrink-0 transition-transform"
          style={{ color: '#A16207', transform: open ? 'rotate(0deg)' : 'rotate(-90deg)' }}
        />
        {Icon && (
          <div className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FEF3C7', color: '#A16207' }}>
            <Icon className="w-3 h-3" strokeWidth={2.2} />
          </div>
        )}
        <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#92400E' }}>
          {title}
        </div>
        <span className="text-[10px] tabular-nums px-1.5 py-0.5 rounded font-semibold" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
          {pairs.length}
        </span>
      </button>
      {open && (
        <div className="divide-y" style={{ borderColor: '#FDE68A' }}>
          {pairs.map(pair => (
            <DuplicatePairRow
              key={`${kind}-${pair.dup.id}`}
              kind={kind}
              pair={pair}
              files={files}
              onSmartMerge={() => onResolve(kind, 'smart_merge', pair.dup.id, pair.canon.id)}
              onDrop={() => onResolve(kind, 'drop', pair.dup.id)}
              onDismiss={() => onResolve(kind, 'dismiss', pair.dup.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function DuplicatePairRow({ kind, pair, files, onSmartMerge, onDrop, onDismiss }) {
  const { dup, canon } = pair;
  const fmtPersonnel = (r) => ({
    title: [r.first_name, r.last_name].filter(Boolean).join(' ') || '(no name)',
    sub: [r.position, r.email_address || r.phone_number, r.hire_date].filter(Boolean).join(' · '),
  });
  const fmtVehicle = (r) => ({
    title: [r.year, r.make, r.model].filter(Boolean).join(' ') || '(unknown vehicle)',
    sub: [r.vin && `VIN ${r.vin}`, r.mileage && `${Number(r.mileage).toLocaleString()} mi`, r.balance_owed && `$${Number(r.balance_owed).toLocaleString()} owed`].filter(Boolean).join(' · '),
  });
  const fmt = kind === 'personnel' ? fmtPersonnel : fmtVehicle;
  const c = fmt(canon);
  const d = fmt(dup);
  const conf = Math.round((dup.dup_confidence || 0) * 100);
  const canonFile = canon.source_file_id ? files[canon.source_file_id] : null;
  const dupFile = dup.source_file_id ? files[dup.source_file_id] : null;

  // Surface the merge preview: which fields differ and what the merged record
  // would look like. Only shown if there's actually new info to gain from
  // merging — a "100% identical" pair just gets the simpler buttons.
  const PER_FIELDS = ['first_name','middle_initial','last_name','position','department','employment_type','hourly_rate','salary_rate','hire_date','phone_number','email_address','hours_per_year','ytd_pto_accrued','pto_entitlement','phone_stipend','clothing_allowance'];
  const VEH_FIELDS = ['year','make','model','vin','mileage','loan_holder','balance_owed','kelly_blue_book'];
  const fields = kind === 'personnel' ? PER_FIELDS : VEH_FIELDS;
  const isEmpty = (v) => v === null || v === undefined || v === '';
  const dupAdds = fields
    .filter(f => isEmpty(canon[f]) && !isEmpty(dup[f]))
    .map(f => ({ field: f, val: dup[f] }));
  const fieldLabel = (f) => f.replace(/_/g, ' ').replace(/\b\w/g, m => m.toUpperCase());

  return (
    <div className="px-4 py-3" style={{ backgroundColor: '#FFFCEF' }}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span
          className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded tabular-nums"
          style={{ backgroundColor: conf >= 90 ? '#FEE2E2' : '#FEF3C7', color: conf >= 90 ? '#B91C1C' : '#92400E', letterSpacing: '0.04em' }}
        >
          {conf}% match
        </span>
        <span className="text-[10px] uppercase font-semibold tracking-wider" style={{ color: '#92400E', letterSpacing: '0.06em' }}>
          {kind === 'personnel' ? 'Personnel' : 'Vehicle'}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-md p-2" style={{ backgroundColor: 'white', border: '1px solid #E5E9F0' }}>
          <div className="text-[10px] uppercase font-semibold tracking-wider mb-1" style={{ color: '#16A34A', letterSpacing: '0.06em' }}>
            Keep (older)
          </div>
          <div className="text-[13px] font-semibold" style={{ color: '#0F172A' }}>{c.title}</div>
          {c.sub && <div className="text-[11px] mt-0.5" style={{ color: '#64748B' }}>{c.sub}</div>}
          {canonFile && <div className="text-[10px] mt-1 italic" style={{ color: '#94A3B8' }}>from {canonFile}</div>}
        </div>
        <div className="rounded-md p-2" style={{ backgroundColor: 'white', border: '1px solid #FECACA' }}>
          <div className="text-[10px] uppercase font-semibold tracking-wider mb-1" style={{ color: '#DC2626', letterSpacing: '0.06em' }}>
            Possible duplicate
          </div>
          <div className="text-[13px] font-semibold" style={{ color: '#0F172A' }}>{d.title}</div>
          {d.sub && <div className="text-[11px] mt-0.5" style={{ color: '#64748B' }}>{d.sub}</div>}
          {dupFile && <div className="text-[10px] mt-1 italic" style={{ color: '#94A3B8' }}>from {dupFile}</div>}
        </div>
      </div>

      {dupAdds.length > 0 && (
        <div className="mt-2 rounded-md p-2 text-[11px]" style={{ backgroundColor: '#ECFDF5', border: '1px solid #A7F3D0', color: '#065F46' }}>
          <span className="font-semibold">Merging would also pull in:</span>{' '}
          {dupAdds.map((d2, i) => (
            <span key={d2.field}>
              {i > 0 && ', '}
              <span className="font-mono text-[10px]" style={{ color: '#047857' }}>{fieldLabel(d2.field)}</span>
              <span className="ml-1">= </span>
              <span className="font-semibold">{String(d2.val)}</span>
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 mt-2 flex-wrap">
        <button
          type="button"
          onClick={onSmartMerge}
          className="h-7 inline-flex items-center gap-1 px-2.5 text-[11px] font-semibold rounded-md text-white transition-all hover:shadow-sm"
          style={{ background: 'linear-gradient(135deg, #1A3665 0%, #2E4B7C 100%)' }}
          title="Combine non-empty fields from both records into one, then drop the duplicate"
        >
          <Sparkles className="w-3 h-3" /> Smart merge
        </button>
        <button
          type="button"
          onClick={onDrop}
          className="h-7 inline-flex items-center gap-1 px-2.5 text-[11px] font-medium rounded-md transition-colors hover:bg-red-50"
          style={{ backgroundColor: 'transparent', color: '#B91C1C', border: '1px solid #FECACA' }}
          title="Discard the duplicate without merging — use if its data is wrong"
        >
          <Trash2 className="w-3 h-3" /> Drop duplicate
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="h-7 inline-flex items-center gap-1 px-2.5 text-[11px] font-medium rounded-md transition-colors hover:bg-white"
          style={{ backgroundColor: 'transparent', color: '#475569', border: '1px solid #E5E9F0' }}
        >
          Keep both — these are different
        </button>
      </div>
    </div>
  );
}

function InfoSection({ title, icon: Icon, count, children, defaultOpen = true, anchorId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      id={anchorId}
      className="rounded-xl overflow-hidden scroll-mt-24"
      style={{ backgroundColor: '#ffffff', border: '1px solid #E5E9F0', boxShadow: '0 1px 2px rgba(15, 23, 42, 0.03)' }}
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left transition-colors hover:bg-[#FAFBFD]"
        style={{ borderBottom: open ? '1px solid #F0F2F6' : 'none' }}
      >
        <ChevronDown
          className="w-3.5 h-3.5 flex-shrink-0 transition-transform"
          style={{ color: '#94A3B8', transform: open ? 'rotate(0deg)' : 'rotate(-90deg)' }}
        />
        {Icon && (
          <div
            className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: '#ECFEFF', color: '#0891B2' }}
          >
            <Icon className="w-3.5 h-3.5" strokeWidth={2.2} />
          </div>
        )}
        <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#475569' }}>
          {title}
        </div>
        {typeof count === 'number' && (
          <span className="text-[10px] tabular-nums px-1.5 py-0.5 rounded font-semibold" style={{ backgroundColor: '#F1F5F9', color: '#64748B' }}>
            {count}
          </span>
        )}
      </button>
      {open && <div className="p-4">{children}</div>}
    </div>
  );
}

function Modal({ children, onClose, title, maxWidth = 'max-w-md', pc = '#1A3665' }) {
  const { displayPrimary } = useDisplayColors(pc, null);
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-lg w-full ${maxWidth} max-h-[90vh] flex flex-col`}>
        <div className="p-4 border-b flex justify-between items-center flex-shrink-0">
          <h3 className="font-semibold text-lg" style={{ color: displayPrimary }}>{title}</h3>
          <button type="button" onClick={onClose} className="hover:bg-gray-100 p-1 rounded transition-colors"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

// ============================================================================
// BUSINESS INFO EDIT MODAL
// Allows LFG team to enter / edit business information on behalf of the seller
// Uses saveInfoSectionInternal (authenticated, no seller token required)
// ============================================================================

const BI_TABS = [
  { id: 'personnel',  label: 'Personnel' },
  { id: 'vehicles',   label: 'Vehicles' },
  { id: 'utilities',  label: 'Utilities' },
  { id: 'preneed',    label: 'Preneed' },
  { id: 'facilities', label: 'Facilities' },
  { id: 'crematory',  label: 'Crematory' },
  { id: 'technology', label: 'Technology' },
];
const BI_UTILITY_TYPES  = ['gas', 'electric', 'water', 'telephone', 'internet', 'refuse_collection'];
const BI_FACILITY_TYPES = ['hvac', 'plumber', 'electrician', 'snow_removal', 'lawn_landscape', 'parking_lot', 'pest_control', 'roofer', 'other'];
const BI_TECH_CATS      = ['website', 'phone_system', 'it_contact', 'domain_registrar', 'email_platform'];
const BI_TECH_LABELS    = { website: 'Website', phone_system: 'Phone System', it_contact: 'IT Contact', domain_registrar: 'Domain Registrar', email_platform: 'Email Platform' };
const BI_TECH_URL_LABEL = { website: 'URL / Domain', phone_system: 'Provider / System', it_contact: 'Name / Company', domain_registrar: 'Registrar', email_platform: 'Platform' };


// ── CSV helpers for BusinessInfoEditModal ────────────────────────────────────
function biParseCSV(text) {
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter(l => l.trim());
  if (lines.length < 2) return [];
  const parseRow = (line) => {
    const cols = []; let cur = '', inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') { inQ = !inQ; }
      else if (c === ',' && !inQ) { cols.push(cur.trim()); cur = ''; }
      else { cur += c; }
    }
    cols.push(cur.trim()); return cols;
  };
  const headers = parseRow(lines[0]).map(h => h.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_'));
  return lines.slice(1).map(line => {
    const vals = parseRow(line); const obj = {};
    headers.forEach((h, i) => { obj[h] = vals[i] ?? ''; }); return obj;
  });
}
function biCol(row, ...aliases) {
  for (const a of aliases) {
    const key = a.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_');
    if (row[key] !== undefined && row[key] !== '') return row[key];
  }
  return '';
}
function biBool(row, ...aliases) {
  const v = biCol(row, ...aliases).toLowerCase();
  return v === 'true' || v === 'yes' || v === '1' || v === 'x';
}
function biDownloadTemplate(filename, headers) {
  const csv = headers.join(',') + '\n';
  const a = document.createElement('a');
  a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
  a.download = filename; a.click();
}
function BiCsvUploadBtn({ onParsed, label = 'Upload CSV' }) {
  const ref = React.useRef();
  const [err, setErr] = React.useState('');
  return (
    <span className="inline-flex items-center gap-1.5">
      <input ref={ref} type="file" accept=".csv,text/csv" className="hidden"
        onChange={e => {
          const file = e.target.files?.[0]; if (!file) return;
          const reader = new FileReader();
          reader.onload = ev => {
            try {
              const rows = biParseCSV(ev.target.result);
              if (!rows.length) { setErr('No data rows found'); return; }
              onParsed(rows); setErr('');
            } catch(ex) { setErr('Parse error'); }
            ref.current.value = '';
          };
          reader.readAsText(file);
        }}
      />
      <button type="button" onClick={() => ref.current.click()}
        className="text-xs px-2 py-1 rounded border border-blue-300 text-blue-600 hover:bg-blue-50 flex items-center gap-1">
        <Upload className="w-3 h-3" /> {label}
      </button>
      {err && <span className="text-xs text-red-500">{err}</span>}
    </span>
  );
}

const emptyBI = {
  personnel:  () => ({ first_name:'', last_name:'', middle_initial:'', phone_number:'', email_address:'', hire_date:'', position:'', employment_type:'Full Time', hours_per_year:'', hourly_rate:'', salary_rate:'', ytd_pto_accrued:'', pto_entitlement:'', has_401k:false, company_credit_card:false, phone_stipend:false, company_vehicle:false, life_insurance:false, clothing_allowance:false }),
  vehicle:    () => ({ year:'', make:'', model:'', vin:'', mileage:'', loan_balance:'', kbb_value:'' }),
  preneed:    () => ({ company_name:'', contract_number:'', contact_name:'', contact_phone:'' }),
  computer:   () => ({ computer_type:'desktop', make:'', model:'', serial_number:'' }),
  software:   () => ({ name:'', monthly_cost:'', annual_cost:'', renewal_date:'', login_url:'', username:'' }),
  crematory:  () => ({ machine:'', installation_date:'', capacity:'', epa_certification:'', regulatory_info:'', maintenance_program:'', last_service_date:'' }),
};

function initUtilities(data) {
  const base = {};
  BI_UTILITY_TYPES.forEach(t => base[t] = { vendor_name:'', account_number:'', contract_end:'' });
  (data || []).forEach(u => { if (u.utility_type) base[u.utility_type] = u; });
  return base;
}
function initFacilities(data) {
  const base = {};
  BI_FACILITY_TYPES.forEach(t => base[t] = { vendor_name:'', vendor_phone:'', contract_end:'' });
  (data || []).forEach(f => { if (f.facility_type) base[f.facility_type] = f; });
  return base;
}
function initTechOther(data) {
  const base = {};
  BI_TECH_CATS.forEach(c => base[c] = { url:'', notes:'' });
  (data || []).forEach(o => { if (o.category) base[o.category] = o; });
  return base;
}

function BusinessInfoEditModal({ dealId, organizationId, infoData, primaryColor, isIntegration, onClose, onSaved }) {
  const pc = primaryColor || '#1A3665';
  const { displayPrimary, displaySecondary, displayNavy, displayGreen } = useDisplayColors(pc, null);
  const [activeTab, setActiveTab] = useState('personnel');
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState({});

  // Form state — pre-populated from existing data
  const [personnel,    setPersonnel]    = useState(() => infoData?.personnel?.length    ? infoData.personnel    : [emptyBI.personnel()]);
  const [vehicles,     setVehicles]     = useState(() => infoData?.vehicles?.length     ? infoData.vehicles     : [emptyBI.vehicle()]);
  const [utilities,    setUtilities]    = useState(() => initUtilities(infoData?.utilities));
  const [preneed,      setPreneed]      = useState(() => infoData?.preneed?.length      ? infoData.preneed      : [emptyBI.preneed()]);
  const [facilities,   setFacilities]   = useState(() => initFacilities(infoData?.facilities));
  const [crematory,    setCrematory]    = useState(() => infoData?.crematory?.length    ? infoData.crematory    : [emptyBI.crematory()]);
  const [techComputers,setTechComputers]= useState(() => infoData?.tech_computers?.length ? infoData.tech_computers : [emptyBI.computer()]);
  const [techSoftware, setTechSoftware] = useState(() => infoData?.tech_software?.length  ? infoData.tech_software  : [emptyBI.software()]);
  const [techOther,    setTechOther]    = useState(() => initTechOther(infoData?.tech_other));

  const getPayload = (tab) => {
    if (tab === 'personnel')  return personnel;
    if (tab === 'vehicles')   return vehicles;
    if (tab === 'utilities')  return BI_UTILITY_TYPES.map(t => ({ utility_type: t, ...utilities[t] }));
    if (tab === 'preneed')    return preneed;
    if (tab === 'facilities') return BI_FACILITY_TYPES.map(t => ({ facility_type: t, ...facilities[t] }));
    if (tab === 'crematory')  return crematory;
    if (tab === 'technology') return { computers: techComputers, software: techSoftware, other: BI_TECH_CATS.map(c => ({ category: c, ...techOther[c] })).filter(o => o.url || o.notes) };
  };

  // Snapshot of what this modal knew about at mount time — by row id for
  // arbitrary lists, by natural key for fixed-slot categories. On save,
  // diffing against this tells the server which rows were explicitly
  // removed; anything absent from the snapshot to begin with (e.g. a row
  // auto-categorize-document inserted while this modal was open) is left
  // alone rather than deleted. This modal only mounts once infoData is
  // already loaded (no async re-sync needed here, unlike the seller
  // portal), so a single ref captured at construction time is enough.
  const snapshotRef = useRef({
    personnel: new Set((infoData?.personnel || []).map(r => r.id).filter(Boolean)),
    vehicles: new Set((infoData?.vehicles || []).map(r => r.id).filter(Boolean)),
    preneed: new Set((infoData?.preneed || []).map(r => r.id).filter(Boolean)),
    crematory: new Set((infoData?.crematory || []).map(r => r.id).filter(Boolean)),
    tech_computers: new Set((infoData?.tech_computers || []).map(r => r.id).filter(Boolean)),
    tech_software: new Set((infoData?.tech_software || []).map(r => r.id).filter(Boolean)),
    utilities: new Set((infoData?.utilities || []).filter(u => u.vendor_name || u.account_number).map(u => u.utility_type)),
    facilities: new Set((infoData?.facilities || []).filter(f => f.vendor_name || f.vendor_phone).map(f => f.facility_type)),
    tech_other: new Set((infoData?.tech_other || []).filter(o => o.url || o.notes).map(o => o.category)),
  });

  const computeRemoved = (tab, data) => {
    const snap = snapshotRef.current;
    if (tab === 'utilities') {
      const current = new Set(data.filter(r => r.vendor_name || r.account_number).map(r => r.utility_type));
      return Array.from(snap.utilities).filter(k => !current.has(k));
    }
    if (tab === 'facilities') {
      const current = new Set(data.filter(r => r.vendor_name || r.vendor_phone).map(r => r.facility_type));
      return Array.from(snap.facilities).filter(k => !current.has(k));
    }
    if (tab === 'technology') {
      const currentComputers = new Set((data.computers || []).map(r => r.id).filter(Boolean));
      const currentSoftware = new Set((data.software || []).map(r => r.id).filter(Boolean));
      const currentOther = new Set((data.other || []).map(r => r.category));
      return {
        computers: Array.from(snap.tech_computers).filter(id => !currentComputers.has(id)),
        software: Array.from(snap.tech_software).filter(id => !currentSoftware.has(id)),
        other: Array.from(snap.tech_other).filter(k => !currentOther.has(k)),
      };
    }
    const current = new Set((Array.isArray(data) ? data : []).map(r => r.id).filter(Boolean));
    return Array.from(snap[tab] || []).filter(id => !current.has(id));
  };

  const handleSave = async (tab) => {
    setSaving(true);
    try {
      const payload = getPayload(tab);
      await saveInfoSectionInternal(dealId, organizationId, tab, payload, computeRemoved(tab, payload));
      setSaveStatus(p => ({ ...p, [tab]: 'saved' }));
      setTimeout(() => setSaveStatus(p => ({ ...p, [tab]: null })), 2500);
      if (onSaved) onSaved(tab);
    } catch (err) {
      console.error('Save error:', err);
      setSaveStatus(p => ({ ...p, [tab]: 'error' }));
    } finally {
      setSaving(false);
    }
  };

  const inp = "w-full border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-300";
  const lbl = "block text-xs text-gray-400 mb-0.5";

  const SaveBtn = ({ tab }) => (
    <button type="button"
      onClick={() => handleSave(tab)}
      disabled={saving}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded text-white text-xs font-medium disabled:opacity-50 hover:opacity-90"
      style={{ backgroundColor: pc }}
    >
      {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : saveStatus[tab] === 'saved' ? <CheckCircle className="w-3.5 h-3.5" /> : <Save className="w-3.5 h-3.5" />}
      {saveStatus[tab] === 'saved' ? 'Saved!' : saveStatus[tab] === 'error' ? 'Error — Retry' : 'Save Section'}
    </button>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg w-full max-w-3xl my-4">
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center">
          <div>
            <h3 className="font-semibold text-base" style={{ color: displayPrimary }}>Edit Business Information</h3>
            <p className="text-xs text-gray-500 mt-0.5">Entering on behalf of seller — save each section individually</p>
          </div>
          <button type="button" onClick={onClose} className="hover:bg-gray-100 p-1.5 rounded transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b overflow-x-auto">
          {BI_TABS.map(tab => (
            <button type="button"
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-2 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.id ? 'border-current' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              style={activeTab === tab.id ? { color: displayPrimary, borderColor: displayPrimary } : {}}
            >
              {tab.label}
              {saveStatus[tab.id] === 'saved' && <CheckCircle className="w-3 h-3 inline ml-1 text-green-500" />}
            </button>
          ))}
        </div>

        {/* Form body */}
        <div className="p-4 max-h-[60vh] overflow-y-auto">

          {/* PERSONNEL */}
          {activeTab === 'personnel' && (
            <div className="space-y-2">
              {personnel.map((p, i) => (
                <div key={i} className="border rounded p-2 relative">
                  {personnel.length > 1 && (
                    <button type="button" onClick={() => setPersonnel(prev => prev.filter((_, j) => j !== i))} className="absolute top-1.5 right-1.5 text-gray-300 hover:text-red-400 text-xs">Remove</button>
                  )}
                  {/* Row 1: Name */}
                  <div className="grid grid-cols-4 gap-2 mb-2">
                    <div><label className={lbl}>First Name</label><input className={inp} value={p.first_name} onChange={e => setPersonnel(prev => prev.map((x,j)=>j===i?{...x,first_name:e.target.value}:x))} /></div>
                    <div><label className={lbl}>MI</label><input className={inp} maxLength={1} value={p.middle_initial||''} onChange={e => setPersonnel(prev => prev.map((x,j)=>j===i?{...x,middle_initial:e.target.value}:x))} /></div>
                    <div><label className={lbl}>Last Name</label><input className={inp} value={p.last_name} onChange={e => setPersonnel(prev => prev.map((x,j)=>j===i?{...x,last_name:e.target.value}:x))} /></div>
                    <div><label className={lbl}>Hire Date</label><input className={inp} type="date" value={p.hire_date||''} onChange={e => setPersonnel(prev => prev.map((x,j)=>j===i?{...x,hire_date:e.target.value}:x))} /></div>
                  </div>
                  {/* Row 2: Contact & Position */}
                  <div className="grid grid-cols-4 gap-2 mb-2">
                    <div><label className={lbl}>Phone</label><input className={inp} value={p.phone_number||''} onChange={e => setPersonnel(prev => prev.map((x,j)=>j===i?{...x,phone_number:e.target.value}:x))} /></div>
                    <div><label className={lbl}>Email</label><input className={inp} type="email" value={p.email_address||''} onChange={e => setPersonnel(prev => prev.map((x,j)=>j===i?{...x,email_address:e.target.value}:x))} /></div>
                    <div><label className={lbl}>Position / Title</label><input className={inp} value={p.position} onChange={e => setPersonnel(prev => prev.map((x,j)=>j===i?{...x,position:e.target.value}:x))} /></div>
                    <div>
                      <label className={lbl}>Employment Status</label>
                      <select className={inp} value={p.employment_type} onChange={e => setPersonnel(prev => prev.map((x,j)=>j===i?{...x,employment_type:e.target.value}:x))}>
                        <option>Full Time</option><option>Part Time</option><option>Contractor</option>
                      </select>
                    </div>
                  </div>
                  {/* Row 3: Compensation — hidden from integration role */}
                  {!isIntegration && (
                    <div className="grid grid-cols-5 gap-2 mb-2">
                      <div><label className={lbl}>Hours/Year</label><input className={inp} type="number" value={p.hours_per_year||''} onChange={e => setPersonnel(prev => prev.map((x,j)=>j===i?{...x,hours_per_year:e.target.value}:x))} /></div>
                      <div><label className={lbl}>Hourly Rate ($)</label><input className={inp} type="number" value={p.hourly_rate} onChange={e => setPersonnel(prev => prev.map((x,j)=>j===i?{...x,hourly_rate:e.target.value}:x))} /></div>
                      <div><label className={lbl}>Salary ($/yr)</label><input className={inp} type="number" value={p.salary_rate} onChange={e => setPersonnel(prev => prev.map((x,j)=>j===i?{...x,salary_rate:e.target.value}:x))} /></div>
                      <div><label className={lbl}>YTD PTO Accrued</label><input className={inp} value={p.ytd_pto_accrued||''} onChange={e => setPersonnel(prev => prev.map((x,j)=>j===i?{...x,ytd_pto_accrued:e.target.value}:x))} /></div>
                      <div><label className={lbl}>PTO Entitlement</label><input className={inp} value={p.pto_entitlement||''} onChange={e => setPersonnel(prev => prev.map((x,j)=>j===i?{...x,pto_entitlement:e.target.value}:x))} /></div>
                    </div>
                  )}
                  {/* Row 4: Benefits checkboxes */}
                  <div className="flex flex-wrap gap-4 mt-1">
                    <label className="flex items-center gap-1.5 text-xs text-gray-600"><input type="checkbox" checked={!!p.has_401k} onChange={e => setPersonnel(prev => prev.map((x,j)=>j===i?{...x,has_401k:e.target.checked}:x))} /> 401(k)</label>
                    <label className="flex items-center gap-1.5 text-xs text-gray-600"><input type="checkbox" checked={!!p.company_credit_card} onChange={e => setPersonnel(prev => prev.map((x,j)=>j===i?{...x,company_credit_card:e.target.checked}:x))} /> Co. Credit Card</label>
                    <label className="flex items-center gap-1.5 text-xs text-gray-600"><input type="checkbox" checked={!!p.phone_stipend} onChange={e => setPersonnel(prev => prev.map((x,j)=>j===i?{...x,phone_stipend:e.target.checked}:x))} /> Phone Stipend</label>
                    <label className="flex items-center gap-1.5 text-xs text-gray-600"><input type="checkbox" checked={!!p.company_vehicle} onChange={e => setPersonnel(prev => prev.map((x,j)=>j===i?{...x,company_vehicle:e.target.checked}:x))} /> Co. Vehicle</label>
                    <label className="flex items-center gap-1.5 text-xs text-gray-600"><input type="checkbox" checked={!!p.life_insurance} onChange={e => setPersonnel(prev => prev.map((x,j)=>j===i?{...x,life_insurance:e.target.checked}:x))} /> Life Insurance</label>
                    <label className="flex items-center gap-1.5 text-xs text-gray-600"><input type="checkbox" checked={!!p.clothing_allowance} onChange={e => setPersonnel(prev => prev.map((x,j)=>j===i?{...x,clothing_allowance:e.target.checked}:x))} /> Clothing Allowance</label>
                  </div>
                </div>
              ))}
              <div className="flex items-center gap-3 flex-wrap mt-1">
                <button type="button" onClick={() => setPersonnel(p => [...p, emptyBI.personnel()])} className="text-xs text-blue-600 hover:underline">+ Add Person</button>
                <BiCsvUploadBtn
                  label="Upload CSV"
                  onParsed={rows => {
                    const mapped = rows.map(r => ({
                      ...emptyBI.personnel(),
                      first_name:          biCol(r, 'first_name', 'first', 'firstname', 'fname'),
                      middle_initial:      biCol(r, 'middle_initial', 'middle', 'mi'),
                      last_name:           biCol(r, 'last_name', 'last', 'lastname', 'lname'),
                      hire_date:           biCol(r, 'hire_date', 'hire', 'start_date', 'hiredate'),
                      phone_number:        biCol(r, 'phone_number', 'phone', 'mobile', 'cell'),
                      email_address:       biCol(r, 'email_address', 'email'),
                      position:            biCol(r, 'position', 'title', 'job_title', 'role'),
                      employment_type:     biCol(r, 'employment_type', 'type', 'status', 'emp_type') || 'Full Time',
                      hours_per_year:      biCol(r, 'hours_per_year', 'hours', 'annual_hours'),
                      hourly_rate:         biCol(r, 'hourly_rate', 'hourly', 'rate'),
                      salary_rate:         biCol(r, 'salary_rate', 'salary', 'annual_salary'),
                      ytd_pto_accrued:     biCol(r, 'ytd_pto_accrued', 'ytd_pto', 'pto_accrued'),
                      pto_entitlement:     biCol(r, 'pto_entitlement', 'pto', 'pto_days'),
                      has_401k:            biBool(r, 'has_401k', '401k', 'retirement'),
                      company_credit_card: biBool(r, 'company_credit_card', 'credit_card', 'corp_card'),
                      phone_stipend:       biBool(r, 'phone_stipend', 'phone_allowance'),
                      company_vehicle:     biBool(r, 'company_vehicle', 'vehicle', 'car'),
                      life_insurance:      biBool(r, 'life_insurance', 'insurance'),
                      clothing_allowance:  biBool(r, 'clothing_allowance', 'uniform', 'clothing'),
                    }));
                    setPersonnel(mapped);
                  }}
                />
                <button type="button"
                  onClick={() => biDownloadTemplate('employees_template.csv', [
                    'first_name','middle_initial','last_name','hire_date','phone_number',
                    'email_address','position','employment_type','hours_per_year',
                    'hourly_rate','salary_rate','ytd_pto_accrued','pto_entitlement',
                    'has_401k','company_credit_card','phone_stipend','company_vehicle',
                    'life_insurance','clothing_allowance'
                  ])}
                  className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1">
                  <Download className="w-3 h-3" /> Template
                </button>
              </div>
            </div>
          )}

          {/* VEHICLES */}
          {activeTab === 'vehicles' && (
            <div className="space-y-2">
              {vehicles.map((v, i) => (
                <div key={i} className="border rounded p-2 relative">
                  {vehicles.length > 1 && (
                    <button type="button" onClick={() => setVehicles(prev => prev.filter((_,j)=>j!==i))} className="absolute top-1.5 right-1.5 text-gray-300 hover:text-red-400 text-xs">Remove</button>
                  )}
                  <div className="grid grid-cols-3 gap-2">
                    <div><label className={lbl}>Year</label><input className={inp} type="number" value={v.year} onChange={e=>setVehicles(p=>p.map((x,j)=>j===i?{...x,year:e.target.value}:x))} /></div>
                    <div><label className={lbl}>Make</label><input className={inp} value={v.make} onChange={e=>setVehicles(p=>p.map((x,j)=>j===i?{...x,make:e.target.value}:x))} /></div>
                    <div><label className={lbl}>Model</label><input className={inp} value={v.model} onChange={e=>setVehicles(p=>p.map((x,j)=>j===i?{...x,model:e.target.value}:x))} /></div>
                    <div><label className={lbl}>VIN</label><input className={inp} value={v.vin} onChange={e=>setVehicles(p=>p.map((x,j)=>j===i?{...x,vin:e.target.value}:x))} /></div>
                    <div><label className={lbl}>Mileage</label><input className={inp} type="number" value={v.mileage} onChange={e=>setVehicles(p=>p.map((x,j)=>j===i?{...x,mileage:e.target.value}:x))} /></div>
                    <div><label className={lbl}>KBB Value ($)</label><input className={inp} type="number" value={v.kbb_value} onChange={e=>setVehicles(p=>p.map((x,j)=>j===i?{...x,kbb_value:e.target.value}:x))} /></div>
                  </div>
                </div>
              ))}
              <div className="flex items-center gap-3 flex-wrap mt-1">
                <button type="button" onClick={() => setVehicles(p => [...p, emptyBI.vehicle()])} className="text-xs text-blue-600 hover:underline">+ Add Vehicle</button>
                <BiCsvUploadBtn
                  label="Upload CSV"
                  onParsed={rows => {
                    const mapped = rows.map(r => ({
                      ...emptyBI.vehicle(),
                      year:         biCol(r, 'year', 'model_year', 'yr'),
                      make:         biCol(r, 'make', 'manufacturer', 'brand'),
                      model:        biCol(r, 'model', 'model_name'),
                      vin:          biCol(r, 'vin', 'vin_number', 'vehicle_id'),
                      mileage:      biCol(r, 'mileage', 'miles', 'odometer'),
                      loan_balance: biCol(r, 'loan_balance', 'loan', 'balance', 'payoff'),
                      kbb_value:    biCol(r, 'kbb_value', 'kbb', 'value', 'book_value', 'market_value'),
                    }));
                    setVehicles(mapped);
                  }}
                />
                <button type="button"
                  onClick={() => biDownloadTemplate('vehicles_template.csv', [
                    'year','make','model','vin','mileage','loan_balance','kbb_value'
                  ])}
                  className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1">
                  <Download className="w-3 h-3" /> Template
                </button>
              </div>
            </div>
          )}

          {/* UTILITIES */}
          {activeTab === 'utilities' && (
            <div className="border rounded-lg overflow-hidden">
              <div className="grid grid-cols-4 gap-0 bg-gray-50 border-b px-2 py-1">
                {['Utility','Vendor / Company','Account #','Contract End'].map(h=><div key={h} className="text-xs font-medium text-gray-500">{h}</div>)}
              </div>
              {BI_UTILITY_TYPES.map((type, i) => (
                <div key={type} className={`grid grid-cols-4 gap-2 px-2 py-1.5 items-center ${i%2===1?'bg-gray-50':'bg-white'}`}>
                  <div className="text-xs font-medium text-gray-600 capitalize">{type.replace('_',' ')}</div>
                  <input className="border rounded px-2 py-1 text-xs w-full focus:outline-none focus:ring-1 focus:ring-blue-300" value={utilities[type]?.vendor_name||''} onChange={e=>setUtilities(p=>({...p,[type]:{...p[type],vendor_name:e.target.value}}))} />
                  <input className="border rounded px-2 py-1 text-xs w-full focus:outline-none focus:ring-1 focus:ring-blue-300" value={utilities[type]?.account_number||''} onChange={e=>setUtilities(p=>({...p,[type]:{...p[type],account_number:e.target.value}}))} />
                  <input className="border rounded px-2 py-1 text-xs w-full focus:outline-none focus:ring-1 focus:ring-blue-300" type="date" value={utilities[type]?.contract_end||''} onChange={e=>setUtilities(p=>({...p,[type]:{...p[type],contract_end:e.target.value}}))} />
                </div>
              ))}
            </div>
          )}

          {/* PRENEED */}
          {activeTab === 'preneed' && (
            <div className="space-y-2">
              {preneed.map((p, i) => (
                <div key={i} className="border rounded p-2 relative">
                  {preneed.length > 1 && (
                    <button type="button" onClick={() => setPreneed(prev => prev.filter((_,j)=>j!==i))} className="absolute top-1.5 right-1.5 text-gray-300 hover:text-red-400 text-xs">Remove</button>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className={lbl}>Company Name</label><input className={inp} value={p.company_name} onChange={e=>setPreneed(prev=>prev.map((x,j)=>j===i?{...x,company_name:e.target.value}:x))} /></div>
                    <div><label className={lbl}>Contract Number</label><input className={inp} value={p.contract_number} onChange={e=>setPreneed(prev=>prev.map((x,j)=>j===i?{...x,contract_number:e.target.value}:x))} /></div>
                    <div><label className={lbl}>Contact Name</label><input className={inp} value={p.contact_name} onChange={e=>setPreneed(prev=>prev.map((x,j)=>j===i?{...x,contact_name:e.target.value}:x))} /></div>
                    <div><label className={lbl}>Contact Phone</label><input className={inp} value={p.contact_phone} onChange={e=>setPreneed(prev=>prev.map((x,j)=>j===i?{...x,contact_phone:e.target.value}:x))} /></div>
                  </div>
                </div>
              ))}
              <button type="button" onClick={() => setPreneed(p => [...p, emptyBI.preneed()])} className="text-xs text-blue-600 hover:underline">+ Add Preneed Company</button>
            </div>
          )}

          {/* FACILITIES */}
          {activeTab === 'facilities' && (
            <div className="border rounded-lg overflow-hidden">
              <div className="grid grid-cols-4 gap-0 bg-gray-50 border-b px-2 py-1">
                {['Service','Vendor / Company','Phone','Contract End'].map(h=><div key={h} className="text-xs font-medium text-gray-500">{h}</div>)}
              </div>
              {BI_FACILITY_TYPES.map((type, i) => (
                <div key={type} className={`grid grid-cols-4 gap-2 px-2 py-1.5 items-center ${i%2===1?'bg-gray-50':'bg-white'}`}>
                  <div className="text-xs font-medium text-gray-600 capitalize">{type.replace(/_/g,' ')}</div>
                  <input className="border rounded px-2 py-1 text-xs w-full focus:outline-none focus:ring-1 focus:ring-blue-300" value={facilities[type]?.vendor_name||''} onChange={e=>setFacilities(p=>({...p,[type]:{...p[type],vendor_name:e.target.value}}))} />
                  <input className="border rounded px-2 py-1 text-xs w-full focus:outline-none focus:ring-1 focus:ring-blue-300" value={facilities[type]?.vendor_phone||''} onChange={e=>setFacilities(p=>({...p,[type]:{...p[type],vendor_phone:e.target.value}}))} />
                  <input className="border rounded px-2 py-1 text-xs w-full focus:outline-none focus:ring-1 focus:ring-blue-300" type="date" value={facilities[type]?.contract_end||''} onChange={e=>setFacilities(p=>({...p,[type]:{...p[type],contract_end:e.target.value}}))} />
                </div>
              ))}
            </div>
          )}

          {/* CREMATORY */}
          {activeTab === 'crematory' && (
            <div className="space-y-2">
              {crematory.map((c, i) => (
                <div key={i} className="border rounded p-2 relative">
                  {crematory.length > 1 && (
                    <button type="button" onClick={() => setCrematory(prev => prev.filter((_,j)=>j!==i))} className="absolute top-1.5 right-1.5 text-gray-300 hover:text-red-400 text-xs">Remove</button>
                  )}
                  {crematory.length > 1 && <div className="text-xs font-medium text-gray-400 mb-1.5">Retort {i+1}</div>}
                  <div className="grid grid-cols-3 gap-2">
                    <div><label className={lbl}>Machine</label><input className={inp} value={c.machine} onChange={e=>setCrematory(p=>p.map((x,j)=>j===i?{...x,machine:e.target.value}:x))} /></div>
                    <div><label className={lbl}>Installation Date</label><input className={inp} type="date" value={c.installation_date} onChange={e=>setCrematory(p=>p.map((x,j)=>j===i?{...x,installation_date:e.target.value}:x))} /></div>
                    <div><label className={lbl}>Last Service Date</label><input className={inp} type="date" value={c.last_service_date} onChange={e=>setCrematory(p=>p.map((x,j)=>j===i?{...x,last_service_date:e.target.value}:x))} /></div>
                    <div><label className={lbl}>Capacity (per day)</label><input className={inp} type="number" value={c.capacity} onChange={e=>setCrematory(p=>p.map((x,j)=>j===i?{...x,capacity:e.target.value}:x))} /></div>
                    <div><label className={lbl}>EPA Certification</label><input className={inp} value={c.epa_certification} onChange={e=>setCrematory(p=>p.map((x,j)=>j===i?{...x,epa_certification:e.target.value}:x))} /></div>
                    <div><label className={lbl}>Regulatory Info</label><input className={inp} value={c.regulatory_info} onChange={e=>setCrematory(p=>p.map((x,j)=>j===i?{...x,regulatory_info:e.target.value}:x))} /></div>
                    <div className="col-span-3"><label className={lbl}>Maintenance Program / Company</label><input className={inp} value={c.maintenance_program} onChange={e=>setCrematory(p=>p.map((x,j)=>j===i?{...x,maintenance_program:e.target.value}:x))} /></div>
                  </div>
                </div>
              ))}
              <button type="button" onClick={() => setCrematory(p => [...p, emptyBI.crematory()])} className="text-xs text-blue-600 hover:underline">+ Add Retort</button>
            </div>
          )}

          {/* TECHNOLOGY */}
          {activeTab === 'technology' && (
            <div className="space-y-4">
              <div>
                <h4 className="text-xs font-semibold text-gray-600 mb-2">Computers &amp; Hardware</h4>
                <div className="space-y-2">
                  {techComputers.map((c, i) => (
                    <div key={i} className="border rounded p-2 relative">
                      {techComputers.length > 1 && (
                        <button type="button" onClick={() => setTechComputers(p => p.filter((_,j)=>j!==i))} className="absolute top-1.5 right-1.5 text-gray-300 hover:text-red-400 text-xs">Remove</button>
                      )}
                      <div className="grid grid-cols-4 gap-2">
                        <div>
                          <label className={lbl}>Type</label>
                          <select className={inp} value={c.computer_type} onChange={e=>setTechComputers(p=>p.map((x,j)=>j===i?{...x,computer_type:e.target.value}:x))}>
                            <option value="desktop">Desktop</option><option value="laptop">Laptop</option><option value="tablet">Tablet</option><option value="server">Server</option><option value="other">Other</option>
                          </select>
                        </div>
                        <div><label className={lbl}>Make</label><input className={inp} value={c.make} onChange={e=>setTechComputers(p=>p.map((x,j)=>j===i?{...x,make:e.target.value}:x))} /></div>
                        <div><label className={lbl}>Model</label><input className={inp} value={c.model} onChange={e=>setTechComputers(p=>p.map((x,j)=>j===i?{...x,model:e.target.value}:x))} /></div>
                        <div><label className={lbl}>Serial #</label><input className={inp} value={c.serial_number} onChange={e=>setTechComputers(p=>p.map((x,j)=>j===i?{...x,serial_number:e.target.value}:x))} /></div>
                      </div>
                    </div>
                  ))}
                  <button type="button" onClick={() => setTechComputers(p => [...p, emptyBI.computer()])} className="text-xs text-blue-600 hover:underline">+ Add Computer</button>
                </div>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-gray-600 mb-2">Software Subscriptions</h4>
                <div className="space-y-2">
                  {techSoftware.map((s, i) => (
                    <div key={i} className="border rounded p-2 relative">
                      {techSoftware.length > 1 && (
                        <button type="button" onClick={() => setTechSoftware(p => p.filter((_,j)=>j!==i))} className="absolute top-1.5 right-1.5 text-gray-300 hover:text-red-400 text-xs">Remove</button>
                      )}
                      <div className="grid grid-cols-3 gap-2">
                        <div><label className={lbl}>Software Name</label><input className={inp} value={s.name} onChange={e=>setTechSoftware(p=>p.map((x,j)=>j===i?{...x,name:e.target.value}:x))} /></div>
                        <div><label className={lbl}>Monthly Cost ($)</label><input className={inp} type="number" value={s.monthly_cost} onChange={e=>setTechSoftware(p=>p.map((x,j)=>j===i?{...x,monthly_cost:e.target.value}:x))} /></div>
                        <div><label className={lbl}>Annual Cost ($)</label><input className={inp} type="number" value={s.annual_cost} onChange={e=>setTechSoftware(p=>p.map((x,j)=>j===i?{...x,annual_cost:e.target.value}:x))} /></div>
                        <div><label className={lbl}>Renewal Date</label><input className={inp} type="date" value={s.renewal_date} onChange={e=>setTechSoftware(p=>p.map((x,j)=>j===i?{...x,renewal_date:e.target.value}:x))} /></div>
                        <div><label className={lbl}>Login URL</label><input className={inp} value={s.login_url} onChange={e=>setTechSoftware(p=>p.map((x,j)=>j===i?{...x,login_url:e.target.value}:x))} /></div>
                        <div><label className={lbl}>Username</label><input className={inp} value={s.username} onChange={e=>setTechSoftware(p=>p.map((x,j)=>j===i?{...x,username:e.target.value}:x))} /></div>
                      </div>
                    </div>
                  ))}
                  <button type="button" onClick={() => setTechSoftware(p => [...p, emptyBI.software()])} className="text-xs text-blue-600 hover:underline">+ Add Software</button>
                </div>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-gray-600 mb-2">Website &amp; Phone System</h4>
                <div className="border rounded-lg overflow-hidden">
                  <div className="grid grid-cols-3 gap-0 bg-gray-50 border-b px-2 py-1">
                    {['Service','URL / Provider','Notes'].map(h=><div key={h} className="text-xs font-medium text-gray-500">{h}</div>)}
                  </div>
                  {BI_TECH_CATS.map((cat, i) => (
                    <div key={cat} className={`grid grid-cols-3 gap-2 px-2 py-1.5 items-center ${i%2===1?'bg-gray-50':'bg-white'}`}>
                      <div className="text-xs font-medium text-gray-600">{BI_TECH_LABELS[cat] || cat.replace('_',' ')}</div>
                      <input className="border rounded px-2 py-1 text-xs w-full focus:outline-none focus:ring-1 focus:ring-blue-300" value={techOther[cat]?.url||''} onChange={e=>setTechOther(p=>({...p,[cat]:{...p[cat],url:e.target.value}}))} />
                      <input className="border rounded px-2 py-1 text-xs w-full focus:outline-none focus:ring-1 focus:ring-blue-300" value={techOther[cat]?.notes||''} onChange={e=>setTechOther(p=>({...p,[cat]:{...p[cat],notes:e.target.value}}))} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t flex justify-between items-center bg-gray-50 rounded-b-lg">
          <button type="button" onClick={onClose} className="px-3 py-1.5 border rounded text-sm text-gray-600 hover:bg-white transition-colors">
            Close
          </button>
          <SaveBtn tab={activeTab} />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// IntegrationTasksPanel — single consolidated section for integration role.
// Shows post-close tasks grouped by phase (1-30, 31-60, 61-90 days).
// Each task is checkable inline. No filters, no docs, no sidebar — the
// whole DealView for integration is timeline + this panel.
// ─────────────────────────────────────────────────────────────────────────
function IntegrationTasksPanel({ tasks, updateTask, primaryColor }) {
  const PHASES = [
    { id: 'integration-1-30',  label: 'Days 1–30',  accent: '#15803D' },
    { id: 'integration-31-60', label: 'Days 31–60', accent: '#1A3665' },
    { id: 'integration-61-90', label: 'Days 61–90', accent: '#7C3AED' },
  ];
  const phaseTasks = (id) => (tasks || [])
    .filter(t => t.phase === id)
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  const totalCount = PHASES.reduce((s, p) => s + phaseTasks(p.id).length, 0);
  const doneCount = PHASES.reduce(
    (s, p) => s + phaseTasks(p.id).filter(t => t.status === 'Complete' || t.status === 'N/A').length,
    0
  );

  if (totalCount === 0) {
    return (
      <section
        className="rounded-2xl mb-6 px-6 py-8 text-center"
        style={{ backgroundColor: '#fff', border: '1px solid #E5E9F0' }}
      >
        <div className="text-sm" style={{ color: '#64748B' }}>
          No integration tasks for this deal yet — they appear once the deal closes.
        </div>
      </section>
    );
  }

  return (
    <section
      className="rounded-2xl mb-6 overflow-hidden relative"
      style={{
        backgroundColor: '#fff', border: '1px solid #E5E9F0',
        boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 4px 12px rgba(15,23,42,0.03)',
      }}
    >
      <div
        className="absolute top-0 left-0 right-0 h-[3px]"
        style={{ background: `linear-gradient(90deg, ${primaryColor} 0%, #2E4B7C 30%, #5470A8 70%, ${primaryColor}40 100%)` }}
      />
      <div className="px-6 py-4 flex items-center justify-between gap-3 flex-wrap" style={{ borderBottom: '1px solid #F0F2F6' }}>
        <div>
          <h2 className="text-base font-semibold tracking-tight" style={{ color: '#0F172A', letterSpacing: '-0.01em' }}>
            Integration Tasks
          </h2>
          <div className="text-xs mt-0.5" style={{ color: '#64748B' }}>
            {doneCount} of {totalCount} complete
          </div>
        </div>
      </div>
      <div>
        {PHASES.map(p => {
          const list = phaseTasks(p.id);
          if (list.length === 0) return null;
          const done = list.filter(t => t.status === 'Complete' || t.status === 'N/A').length;
          return (
            <div key={p.id} style={{ borderTop: '1px solid #F0F2F6' }}>
              <div className="px-6 py-2.5 flex items-center justify-between" style={{ backgroundColor: '#FAFBFD' }}>
                <span
                  className="text-[11px] font-semibold uppercase tracking-wider"
                  style={{ color: p.accent, letterSpacing: '0.08em' }}
                >
                  {p.label}
                </span>
                <span className="text-[11px] tabular-nums" style={{ color: '#94A3B8' }}>
                  {done}/{list.length}
                </span>
              </div>
              <ul>
                {list.map(t => {
                  const isComplete = t.status === 'Complete' || t.status === 'N/A';
                  return (
                    <li
                      key={t.id}
                      className="px-6 py-2.5 flex items-start gap-3 hover:bg-slate-50 transition-colors"
                      style={{ borderTop: '1px solid #F8FAFC' }}
                    >
                      <input
                        type="checkbox"
                        checked={isComplete}
                        onChange={(e) => updateTask(t.id, { status: e.target.checked ? 'Complete' : 'Not Started' })}
                        className="mt-0.5 rounded accent-[#1A3665] cursor-pointer"
                      />
                      <div className="flex-1 min-w-0">
                        <div
                          className="text-[13px]"
                          style={{
                            color: isComplete ? '#94A3B8' : '#0F172A',
                            textDecoration: isComplete ? 'line-through' : 'none',
                          }}
                        >
                          {t.name}
                        </div>
                        {(t.assignee_name || t.due_date) && (
                          <div className="text-[11px] mt-0.5" style={{ color: '#64748B' }}>
                            {t.assignee_name && <span>{t.assignee_name}</span>}
                            {t.assignee_name && t.due_date && <span style={{ color: '#CBD5E1' }}> · </span>}
                            {t.due_date && <span>Due {new Date(t.due_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ============================================================================
// Phase Q.8 — Send Request to Deal Advisor
// ============================================================================
// Lets a member/admin compose a focused request for a specific deal_advisor
// on this deal. Optionally also creates a task assigned to that advisor.
// Calls send-deal-advisor-request which generates a magic link and sends a
// branded email.
function SendAdvisorRequestModal({ advisor, dealId, dealName, primaryColor, onClose, onSent, showToast }) {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [createTask, setCreateTask] = useState(true);
  const [taskName, setTaskName] = useState('');
  const [taskDueDate, setTaskDueDate] = useState('');
  const [isDocRequest, setIsDocRequest] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const advisorDisplay = advisor?.full_name || advisor?.email || 'this advisor';

  const handleSend = async () => {
    setError(null);
    if (!message.trim()) { setError('Add a message — tell them what you need.'); return; }
    if (createTask && !taskName.trim() && !subject.trim()) {
      setError('Either set a Subject or a Task name (used to label what they owe).');
      return;
    }
    setSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not signed in');
      const r = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-deal-advisor-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          deal_id: dealId,
          advisor_user_id: advisor.id,
          subject: subject.trim() || null,
          message: message.trim(),
          create_task: createTask,
          task_name: taskName.trim() || subject.trim() || null,
          task_due_date: taskDueDate || null,
          is_doc_request: isDocRequest,
        }),
      });
      const result = await r.json();
      if (!r.ok) throw new Error(result.error || `HTTP ${r.status}`);
      onSent?.();
    } catch (e) {
      setError(e?.message || 'Send failed');
      showToast?.(e?.message || 'Send failed', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(15,23,42,0.5)' }}>
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid #F0F2F6' }}>
          <div>
            <h2 className="text-base font-semibold" style={{ color: '#0F172A' }}>Send request</h2>
            <div className="text-[12px]" style={{ color: '#64748B' }}>
              To <strong>{advisorDisplay}</strong> · {dealName}
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-slate-100 cursor-pointer" style={{ color: '#64748B' }}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          {error && (
            <div className="rounded-lg px-3 py-2 text-[12px]" style={{ backgroundColor: '#FEE2E2', border: '1px solid #FECACA', color: '#B91C1C' }}>{error}</div>
          )}

          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#64748B' }}>Subject (optional)</label>
            <input
              type="text"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder={`e.g. Need your review of the LOI`}
              className="w-full h-9 text-[13px] rounded-md px-2 mt-1 focus:outline-none"
              style={{ border: '1px solid #E5E9F0', backgroundColor: 'white', color: '#0F172A' }}
            />
          </div>

          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#64748B' }}>Message *</label>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder={`Hi ${advisor?.full_name?.split(' ')[0] || ''} — could you take a look at...`}
              className="w-full text-[13px] rounded-md px-2 py-2 mt-1 focus:outline-none"
              style={{ border: '1px solid #E5E9F0', backgroundColor: 'white', color: '#0F172A', minHeight: 120 }}
            />
          </div>

          <label className="flex items-center gap-2 text-[13px] cursor-pointer" style={{ color: '#475569' }}>
            <input
              type="checkbox"
              checked={createTask}
              onChange={e => setCreateTask(e.target.checked)}
              className="cursor-pointer"
            />
            Also create a task assigned to them on this deal
          </label>

          {createTask && (
            <div className="pl-6 space-y-2" style={{ borderLeft: '2px solid #E5E9F0' }}>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#64748B' }}>Task name (defaults to Subject)</label>
                <input
                  type="text"
                  value={taskName}
                  onChange={e => setTaskName(e.target.value)}
                  placeholder={subject || 'What they need to do'}
                  className="w-full h-9 text-[13px] rounded-md px-2 mt-1 focus:outline-none"
                  style={{ border: '1px solid #E5E9F0', backgroundColor: 'white', color: '#0F172A' }}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#64748B' }}>Due date</label>
                  <input
                    type="date"
                    value={taskDueDate}
                    onChange={e => setTaskDueDate(e.target.value)}
                    className="w-full h-9 text-[13px] rounded-md px-2 mt-1 focus:outline-none"
                    style={{ border: '1px solid #E5E9F0', backgroundColor: 'white', color: '#0F172A' }}
                  />
                </div>
                <label className="flex items-center gap-2 text-[12px] mt-5 cursor-pointer" style={{ color: '#475569' }}>
                  <input
                    type="checkbox"
                    checked={isDocRequest}
                    onChange={e => setIsDocRequest(e.target.checked)}
                    className="cursor-pointer"
                  />
                  They'll be uploading a document
                </label>
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-3 flex items-center justify-end gap-2" style={{ borderTop: '1px solid #F0F2F6' }}>
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-[12px] font-semibold rounded-md cursor-pointer"
                  style={{ backgroundColor: 'white', color: '#475569', border: '1px solid #E5E9F0' }}>
            Cancel
          </button>
          <button type="button" onClick={handleSend} disabled={submitting}
                  className="px-3 py-1.5 text-[12px] font-semibold rounded-md text-white cursor-pointer disabled:opacity-50"
                  style={{ backgroundColor: primaryColor }}>
            {submitting ? 'Sending…' : 'Send request'}
          </button>
        </div>
      </div>
    </div>
  );
}
